import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, decimal, boolean, timestamp, uuid, jsonb, uniqueIndex, index, date } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Session storage table (required for Replit Auth)
export const sessions = pgTable("sessions", {
  sid: varchar("sid").primaryKey(),
  sess: jsonb("sess").notNull(),
  expire: timestamp("expire").notNull(),
});

// User Roles (users can have multiple roles via array)
export const USER_ROLES = ["PROSPECT", "BUYER", "SUPPLIER", "DISTRIBUTION_CENTER", "SOHNEN_ADMIN", "SHOPJIMMY_ADMIN", "SUPER_ADMIN"] as const;
export type UserRole = typeof USER_ROLES[number];

// Auth Providers
export const AUTH_PROVIDERS = ["replit", "email", "google", "twitter", "apple"] as const;
export type AuthProvider = typeof AUTH_PROVIDERS[number];

// Freight Carrier Types
export const FREIGHT_CARRIER_TYPES = ["DATABASE", "UBER_FREIGHT", "CXT_SOFTWARE", "FREIGHTVIEW", "EFW", "CUSTOM"] as const;
export type FreightCarrierType = typeof FREIGHT_CARRIER_TYPES[number];

// Shipping Methods
export const SHIPPING_METHODS = ["FREIGHT", "LOCAL_PICKUP", "CUSTOMER_FREIGHT"] as const;
export type ShippingMethod = typeof SHIPPING_METHODS[number];

// Account Types
export const ACCOUNT_TYPES = ["CONSUMER", "BUSINESS"] as const;
export type AccountType = typeof ACCOUNT_TYPES[number];

// Address Classification Types
export const ADDRESS_CLASSIFICATION_TYPES = ["WAREHOUSE", "RESIDENTIAL"] as const;
export type AddressClassificationType = typeof ADDRESS_CLASSIFICATION_TYPES[number];

// US Timezone Options (IANA format)
export const US_TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Phoenix", label: "Arizona (MST - No DST)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HST)" },
] as const;
export type USTimezone = typeof US_TIMEZONES[number]["value"];

// User storage table (supports both Replit Auth and new multi-auth system)
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  emailVerifiedAt: timestamp("email_verified_at"),
  passwordHash: varchar("password_hash"),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  profileImageUrl: varchar("profile_image_url"),
  phone: varchar("phone"),
  businessName: varchar("business_name"),
  xHandle: varchar("x_handle").unique(),
  roles: text("roles").array().default(sql`ARRAY['PROSPECT']::text[]`), // Multi-role support: user can be BUYER, SUPPLIER, DISTRIBUTION_CENTER, ADMIN, etc.
  isActive: boolean("is_active").default(true),
  provider: varchar("provider").default("replit"), // Primary auth provider
  providerId: varchar("provider_id"), // ID from OAuth provider
  setupToken: varchar("setup_token"), // Token for account setup completion link
  setupTokenExpiresAt: timestamp("setup_token_expires_at"), // Expiry for setup token
  defaultShippingMethod: varchar("default_shipping_method").$type<ShippingMethod>().default("FREIGHT"), // User's default shipping method preference
  defaultPickupWarehouseId: uuid("default_pickup_warehouse_id").references(() => supplierAddresses.id), // Preferred warehouse for local pickup
  attributedSalesRepId: varchar("attributed_sales_rep_id"), // Sales rep credited for converting this user (from invitation)
  timezone: varchar("timezone").default("America/Chicago"), // User's preferred timezone (IANA format)
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Country options (shipping restricted to North America)
export const COUNTRIES = ["US", "CA", "MX"] as const;
export type Country = typeof COUNTRIES[number];

// Address types
export const ADDRESS_TYPES = ["BILLING", "SHIPPING"] as const;
export type AddressType = typeof ADDRESS_TYPES[number];

// Accounts (B2B organizations, companies, and consumer accounts)
export const accounts = pgTable("accounts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountType: varchar("account_type").$type<AccountType>().notNull().default("BUSINESS"), // CONSUMER or BUSINESS
  companyName: text("company_name").notNull(),
  legalCompanyName: text("legal_company_name"),
  dbaName: text("dba_name"),
  ein: text("ein"),
  companyType: text("company_type"),
  businessType: text("business_type"),
  employeeCount: text("employee_count"),
  annualRevenue: text("annual_revenue"),
  hasLoadingDock: boolean("has_loading_dock").default(false),
  hasForklift: boolean("has_forklift").default(false),
  salesTaxExemptUrl: text("sales_tax_exempt_url"), // URL to W9/sales tax exemption certificate (legacy)
  resaleCertificateState: text("resale_certificate_state"), // State where resale certificate is valid (e.g., "MN", "WI")
  resaleCertificateTaxId: text("resale_certificate_tax_id"), // Sales tax ID / resale certificate number
  resaleCertificateUrl: text("resale_certificate_url"), // URL to uploaded resale certificate
  resaleCertificateAcknowledgedAt: timestamp("resale_certificate_acknowledged_at"), // When user acknowledged certificate validity
  notes: text("notes"),
  accountRepId: varchar("account_rep_id").references(() => users.id), // Assigned account representative (ShopJimmy admin)
  minimumOrderQuantity: integer("minimum_order_quantity").default(4).notNull(), // Minimum total unit quantity required per order
  paymentTerms: varchar("payment_terms").$type<"COD" | "NET_15">().default("COD").notNull(), // Payment terms: COD or NET_15
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Account-User junction (many-to-many: one company can have multiple users, one user can belong to multiple accounts)
export const accountUsers = pgTable("account_users", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id).notNull(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  isPrimary: boolean("is_primary").default(false),
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Password Reset Token Types
export const PASSWORD_TOKEN_TYPES = ["RESET", "SETUP", "ADMIN_SETUP"] as const;
export type PasswordTokenType = typeof PASSWORD_TOKEN_TYPES[number];

// Password reset/setup tokens table
export const passwordTokens = pgTable("password_tokens", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  token: varchar("token").notNull().unique(), // Secure random token
  userId: varchar("user_id").references(() => users.id).notNull(),
  tokenType: varchar("token_type").$type<PasswordTokenType>().notNull(), // RESET, SETUP, or ADMIN_SETUP
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"), // Track when token was used
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Addresses (replaces saved_addresses, supports billing and shipping for accounts)
export const addresses = pgTable("addresses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id).notNull(),
  isBilling: boolean("is_billing").notNull().default(false),
  isShipping: boolean("is_shipping").notNull().default(false),
  addressClassification: varchar("address_classification").$type<AddressClassificationType>().default("WAREHOUSE"), // WAREHOUSE or RESIDENTIAL
  requiresLiftgate: boolean("requires_liftgate").default(false), // Required for residential deliveries
  locationName: text("location_name").notNull(), // Required: name of the location
  line1: text("line1").notNull(),
  line2: text("line2"),
  city: text("city").notNull(),
  region: text("region").notNull(), // state/province
  postalCode: text("postal_code").notNull(),
  country: varchar("country").notNull().default("US"), // "US", "CA", "MX"
  phone: text("phone"),
  // Shipping contact information (required when isShipping is true)
  deliveryContactName: text("delivery_contact_name"),
  deliveryContactPhone: text("delivery_contact_phone"),
  deliveryContactEmail: text("delivery_contact_email"),
  hasLoadingDock: boolean("has_loading_dock").default(false),
  hasForklift: boolean("has_forklift").default(false),
  isDefaultShipping: boolean("is_default_shipping").default(false),
  defaultShippingMethod: varchar("default_shipping_method").$type<ShippingMethod>().default("FREIGHT"), // Default shipping method for this address
  defaultPickupWarehouseId: uuid("default_pickup_warehouse_id").references(() => supplierAddresses.id), // Preferred warehouse for local pickup from this address
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Admin notes (audit trail for account management)
export const adminNotes = pgTable("admin_notes", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id).notNull(),
  authorUserId: varchar("author_user_id").references(() => users.id).notNull(),
  body: text("body").notNull(),
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Saved addresses for customer checkout (DEPRECATED - kept for backward compatibility)
export const savedAddresses = pgTable("saved_addresses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  label: text("label"), // "Primary", "Warehouse", etc.
  companyName: text("company_name"),
  addressLine1: text("address_line1").notNull(),
  addressLine2: text("address_line2"),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zipCode: text("zip_code").notNull(),
  phone: text("phone"),
  hasLoadingDock: boolean("has_loading_dock").default(false),
  hasForklift: boolean("has_forklift").default(false),
  isDefault: boolean("is_default").default(false),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const categories = pgTable("categories", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  parentId: uuid("parent_id"), // Self-referencing for subcategories (null = top-level)
  name: text("name").notNull().unique(),
  // Compact label admins can edit alongside `name` — e.g. "Window AC" for
  // a "Window Air Conditioners" subcategory. Storefront use is wired up in
  // a follow-up; for now we just persist + expose it via the API so the
  // admin can curate it.
  shortName: text("short_name"),
  slug: text("slug").notNull().unique(),
  description: text("description"),
  isActive: boolean("is_active").default(true), // Toggle visibility
  sortOrder: integer("sort_order").default(0),
  // Category-first navigation (Task #111)
  showroomImage: text("showroom_image"), // Hero/tile image for /showroom landing
  // Per-category icon shown next to the in-stock count on the showroom tile
  // ("[icon] 42 pallets"). Admin-uploaded URL or pasted external URL.
  // When null the storefront falls back to a lucide default (Refrigerator
  // for refrigerators, Boxes for everything else).
  iconUrl: text("icon_url"),
  gradeScaleHint: text("grade_scale_hint"), // e.g. "A → B+ → B− scale"
  heroBlurb: text("hero_blurb"), // Short description shown on /category/:slug header
  types: text("types").array(), // Optional sub-type chips (e.g. ["Treadmills","Bikes"]) — only rendered when present
  // How items in this category are sold to the customer. Drives the count
  // noun ("11 products" vs "42 pallets") and the per-item price suffix on
  // the Showroom landing tile (Task #126). Stored as text so it's trivial
  // to extend later (e.g. "kits"); current values are "products" | "pallets".
  saleType: text("sale_type").default("products").notNull(),
  // Per-category labels for the Showroom landing tile count line.
  // Renders as "{inStockCount} {modelCountNounPlural} — {totalUnitCount} total {unitCountNounPlural}".
  // All four are nullable; defaults are derived server-side from saleType + category
  // name (e.g. products → "model"/"models" + lowercased category name; pallets →
  // "pallet mix"/"pallet mixes" + "pallet"/"pallets").
  modelCountNounSingular: text("model_count_noun_singular"),
  modelCountNounPlural: text("model_count_noun_plural"),
  unitCountNounSingular: text("unit_count_noun_singular"),
  unitCountNounPlural: text("unit_count_noun_plural"),
  // Per-category label for the strikethrough reference price shown next to
  // the live price (PDP title, product cards, spec table). Defaults to
  // "MSRP" but small-appliances admins typically prefer "Retail". Empty
  // string treated as null and falls back to "MSRP" client-side.
  referencePriceLabel: text("reference_price_label").default("MSRP"),
  // When true, the pallet PDP packaging example block is hidden for
  // products in this category. Small-appliance pallets ship in their
  // original retail boxes so the AC carton example is misleading.
  hidePalletPackagingExample: boolean("hide_pallet_packaging_example").default(false),
  // When false, the storefront product detail page hides the rich
  // Specifications section (Dimensions / Product Details cards) for products
  // in this category. Useful for categories like Shark/Ninja pallets where
  // per-SKU specs aren't meaningful at the pallet level. Defaults to true so
  // existing categories keep showing specs.
  showSpecifications: boolean("show_specifications").default(true).notNull(),
  // Per-category Packaging section shown on /grading. Admin uploads any
  // image (rendered with an "EXAMPLE" overlay on the storefront) and edits
  // the descriptive copy. NULL → section falls back to a generic blurb.
  packagingImage: text("packaging_image"),
  packagingDescription: text("packaging_description"),
  // Per-category product attribute schema (Task #117). Stored as an array of
  // {key,label,type,options?} entries. Drives the admin Product Attributes editor
  // and the storefront attribute chips. NULL falls back to defaults in
  // `shared/categoryAttributes.ts` so behavior is unchanged for unmigrated rows.
  attributesSchema: jsonb("attributes_schema"),
  // Default shipping dimensions/weight applied to products in this category
  // when their own shipping_* fields are NULL. Lets admins set
  // sensible per-category defaults (e.g. refrigerators 48"W × 77"H × 40"D
  // @ 450 lbs) instead of forcing every product to be edited individually.
  // Width/Depth/Height in inches; weight in pounds.
  defaultShippingWidth: integer("default_shipping_width"),
  defaultShippingDepth: integer("default_shipping_depth"),
  defaultShippingHeight: integer("default_shipping_height"),
  defaultShippingWeight: decimal("default_shipping_weight", { precision: 10, scale: 2 }),
  // Per-category grading copy (Task #156). Drives the inventory-card grade
  // pill tooltip and the per-grade detail line. NULL falls back to the
  // refrigerator copy in shared/grading.ts (GRADING_TOOLTIP +
  // tooltipDetailFor()) so unmigrated categories keep today's wording.
  gradingTooltip: text("grading_tooltip"),
  // Map of grade code → short detail blurb, e.g. {"A": "...", "B+": "...", "B-": "..."}.
  gradeDetailLines: jsonb("grade_detail_lines"),
  // Label rendered inside the pallet inventory pill (Task #156). Only used
  // when saleType="pallets" — replaces the standalone red "X" letter badge
  // with a category-icon + label pill ("Pallet · $2,663.80"). Defaults to
  // "Pallet" client-side when null.
  inventoryPillLabel: text("inventory_pill_label"),
  createdAt: timestamp("created_at").default(sql`now()`),
});

export const models = pgTable("models", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  categoryId: uuid("category_id").references(() => categories.id),
  supplierProgramId: uuid("supplier_program_id").references(() => supplierPrograms.id), // Links to supplier program for grading rules
  brand: text("brand").notNull(),
  mfgSku: text("mfg_sku").notNull(),
  description: text("description").notNull(),
  color: text("color"), // Product color from Excel (e.g., BLACK, WHITE, STAINLESS)
  imageUrl: text("image_url"),
  additionalImages: text("additional_images").array(),
  msrp: decimal("msrp", { precision: 10, scale: 2 }),
  // Our selling price each for pallet-only constituents. Single source of truth
  // for pallet selling-price rollups (Σ sellPriceUsd × qty). Distinct from msrp
  // (retail MSRP each). Null for non-constituent models / not-yet-backfilled rows.
  sellPriceUsd: decimal("sell_price_usd", { precision: 10, scale: 2 }),
  dimensionsWidth: integer("dimensions_width"), // in inches
  dimensionsDepth: integer("dimensions_depth"), // in inches
  dimensionsHeight: integer("dimensions_height"), // in inches
  weightLbs: decimal("weight_lbs", { precision: 10, scale: 2 }), // in pounds
  shippingWidth: integer("shipping_width").default(40), // Shipping dimensions in inches
  shippingDepth: integer("shipping_depth").default(48), // Shipping dimensions in inches
  shippingHeight: integer("shipping_height").default(75), // Shipping dimensions in inches
  shippingWeight: decimal("shipping_weight", { precision: 10, scale: 2 }).default("350"), // Shipping weight in pounds
  stackable: boolean("stackable").default(true), // Can items be stacked on top of this product
  fragilityScore: integer("fragility_score").default(5), // 1-10 scale, 1=very fragile, 10=very durable
  isStackable: boolean("is_stackable").default(true), // Paxter integration: can be stacked in optimization
  isRotatable: boolean("is_rotatable").default(true), // Paxter integration: can be rotated during packing
  attributes: jsonb("attributes"), // Category-specific attributes: { door_style: "french_door", ice_maker: true, BTU: 12000, etc. }
  // E-commerce product enrichment fields (canonical data)
  features: text("features").array(), // Product feature bullets (e.g., "29 cu. ft. of capacity")
  specifications: jsonb("specifications"), // {dimensions: [{name, value}], details: [{name, value}]}
  warranty: text("warranty"), // Warranty information text
  relatedDocuments: jsonb("related_documents"), // [{name: "Energy Guide", url: "https://..."}]
  // Ordering rules
  minOrderQty: integer("min_order_qty").default(1).notNull(), // Minimum order quantity per line item
  orderMultiple: integer("order_multiple").default(1).notNull(), // Must order in multiples of this number
  unitsPerPalletLayer: integer("units_per_pallet_layer"), // How many units fit on one pallet layer (null = not configured)
  palletLayerWidth: decimal("pallet_layer_width", { precision: 10, scale: 2 }), // Pallet layer width in inches (auto-calc or manual)
  palletLayerDepth: decimal("pallet_layer_depth", { precision: 10, scale: 2 }), // Pallet layer depth in inches (auto-calc or manual)
  rating: decimal("rating", { precision: 3, scale: 2 }), // Average product rating (e.g., 4.6)
  reviewsCount: integer("reviews_count"), // Number of reviews
  enrichedAt: timestamp("enriched_at"), // Last time an enrichment result was approved for this model
  // Particle Recycler partner enrichment (Costco-sourced)
  particlesId: uuid("particles_id"), // appliance_id returned by Particles
  particlesCheckedAt: timestamp("particles_checked_at"), // Last time we hit the Particles API for this SKU (any outcome)
  particlesEnrichedAt: timestamp("particles_enriched_at"), // Last time an admin applied Particles fields to this model
  particlesStatus: text("particles_status"), // 'found' | 'not_found' | 'pending' | 'error'
  costcoPriceUsd: decimal("costco_price_usd", { precision: 10, scale: 2 }), // Costco selling price (admin-only, never displayed publicly)
  costcoOriginalPriceUsd: decimal("costco_original_price_usd", { precision: 10, scale: 2 }), // Pre-discount Costco price (admin-only)
  costcoPriceCapturedAt: timestamp("costco_price_captured_at"),
  // Virtual pallet flags (used by AC virtual-pallet system)
  isPallet: boolean("is_pallet").default(false).notNull(), // True for synthetic pallet "products"
  isPalletOnly: boolean("is_pallet_only").default(false).notNull(), // True for constituent SKUs only sold as part of pallets (hidden from direct purchase)
  boxType: text("box_type"), // Packaging code on the SKU itself (e.g., "EOB", "EGB", "EAB", "OEM"). Distinct from inventory.boxType which is per-row metadata.
  boxTypeDescription: text("box_type_description"), // Human-friendly box description ("brown box with artwork")
  descriptor: text("descriptor"), // Free-text human-readable category descriptor shown after brand+SKU on pallet composition lines (e.g. "Robot Vacuums", "Upright Vacuums").
  featured: boolean("featured").default(false).notNull(), // Hand-picked for the Featured Deals row on /showroom landing
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Inventory status types
export const INVENTORY_STATUSES = ["in-stock", "en-route", "available-to-order"] as const;
export type InventoryStatus = typeof INVENTORY_STATUSES[number];

// Import modes for Excel uploads
export const IMPORT_MODES = ["add", "replace"] as const;
export type ImportMode = typeof IMPORT_MODES[number];

// Sold reason types - tracks why inventory was marked unavailable
export const SOLD_REASONS = ["customer_order", "sohnen_sold", "damaged", "returned_to_supplier", "manual"] as const;
export type SoldReason = typeof SOLD_REASONS[number];

// Inventory type classification — two orthogonal dimensions:
//   (a) serialized vs bulk_quantity — do we know the exact serial of each unit?
//   (b) local vs remote             — do we physically possess it (Burnsville)
//                                     or does a 3PL (e.g. Otay Mesa) hold it?
//
//   serialized           — local + per-unit serial known (Burnsville fridges/TVs).
//   bulk_quantity        — local + counted by quantity (Burnsville vacuums/SA);
//                          our picker enters the outbound serial at ship time.
//   serialized_remote    — remote + per-unit serial known (e.g. Otay sends us a
//                          serial list for B+/B- fridges; WE dictate which serial
//                          they ship).
//   bulk_quantity_remote — remote + count only (e.g. Otay A-grade fridges and
//                          remote vacuums/blenders); WE tell them model + grade,
//                          THEY ship and report back the serial they sent.
export const INVENTORY_TYPES = ["serialized", "bulk_quantity", "serialized_remote", "bulk_quantity_remote"] as const;
export type InventoryType = typeof INVENTORY_TYPES[number];

export const inventory = pgTable("inventory", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  addressId: uuid("address_id").references(() => supplierAddresses.id),
  itemNumber: text("item_number").notNull(),
  grade: text("grade").notNull(), // Category-specific grades: "A", "B+", "B-", "X", "B-Complete", "B-Incomplete", "VX", "Z", etc.
  quantity: integer("quantity").default(0),
  sellingPrice: decimal("selling_price", { precision: 10, scale: 2 }),
  promoPrice: decimal("promo_price", { precision: 10, scale: 2 }),
  accessoriesStatus: text("accessories_status"),
  accessoryNotes: text("accessory_notes"), // For B-Incomplete TVs: tracks missing accessories like "No remote", "Missing stand"
  inventoryStatus: text("inventory_status").notNull().default("in-stock"), // "in-stock", "en-route", "available-to-order"
  inventoryType: text("inventory_type").$type<InventoryType>().notNull().default("serialized"), // How this row is physically tracked — see INVENTORY_TYPES above
  eta: timestamp("eta"), // Estimated arrival date for "en-route" or "available-to-order" items
  isAvailable: boolean("is_available").default(true),
  isShowroomEligible: boolean("is_showroom_eligible").default(false),
  soldReason: text("sold_reason"), // Why inventory became unavailable: customer_order, sohnen_sold, damaged, returned_to_supplier, manual
  soldAt: timestamp("sold_at"), // When inventory was marked as sold/unavailable
  soldQuantity: integer("sold_quantity"), // For A-grade: quantity reduction from supplier sale (when isAvailable stays true but qty decreased)
  boxType: text("box_type"), // Packaging type from supplier: "GENERIC BOX", "ORIGINAL BOX", "2-COLOR LINE ART RETAIL BOX", "GENERIC W/ LABEL", etc.
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Serialized Units - tracks individual units (was previously named `b_grade_units`).
// Note on legacy naming: when the table was renamed in
// `server/migrations/rename-b-grade-units-to-serialized-units.ts` we
// intentionally kept some legacy DB-level identifiers (column name
// `linked_b_grade_unit_id` on expected_inventory_items, index
// `serial_events_b_grade_unit_idx` on serial_events) to avoid touching
// data-bearing columns. They map to the new Drizzle field names
// (`linkedSerializedUnitId`, `serializedUnitIdx`) and reference the
// renamed `serialized_units` table correctly.
// Lifecycle status — Task #187. Terminal-retirement flag so scrapped/salvaged
// returned units stay in the system as an audit record but are excluded from
// every active-inventory surface (unassigned-units, inventory queries,
// pickable/allocatable lookups, location-missing health checks).
export const SERIALIZED_UNIT_LIFECYCLE_STATUSES = [
  "ACTIVE",
  "RETIRED_SCRAPPED",
  "RETIRED_SALVAGE",
] as const;
export type SerializedUnitLifecycleStatus = typeof SERIALIZED_UNIT_LIFECYCLE_STATUSES[number];

export const serializedUnits = pgTable("serialized_units", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  inventoryId: uuid("inventory_id").references(() => inventory.id).notNull(),
  serialNumber: text("serial_number").notNull(),
  videoUrl: text("video_url"),
  thumbnailUrl: text("thumbnail_url"), // Extracted from video at 1-second mark using ffmpeg
  noVideoExpected: boolean("no_video_expected").default(false), // True = show placeholder image instead of video
  notes: text("notes"),
  firstSeenDate: timestamp("first_seen_date").default(sql`now()`),
  isShipped: boolean("is_shipped").default(false), // True when item has physically left the building
  allocatedOrderItemId: uuid("allocated_order_item_id"), // Order item this unit is allocated to (prevents overselling)
  allocatedAt: timestamp("allocated_at"), // When this unit was allocated to an order
  palletId: uuid("pallet_id"), // Link to pallet (added via migration)
  grade: text("grade"), // Unit-level grade: A, B+, B, B- (mandatory before put-away)
  manifestDeleted: boolean("manifest_deleted").default(false), // True when origin manifest was deleted (unit remains but flagged)
  // Task #187 — terminal retirement
  lifecycleStatus: varchar("lifecycle_status", { length: 32 }).$type<SerializedUnitLifecycleStatus>().notNull().default("ACTIVE"),
  retiredAt: timestamp("retired_at"),
  retiredReason: text("retired_reason"),
  retiredByUserId: varchar("retired_by_user_id"),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  uniqueSerialNumber: uniqueIndex("serialized_units_serial_number_unique").on(table.serialNumber),
  lifecycleStatusIdx: index("serialized_units_lifecycle_status_idx").on(table.lifecycleStatus),
}));

// Serialized Unit Event Types - event-sourced inventory tracking
export const SERIALIZED_UNIT_EVENT_TYPES = [
  "RECEIVED",       // Unit first scanned/received from manifest
  "GRADED",         // Unit grade assigned or changed
  "PUTAWAY",        // Unit placed in warehouse location
  "MOVED",          // Unit moved to different location
  "ALLOCATED",      // Unit allocated to an order
  "DEALLOCATED",    // Unit removed from order allocation
  "PICKED",         // Unit picked for shipment
  "SHIPPED",        // Unit shipped to customer
  "RETURNED",       // Unit returned from customer
  "DAMAGED",        // Unit marked as damaged
  "VIDEO_ADDED",    // Video URL added or updated
  "NOTES_UPDATED",  // Notes field updated
  "ADJUSTED",       // Manual inventory adjustment
  "RETURN_EXPECTED",   // Inbound return: refund logged, unit expected back
  "RETURN_IN_TRANSIT", // Inbound return: carrier picked up, in transit to warehouse
  "RETURN_RECEIVED",   // Inbound return: physically received and re-stocked at a grade
  "RETURN_SCRAPPED",   // Inbound return: physically received but damaged beyond resale
  "RETURN_SALVAGED",   // Inbound return: physically received, parted-out for salvage (Task #187)
] as const;
export type SerializedUnitEventType = typeof SERIALIZED_UNIT_EVENT_TYPES[number];

// Serialized Unit Events - immutable audit trail for all unit state changes
export const serializedUnitEvents = pgTable("serialized_unit_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  unitId: uuid("unit_id").references(() => serializedUnits.id).notNull(),
  eventType: text("event_type").$type<SerializedUnitEventType>().notNull(),
  userId: varchar("user_id").references(() => users.id), // Who performed the action (null for system actions)
  orderId: uuid("order_id").references(() => orders.id), // Related order for allocation/shipment events
  warehouseLocationId: uuid("warehouse_location_id").references(() => warehouseLocations.id), // For location-related events
  previousValue: text("previous_value"), // JSON of previous state (for grade changes, etc.)
  newValue: text("new_value"), // JSON of new state
  notes: text("notes"), // Additional context
  createdAt: timestamp("created_at").default(sql`now()`).notNull(),
}, (table) => ({
  unitIdIdx: index("serialized_unit_events_unit_id_idx").on(table.unitId),
  createdAtIdx: index("serialized_unit_events_created_at_idx").on(table.createdAt),
  eventTypeIdx: index("serialized_unit_events_type_idx").on(table.eventType),
}));

// Pallets - track pallet IDs and their relationship to serial numbers
// Design supports 1:1 (refrigerators) and 1:many (future products like vacuums)
// Note: manifestId forward reference handled via text field with application-level enforcement
export const pallets = pgTable("pallets", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  palletId: text("pallet_id").notNull().unique(), // Scannable pallet barcode/ID
  warehouseId: uuid("warehouse_id").references(() => supplierAddresses.id), // Current location
  manifestId: uuid("manifest_id"), // Link to inventory_manifests (forward reference - FK added after table created)
  status: text("status").notNull().default("expected"), // "expected", "received", "shipped"
  weightLbs: decimal("weight_lbs", { precision: 10, scale: 2 }), // Pallet weight in pounds for freight calculations
  receivedAt: timestamp("received_at"),
  receivedByUserId: varchar("received_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// User-Warehouse Permissions - control which users can access which warehouses
export const userWarehousePermissions = pgTable("user_warehouse_permissions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => supplierAddresses.id).notNull(),
  canReceive: boolean("can_receive").default(true), // Permission to receive inventory
  canShip: boolean("can_ship").default(true), // Permission to ship inventory
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Expected Inventory Manifests - uploaded CSV/Excel files with expected arrivals
export const inventoryManifests = pgTable("inventory_manifests", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  fileName: text("file_name").notNull(),
  supplierLpn: text("supplier_lpn"), // Supplier shipment reference (e.g., "SFS251589")
  warehouseId: uuid("warehouse_id").references(() => supplierAddresses.id).notNull(),
  uploadedByUserId: varchar("uploaded_by_user_id").references(() => users.id).notNull(),
  totalItems: integer("total_items").default(0),
  receivedItems: integer("received_items").default(0),
  eta: timestamp("eta"), // Expected arrival date for the entire manifest
  availableDate: date("available_date"), // Date items become available (ETA + 1 day typically)
  status: text("status").notNull().default("pending"), // "pending", "partial", "complete"
  notes: text("notes"),
  receivingNotes: text("receiving_notes"), // Notes visible to receiving workers during pallet scan
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Resolution Status for manifest reconciliation
export const RESOLUTION_STATUSES = ["PENDING", "MATCHED", "SHORT", "EXTRA"] as const;
export type ResolutionStatus = typeof RESOLUTION_STATUSES[number];

// Source type for expected inventory items
export const SOURCE_TYPES = ["MANIFEST", "EXTRA"] as const;
export type SourceType = typeof SOURCE_TYPES[number];

// Expected Inventory Item Status - full lifecycle tracking
// Warehouse phases: expected -> location_pending -> received
// Fulfillment phases: allocated -> picked -> shipped
// Terminal states: sold, returned, cancelled
export const EXPECTED_ITEM_STATUSES = [
  "expected",        // On manifest, not yet scanned
  "location_pending", // Scanned but needs location assignment
  "received",        // In warehouse with assigned location
  "allocated",       // Reserved for an order
  "picked",          // Picked from location for shipment
  "shipped",         // Left the building
  "sold",            // Transaction complete
  "returned",        // Returned from customer
  "cancelled",       // Manifest item cancelled/voided
] as const;
export type ExpectedItemStatus = typeof EXPECTED_ITEM_STATUSES[number];

// Serial Event Types - all lifecycle events for audit trail
export const SERIAL_EVENT_TYPES = [
  "MANIFEST_UPLOADED",    // Item appeared on manifest
  "SCAN_RECEIVED",        // Scanned during pallet receiving
  "LOCATION_ASSIGNED",    // Placed in warehouse location
  "LOCATION_MOVED",       // Moved to different location
  "LOCATION_CLEARED",     // Removed from location
  "ORDER_ALLOCATED",      // Reserved for customer order
  "ORDER_DEALLOCATED",    // Allocation released
  "PICK_STARTED",         // Pick workflow began
  "PICK_CONFIRMED",       // Item confirmed picked
  "STAGED_FOR_SHIPMENT",  // Moved to outbound lane
  "SHIPPED",              // Left the building
  "DELIVERED",            // Confirmed delivered (from carrier)
  "RETURNED",             // Customer returned item
  "GRADE_CHANGED",        // Grade was modified
  "STATUS_CHANGED",       // Generic status change
  "NOTE_ADDED",           // Note added to unit
  "VIDEO_UPLOADED",       // Video added to unit
] as const;
export type SerialEventType = typeof SERIAL_EVENT_TYPES[number];

// Expected Inventory Items - individual items from manifests OR discovered extras
// This is the single reconciliation ledger for all inventory items
export const expectedInventoryItems = pgTable("expected_inventory_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  manifestId: uuid("manifest_id").references(() => inventoryManifests.id), // Nullable for EXTRA items discovered without manifest
  sourceType: text("source_type").$type<SourceType>().notNull().default("MANIFEST"), // MANIFEST = from uploaded manifest, EXTRA = discovered during receiving
  palletId: text("pallet_id"), // The pallet ID from the manifest (nullable for EXTRA items)
  modelNumber: text("model_number"), // e.g., "RF29BB860012"
  modelName: text("model_name"),
  brand: text("brand"), // e.g., "LG", "Samsung"
  serialNumber: text("serial_number").notNull(),
  grade: text("grade"), // Grade from manifest: "A", "B+", "B-", etc. (used to find correct inventory record)
  quantity: integer("quantity").default(1), // Usually 1, but allows for multi-unit pallets
  eta: timestamp("eta"), // Item-specific ETA (overrides manifest ETA if set)
  status: text("status").notNull().default("expected"), // "expected", "received", "cancelled"
  receivedAt: timestamp("received_at"),
  receivedByUserId: varchar("received_by_user_id").references(() => users.id),
  linkedSerializedUnitId: uuid("linked_b_grade_unit_id"), // Link to created inventory unit after receiving (works for all grades)
  linkedPalletId: uuid("linked_pallet_id"), // Link to created pallet record after receiving
  resolutionStatus: text("resolution_status").$type<ResolutionStatus>().default("PENDING"), // Reconciliation: PENDING, MATCHED (found in inventory), SHORT (never received), EXTRA (received but not on manifest)
  resolutionNotes: text("resolution_notes"), // Notes about the reconciliation decision
  receivedWarehouseLocationId: uuid("received_warehouse_location_id").references(() => warehouseLocations.id), // Where item was placed after receiving
  discoveredWarehouseId: uuid("discovered_warehouse_id").references(() => supplierAddresses.id), // For EXTRA items: where they were discovered
  discoveredByUserId: varchar("discovered_by_user_id").references(() => users.id), // For EXTRA items: who discovered them
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Supplier Sales Log - tracks items sold by suppliers (e.g., Sohnen) that disappeared from inventory
// Used to track when items are removed or quantities reduced in supplier Excel uploads
export const supplierSalesLog = pgTable("supplier_sales_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  inventoryId: uuid("inventory_id").references(() => inventory.id),
  modelId: uuid("model_id").references(() => models.id),
  supplierId: uuid("supplier_id").references(() => suppliers.id),
  addressId: uuid("address_id").references(() => supplierAddresses.id),
  brand: text("brand"),
  mfgSku: text("mfg_sku"),
  grade: text("grade"),
  saleType: text("sale_type").notNull(), // "full_removal" = item disappeared, "quantity_reduction" = quantity decreased
  quantitySold: integer("quantity_sold").default(1),
  previousQuantity: integer("previous_quantity"),
  newQuantity: integer("new_quantity"),
  previousPrice: decimal("previous_price", { precision: 10, scale: 2 }),
  importFileName: text("import_file_name"), // Name of the Excel file that triggered this
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  supplierIdx: index("supplier_sales_log_supplier_idx").on(table.supplierId),
  modelIdx: index("supplier_sales_log_model_idx").on(table.modelId),
  createdAtIdx: index("supplier_sales_log_created_at_idx").on(table.createdAt),
}));

// Serial Events - complete audit trail for serial number lifecycle
// Every significant event for any serialized item is recorded here
export const serialEvents = pgTable("serial_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  serialNumber: text("serial_number").notNull(), // The serial being tracked
  eventType: text("event_type").$type<SerialEventType>().notNull(),
  occurredAt: timestamp("occurred_at").default(sql`now()`).notNull(),
  userId: varchar("user_id").references(() => users.id), // Who triggered the event (nullable for system events)
  expectedItemId: uuid("expected_item_id").references(() => expectedInventoryItems.id), // Link to manifest item
  serializedUnitId: uuid("b_grade_unit_id"), // Link to serialized_unit (can't reference due to circular dep). DB column intentionally retains the legacy `b_grade_unit_id` name from the table rename — see `rename-b-grade-units-to-serialized-units.ts`.
  orderId: uuid("order_id").references(() => orders.id), // Link to order if applicable
  shipmentId: uuid("shipment_id"), // Link to outbound_shipment if applicable
  warehouseLocationId: uuid("warehouse_location_id").references(() => warehouseLocations.id), // Location involved
  previousStatus: text("previous_status"), // Status before this event
  newStatus: text("new_status"), // Status after this event
  metadata: jsonb("metadata"), // Additional event-specific data
  notes: text("notes"), // Human-readable notes
}, (table) => ({
  serialIdx: index("serial_events_serial_idx").on(table.serialNumber),
  eventTypeIdx: index("serial_events_event_type_idx").on(table.eventType),
  occurredAtIdx: index("serial_events_occurred_at_idx").on(table.occurredAt),
  expectedItemIdx: index("serial_events_expected_item_idx").on(table.expectedItemId),
  serializedUnitIdx: index("serial_events_b_grade_unit_idx").on(table.serializedUnitId),
}));

// Outbound Shipment Statuses
export const OUTBOUND_SHIPMENT_STATUSES = ["pending", "picking", "picked", "staged", "shipped"] as const;
export type OutboundShipmentStatus = typeof OUTBOUND_SHIPMENT_STATUSES[number];

// Outbound Shipments - tracks shipping/picking workflow
export const outboundShipments = pgTable("outbound_shipments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => supplierAddresses.id),
  pickerUserId: varchar("picker_user_id").references(() => users.id), // Who did the picking
  status: text("status").$type<OutboundShipmentStatus>().notNull().default("pending"), // "pending", "picking", "picked", "staged", "shipped"
  pickingStartedAt: timestamp("picking_started_at"),
  pickingCompletedAt: timestamp("picking_completed_at"),
  shippedAt: timestamp("shipped_at"),
  shippedByUserId: varchar("shipped_by_user_id").references(() => users.id), // Logistics manager who marked shipped
  carrierName: text("carrier_name"),
  trackingNumber: text("tracking_number"),
  proNumber: text("pro_number"), // PRO# for freight shipments (carrier reference number)
  bolDocumentId: uuid("bol_document_id"), // Reference to uploaded BOL document in orderDocuments
  estimatedDelivery: timestamp("estimated_delivery"),
  notes: text("notes"),
  totalPallets: integer("total_pallets"), // Total number of pallets in this shipment
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Outbound Shipment Photos - photos uploaded during picking/shipping
export const outboundShipmentPhotos = pgTable("outbound_shipment_photos", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  shipmentId: uuid("shipment_id").references(() => outboundShipments.id).notNull(),
  photoUrl: text("photo_url").notNull(),
  caption: text("caption"),
  uploadedByUserId: varchar("uploaded_by_user_id").references(() => users.id),
  uploadedAt: timestamp("uploaded_at").default(sql`now()`),
});

// Outbound Shipment Pallet Scans - tracks which pallets were scanned during picking
export const outboundShipmentPalletScans = pgTable("outbound_shipment_pallet_scans", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  shipmentId: uuid("shipment_id").references(() => outboundShipments.id).notNull(),
  palletId: uuid("pallet_id").references(() => pallets.id).notNull(),
  scannedByUserId: varchar("scanned_by_user_id").references(() => users.id),
  scannedAt: timestamp("scanned_at").default(sql`now()`),
  confirmed: boolean("confirmed").default(false), // Whether picker confirmed this pallet
});

// Outbound Lanes - warehouse-specific staging lanes for outbound shipments (OUTBOUND 1, 2, 3)
export const outboundLanes = pgTable("outbound_lanes", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  warehouseId: uuid("warehouse_id").references(() => supplierAddresses.id).notNull(),
  name: text("name").notNull(), // "OUTBOUND 1", "OUTBOUND 2", etc.
  sortOrder: integer("sort_order").default(0), // For display ordering
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  warehouseLaneIdx: uniqueIndex("unique_warehouse_lane").on(table.warehouseId, table.name),
}));

// Shipment Pallets - pallets built for a shipment with labeling info
export const shipmentPallets = pgTable("shipment_pallets", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  shipmentId: uuid("shipment_id").references(() => outboundShipments.id).notNull(),
  palletNumber: integer("pallet_number").notNull(), // 1, 2, 3... (for "Pallet X of Y")
  outboundLaneId: uuid("outbound_lane_id").references(() => outboundLanes.id), // Which lane pallet is staged in
  contents: text("contents"), // Human-readable description of pallet contents
  weightLbs: decimal("weight_lbs", { precision: 10, scale: 2 }), // Total weight
  labelPrintedAt: timestamp("label_printed_at"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Shipment Pallet Items - individual items/units assigned to a shipment pallet
export const shipmentPalletItems = pgTable("shipment_pallet_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  shipmentPalletId: uuid("shipment_pallet_id").references(() => shipmentPallets.id).notNull(),
  orderItemId: uuid("order_item_id").references(() => orderItems.id).notNull(),
  unitId: uuid("unit_id").references(() => serializedUnits.id), // For B-grade specific units
  quantity: integer("quantity").default(1), // For A-grade items, may be > 1
  pickedAt: timestamp("picked_at").default(sql`now()`),
  pickedByUserId: varchar("picked_by_user_id").references(() => users.id),
});

// Order Document Types
export const ORDER_DOCUMENT_TYPES = ["BOL", "PICKUP_SIGNATURE", "INVOICE", "OTHER"] as const;
export type OrderDocumentType = typeof ORDER_DOCUMENT_TYPES[number];

// Order Documents - BOL uploads, signed pickup forms, invoices
export const orderDocuments = pgTable("order_documents", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  documentType: varchar("document_type").$type<OrderDocumentType>().notNull(),
  documentUrl: text("document_url").notNull(),
  fileName: text("file_name"),
  notes: text("notes"),
  uploadedByUserId: varchar("uploaded_by_user_id").references(() => users.id),
  uploadedAt: timestamp("uploaded_at").default(sql`now()`),
});

// Warehouse Location Types
export const WAREHOUSE_LOCATION_TYPES = ["storage", "outbound", "receiving"] as const;
export type WarehouseLocationType = typeof WAREHOUSE_LOCATION_TYPES[number];

// Warehouse Locations - physical storage locations within a warehouse (A.1, A.2, B.1, etc.)
export const warehouseLocations = pgTable("warehouse_locations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  warehouseId: uuid("warehouse_id").references(() => supplierAddresses.id).notNull(),
  row: text("row").notNull(), // e.g., "A", "B", "C", "D", "OUTBOUND"
  slot: integer("slot").notNull(), // e.g., 1, 2, 3... 26
  label: text("label").notNull(), // Human-readable: "A.1", "A.2", "OUTBOUND.1", etc.
  locationType: varchar("location_type").$type<WarehouseLocationType>().default("storage"), // storage (default), outbound (staging for shipment), receiving
  isActive: boolean("is_active").default(true), // Can be disabled if location is damaged/unavailable
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  uniqueLocation: uniqueIndex("unique_warehouse_location").on(table.warehouseId, table.row, table.slot),
}));

// Unit Locations - tracks current and historical location assignments for Serialized units
export const unitLocations = pgTable("unit_locations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  unitId: uuid("unit_id").references(() => serializedUnits.id).notNull(),
  locationId: uuid("location_id").references(() => warehouseLocations.id).notNull(),
  isCurrent: boolean("is_current").default(true), // Only one current location per unit
  assignedAt: timestamp("assigned_at").default(sql`now()`),
  assignedByUserId: varchar("assigned_by_user_id").references(() => users.id),
  clearedAt: timestamp("cleared_at"), // When item was removed from location
  clearedByUserId: varchar("cleared_by_user_id").references(() => users.id),
  clearReason: text("clear_reason"), // "shipped", "moved", "damaged", etc.
}, (table) => ({
  currentLocationIndex: index("idx_unit_current_location").on(table.unitId, table.isCurrent),
}));

// Inventory Location Assignments - tracks which warehouse bin holds how many units of an aggregate-tracked SKU
// This is the quantity-tracked equivalent of unit_locations (which handles individual serialized units)
// Supports: 1 inventory SKU → many locations; 1 location → many inventory SKUs
export const inventoryLocationAssignments = pgTable("inventory_location_assignments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  inventoryId: uuid("inventory_id").references(() => inventory.id, { onDelete: 'cascade' }).notNull(),
  locationId: uuid("location_id").references(() => warehouseLocations.id, { onDelete: 'cascade' }).notNull(),
  quantity: integer("quantity").notNull().default(0),
  notes: text("notes"),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  uniqueInventoryLocation: uniqueIndex("unique_inv_location_assignment").on(table.inventoryId, table.locationId),
}));

// Shopping Cart tables - one cart per user
export const carts = pgTable("carts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull().unique(), // One cart per user
  poNumber: text("po_number"), // Purchase order number auto-saved from checkout, persists until order is placed
  shippingMethod: varchar("shipping_method").$type<ShippingMethod>(), // FREIGHT or LOCAL_PICKUP - null means not selected yet
  selectedPickupWarehouseId: uuid("selected_pickup_warehouse_id").references(() => supplierAddresses.id), // Selected pickup warehouse for LOCAL_PICKUP
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const cartItems = pgTable("cart_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  cartId: uuid("cart_id").references(() => carts.id).notNull(),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  inventoryId: uuid("inventory_id").references(() => inventory.id),
  unitId: uuid("unit_id").references(() => serializedUnits.id), // For B-grade specific units
  grade: text("grade").notNull(), // "A", "B+", "B-"
  quantity: integer("quantity").notNull().default(1),
  price: decimal("price", { precision: 10, scale: 2 }).notNull(), // Price at time of add to cart
  addedAt: timestamp("added_at").default(sql`now()`), // For tracking activity
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  // A serialized unit is one physical item — it can only appear once per cart.
  // Partial unique index (unit_id IS NOT NULL) guards against duplicate rows
  // even under concurrent add-to-cart requests.
  uniqueCartUnit: uniqueIndex("cart_items_cart_unit_unique")
    .on(table.cartId, table.unitId)
    .where(sql`unit_id IS NOT NULL`),
}));

export const ORDER_STATUSES = ["pending", "awaiting_payment", "confirmed", "picking", "picked", "paid", "processing", "shipped", "delivered", "cancelled", "unfillable"] as const;
export type OrderStatus = typeof ORDER_STATUSES[number];

export const orders = pgTable("orders", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  displayOrderNumber: varchar("display_order_number", { length: 20 }).unique(), // Human-readable order number (OB10000, OB10001, etc.)
  userId: varchar("user_id").references(() => users.id), // Nullable for backward compatibility
  accountId: uuid("account_id").references(() => accounts.id), // For B2B accounts
  addressId: uuid("address_id").references(() => addresses.id), // For B2B shipping address
  apiCredentialId: uuid("api_credential_id"), // For orders created via public API - tracks which API credential created the order
  customerInfo: jsonb("customer_info"),
  truckSize: text("truck_size"), // "24ft_box", "53ft_semi", "40ft_container", "unknown"
  capacityUsedPercentage: decimal("capacity_used_percentage", { precision: 5, scale: 2 }),
  totalAmount: decimal("total_amount", { precision: 10, scale: 2 }),
  // Shipping method
  shippingMethod: varchar("shipping_method").$type<ShippingMethod>().default("FREIGHT"), // FREIGHT or LOCAL_PICKUP
  pickupWarehouseId: uuid("pickup_warehouse_id").references(() => supplierAddresses.id), // Warehouse ID for local pickup
  pickupFee: decimal("pickup_fee", { precision: 10, scale: 2 }), // Local pickup fee amount
  // Freight quote details
  freightCarrierId: uuid("freight_carrier_id").references(() => freightCarriers.id),
  freightCarrierName: text("freight_carrier_name"), // Carrier name (supports both DB and API carriers)
  freightCarrierType: text("freight_carrier_type").$type<FreightCarrierType>(), // "DATABASE", "UBER_FREIGHT", or "CXT_SOFTWARE"
  freightServiceLevel: text("freight_service_level"), // Service level (e.g., "Standard", "Express")
  freightQuoteId: text("freight_quote_id"), // External quote ID (e.g., Uber Freight quote UUID)
  freightBaseAmount: decimal("freight_base_amount", { precision: 10, scale: 2 }),
  freightFuelSurcharge: decimal("freight_fuel_surcharge", { precision: 10, scale: 2 }),
  freightAccessorials: jsonb("freight_accessorials"), // Array of { code, name, amount }
  freightTotal: decimal("freight_total", { precision: 10, scale: 2 }),
  customFreightNotes: text("custom_freight_notes"), // Admin notes for custom freight quotes
  shippingState: text("shipping_state"), // Destination state code
  hasLoadingDock: boolean("has_loading_dock").default(false),
  hasForklift: boolean("has_forklift").default(false),
  // Sales tax (for consumer accounts without W9)
  salesTaxRate: decimal("sales_tax_rate", { precision: 5, scale: 4 }), // Tax rate (e.g., 0.0775 for 7.75%)
  salesTaxAmount: decimal("sales_tax_amount", { precision: 10, scale: 2 }), // Calculated sales tax
  // Order status and workflow
  status: text("status").default("pending"), // "pending", "awaiting_payment", "paid", "processing", "shipped", "delivered", "cancelled"
  // Payment tracking
  poNumber: text("po_number"), // Purchase order number from customer checkout
  paymentMethod: text("payment_method"), // "wire_transfer", "credit_card", "ach", etc.
  paymentDate: timestamp("payment_date"),
  wireConfirmationNumber: text("wire_confirmation_number"), // Wire transfer confirmation number
  paymentBankAccount: text("payment_bank_account"), // Which bank account received payment (Sohnen, supplier accounts, etc.)
  paymentDeadline: timestamp("payment_deadline"), // 24 hours from order creation
  // Customer-arranged freight (CUSTOMER_FREIGHT) fields
  customerFreightCarrierName: text("customer_freight_carrier_name"), // Name of customer's carrier
  customerFreightPickupDate: date("customer_freight_pickup_date"), // Requested pickup date
  customerFreightPickupTime: text("customer_freight_pickup_time"), // Requested pickup time slot
  customerFreightContactName: text("customer_freight_contact_name"), // Carrier contact name
  customerFreightContactPhone: text("customer_freight_contact_phone"), // Carrier contact phone
  customerFreightNotes: text("customer_freight_notes"), // Special instructions for pickup
  // Shipping tracking
  shippingCarrier: text("shipping_carrier"), // Actual carrier used for shipping
  trackingNumber: text("tracking_number"),
  shipDate: timestamp("ship_date"),
  deliveryDate: timestamp("delivery_date"),
  // Store credit applied at checkout
  storeCreditApplied: decimal("store_credit_applied", { precision: 10, scale: 2 }),
  // Cancellation tracking
  cancelledAt: timestamp("cancelled_at"),
  cancelReason: text("cancel_reason"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const orderItems = pgTable("order_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  modelId: uuid("model_id").references(() => models.id), // Direct model reference for tracking pending demand
  inventoryId: uuid("inventory_id").references(() => inventory.id),
  unitId: uuid("unit_id").references(() => serializedUnits.id),
  quantity: integer("quantity").default(1),
  priceAtPurchase: decimal("price_at_purchase", { precision: 10, scale: 2 }),
  grade: text("grade").notNull(),
  // Outbound serial captured at ship time. Used for:
  //   - bulk_quantity items: serial of the unit physically picked off the shelf (return fraud prevention)
  //   - serialized_remote A-grade items: FS serial confirmed by Sohnen when the unit ships from Otay
  //   - bulk_quantity_remote items: 3PL ships and reports back the actual outbound serial
  // Always nullable; for serialized inventory the canonical serial lives on serialized_units via unitId.
  shippedSerialNumber: text("shipped_serial_number"),
  // Snapshot of expected ship date for this line, captured at order creation time
  // from the same derivation used in the cart hydration logic. Stored as ISO date
  // string (YYYY-MM-DD). NULL means "ships now / available immediately".
  // UI must NEVER recompute — always read this snapshot.
  expectedShipDate: date("expected_ship_date"),
});

// Order History - audit trail for all order changes
export const orderHistory = pgTable("order_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  userId: varchar("user_id").references(() => users.id), // Who made the change
  action: text("action").notNull(), // "created", "status_changed", "payment_confirmed", "shipped", "cancelled", "item_added", "item_removed"
  changes: jsonb("changes"), // JSON object describing what changed
  notes: text("notes"), // Optional notes about the change
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Payment Methods (used for tracking non-wire-transfer payments)
export const PAYMENT_METHOD_TYPES = ["WIRE", "CASH", "CHECK", "DIGITAL", "OTHER"] as const;
export type PaymentMethodType = typeof PAYMENT_METHOD_TYPES[number];

// Digital Payment Platforms
export const DIGITAL_PLATFORMS = ["PAYPAL", "VENMO", "AMAZON_PAY", "ZELLE", "CASHAPP", "OTHER"] as const;
export type DigitalPlatform = typeof DIGITAL_PLATFORMS[number];

// Order Payments - Multi-payment support (partial payments, different methods)
export const orderPayments = pgTable("order_payments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id, { onDelete: 'cascade' }).notNull(),
  paymentMethod: text("payment_method").$type<PaymentMethodType>().notNull(), // WIRE, CASH, CHECK, DIGITAL, OTHER
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(), // Payment amount (in dollars)
  // Wire transfer specific
  wireConfirmationNumber: text("wire_confirmation_number"),
  bankAccount: text("bank_account"), // Which bank received the wire
  bankNote: text("bank_note"), // Additional bank info if "Other" bank selected
  // Check specific
  checkNumber: text("check_number"),
  checkPhotoUrl: text("check_photo_url"), // URL to uploaded check photo
  // Digital payment specific
  digitalPlatform: text("digital_platform").$type<DigitalPlatform>(), // PAYPAL, VENMO, AMAZON_PAY, ZELLE, CASHAPP, OTHER
  digitalReference: text("digital_reference"), // Transaction ID or reference number
  // Other payment method
  otherDescription: text("other_description"), // Description if "OTHER" method selected
  // Tracking
  recordedById: varchar("recorded_by_id").references(() => users.id).notNull(), // Admin who recorded this payment
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Account Statement Emails (Net 15 audit + dedupe log)
export const accountStatementEmails = pgTable("account_statement_emails", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id, { onDelete: 'cascade' }).notNull(),
  accountId: uuid("account_id").notNull(),
  trigger: text("trigger").notNull(), // DAY_15 | PAYMENT | TERMS_CHANGE
  paymentId: uuid("payment_id"),
  recipientEmail: text("recipient_email").notNull(),
  sentAt: timestamp("sent_at").default(sql`now()`).notNull(),
});

// Shipment System - Multi-shipment support with tracking
export const SHIPMENT_STATUSES = ["preparing", "shipped", "in_transit", "delivered", "cancelled", "ready_for_pickup"] as const;
export type ShipmentStatus = typeof SHIPMENT_STATUSES[number];

export const orderShipments = pgTable("order_shipments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id, { onDelete: 'cascade' }).notNull(),
  shipmentNumber: integer("shipment_number").notNull(), // Sequential number within order (1, 2, 3...)
  status: text("status").notNull().default("preparing"), // "preparing", "shipped", "in_transit", "delivered", "cancelled"
  shippingMethod: varchar("shipping_method").$type<ShippingMethod>().default("FREIGHT"), // "FREIGHT" or "LOCAL_PICKUP"
  localPickupWarehouseId: uuid("local_pickup_warehouse_id").references(() => supplierAddresses.id), // Warehouse for local pickup (null for freight)
  shippedDate: timestamp("shipped_date"),
  estimatedDelivery: timestamp("estimated_delivery"),
  actualDelivery: timestamp("actual_delivery"),
  notes: text("notes"),
  cachedWeightLbs: decimal("cached_weight_lbs", { precision: 10, scale: 2 }), // Optional cached total weight for performance
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  // Unique constraint on order + shipment number
  uniqueOrderShipment: uniqueIndex("order_shipments_order_id_shipment_number_key").on(table.orderId, table.shipmentNumber),
}));

export const shipmentTracking = pgTable("shipment_tracking", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  shipmentId: uuid("shipment_id").references(() => orderShipments.id, { onDelete: 'cascade' }).notNull(),
  carrierId: uuid("carrier_id").references(() => freightCarriers.id), // Optional - for database carriers
  carrierName: text("carrier_name").notNull(), // Required - carrier name for display
  trackingNumber: text("tracking_number").notNull(),
  trackingUrl: text("tracking_url"), // Precomputed full URL with tracking number interpolated
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const shipmentItems = pgTable("shipment_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  shipmentId: uuid("shipment_id").references(() => orderShipments.id, { onDelete: 'cascade' }).notNull(),
  orderItemId: uuid("order_item_id").references(() => orderItems.id, { onDelete: 'cascade' }).notNull(),
  quantity: integer("quantity").notNull(), // How many of this item in this shipment
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  // Index for allocation lookup performance
  orderItemIdx: index("shipment_items_order_item_id_idx").on(table.orderItemId),
}));

export const searchQuota = pgTable("search_quota", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  date: text("date").notNull().unique(), // YYYY-MM-DD format
  searchCount: integer("search_count").default(0).notNull(),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const categoryIcons = pgTable("category_icons", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  slug: text("slug").notNull().unique(),
  iconUrl: text("icon_url"),
  prompt: text("prompt"),
  generatedAt: timestamp("generated_at"),
  createdAt: timestamp("created_at").default(sql`now()`),
});

export type CategoryIcon = typeof categoryIcons.$inferSelect;
export type InsertCategoryIcon = typeof categoryIcons.$inferInsert;

// Product Type Images - AI-generated hero images for equipment types (Johnson Fitness)
export const productTypeImages = pgTable("product_type_images", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  productType: text("product_type").notNull().unique(), // e.g., "treadmill", "elliptical", "recumbent-bike"
  displayName: text("display_name").notNull(), // e.g., "Treadmill", "Elliptical Trainer"
  imageUrl: text("image_url"),
  thumbnailUrl: text("thumbnail_url"),
  prompt: text("prompt"),
  styleToken: text("style_token"), // For consistent styling across generations
  status: text("status").notNull().default("pending"), // "pending", "generating", "approved", "rejected"
  generatedAt: timestamp("generated_at"),
  approvedAt: timestamp("approved_at"),
  approvedByUserId: varchar("approved_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export type ProductTypeImage = typeof productTypeImages.$inferSelect;
export type InsertProductTypeImage = typeof productTypeImages.$inferInsert;

// Category Hero Images - AI-generated hero/banner images for category pages (e.g., refrigerator kitchen scenes)
export const categoryHeroImages = pgTable("category_hero_images", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  categorySlug: text("category_slug").notNull(), // e.g., "refrigerators"
  sceneType: text("scene_type").notNull(), // e.g., "modern-kitchen", "traditional-kitchen", "luxury-kitchen"
  displayName: text("display_name").notNull(), // e.g., "Modern Kitchen Scene"
  imageUrl: text("image_url"),
  thumbnailUrl: text("thumbnail_url"),
  prompt: text("prompt"),
  // Optional admin-uploaded reference image (legacy single-image column,
  // kept for backward compatibility). New code reads/writes
  // `referenceImageUrls` (array, up to 4) and treats this column as a
  // fallback when the array is empty.
  referenceImageUrl: text("reference_image_url"),
  // Up to 4 admin-uploaded reference images. When present, generation uses
  // openai.images.edit with these images as the composition seed instead of
  // pure text-to-image — the most reliable way to lock the model onto a
  // specific multi-product scene.
  referenceImageUrls: text("reference_image_urls").array(),
  status: text("status").notNull().default("pending"), // "pending", "generating", "approved", "rejected"
  sortOrder: integer("sort_order").default(0), // For carousel ordering
  // True for admin-added scenes; false for scenes seeded from the built-in
  // default catalog. Admins can edit defaults but the `isCustom=false` flag
  // lets them reset back to the original seeded prompt/name.
  isCustom: boolean("is_custom").notNull().default(true),
  generatedAt: timestamp("generated_at"),
  approvedAt: timestamp("approved_at"),
  approvedByUserId: varchar("approved_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export type CategoryHeroImage = typeof categoryHeroImages.$inferSelect;
export type InsertCategoryHeroImage = typeof categoryHeroImages.$inferInsert;

// Hero Slides - Unified hero carousel management for all categories
// Allows admin to control which images appear in the hero carousel and customize content
export const HERO_SLIDE_MODES = ["fixed", "random"] as const;
export type HeroSlideMode = typeof HERO_SLIDE_MODES[number];

export const HERO_IMAGE_SOURCES = ["category_hero", "product_type"] as const;
export type HeroImageSource = typeof HERO_IMAGE_SOURCES[number];

// Slide status: active = normal carousel slide, coming_soon = promotional slide for upcoming features
export const HERO_SLIDE_STATUSES = ["active", "coming_soon"] as const;
export type HeroSlideStatus = typeof HERO_SLIDE_STATUSES[number];

// Coming Soon details structure for landing page content
export interface ComingSoonDetails {
  pageTitle?: string; // Title for the landing page
  pageDescription?: string; // Main description/body text
  features?: string[]; // List of features/benefits
  expectedDate?: string; // Expected launch date (text, e.g., "Q2 2026")
  contactEmail?: string; // Optional contact email for inquiries
  signupEnabled?: boolean; // Whether to show email signup
}

export const heroSlides = pgTable("hero_slides", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(), // Admin-friendly name for the slide
  status: text("status").$type<HeroSlideStatus>().notNull().default("active"), // "active" or "coming_soon"
  categoryId: uuid("category_id").references(() => categories.id), // Links to category for visibility filtering (optional for coming_soon)
  imageSource: text("image_source").$type<HeroImageSource>().notNull(), // "category_hero" or "product_type"
  categoryHeroImageId: uuid("category_hero_image_id").references(() => categoryHeroImages.id), // FK when source is category_hero
  productTypeImageId: uuid("product_type_image_id").references(() => productTypeImages.id), // FK when source is product_type
  mode: text("mode").$type<HeroSlideMode>().notNull().default("fixed"), // "fixed" or "random"
  // Fixed mode content
  headline: text("headline"), // Main headline text
  subheadline: text("subheadline"), // Secondary text
  ctaLabel: text("cta_label"), // Call to action button text
  ctaUrl: text("cta_url"), // CTA link destination
  // Random mode content - phrase pool as JSON array
  phrasePool: jsonb("phrase_pool").$type<string[]>(), // Array of phrases to randomly cycle through
  subheadlinePool: jsonb("subheadline_pool").$type<string[]>(), // Array of subheadlines for random mode
  // Coming Soon specific fields
  targetSlug: text("target_slug"), // URL slug for the landing page (e.g., "exercise-equipment")
  comingSoonDetails: jsonb("coming_soon_details").$type<ComingSoonDetails>(), // Landing page content
  enabled: boolean("enabled").notNull().default(true),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export type HeroSlide = typeof heroSlides.$inferSelect;
export type InsertHeroSlide = typeof heroSlides.$inferInsert;

export const insertHeroSlideSchema = createInsertSchema(heroSlides).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// ============================================================================
// AI Product Enrichment - Queue and results for AI-powered product data enrichment
// ============================================================================

// Enrichment job statuses
export const ENRICHMENT_JOB_STATUSES = ["pending", "processing", "completed", "failed", "cancelled"] as const;
export type EnrichmentJobStatus = typeof ENRICHMENT_JOB_STATUSES[number];

// Enrichment result statuses
export const ENRICHMENT_RESULT_STATUSES = ["pending_review", "approved", "rejected", "auto_applied", "conflict"] as const;
export type EnrichmentResultStatus = typeof ENRICHMENT_RESULT_STATUSES[number];

// Enrichment Jobs - Batch jobs for enriching products by category/supplier
export const productEnrichmentJobs = pgTable("product_enrichment_jobs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(), // Job name for display (e.g., "Enrich Exercise Equipment")
  categoryId: uuid("category_id").references(() => categories.id), // Optional filter by category
  supplierId: uuid("supplier_id").references(() => suppliers.id), // Optional filter by supplier
  filters: jsonb("filters"), // Additional filters: { brand: "Horizon", hasImage: false, etc. }
  status: text("status").$type<EnrichmentJobStatus>().notNull().default("pending"),
  totalProducts: integer("total_products").default(0),
  processedProducts: integer("processed_products").default(0),
  successCount: integer("success_count").default(0),
  failureCount: integer("failure_count").default(0),
  conflictCount: integer("conflict_count").default(0),
  autoApplyEmpty: boolean("auto_apply_empty").default(true), // Auto-apply results for empty fields
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").default(sql`now()`),
});

export type ProductEnrichmentJob = typeof productEnrichmentJobs.$inferSelect;
export type InsertProductEnrichmentJob = typeof productEnrichmentJobs.$inferInsert;

// Enrichment Results - Individual AI-found data for each product
export const productEnrichmentResults = pgTable("product_enrichment_results", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  jobId: uuid("job_id").references(() => productEnrichmentJobs.id).notNull(),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  status: text("status").$type<EnrichmentResultStatus>().notNull().default("pending_review"),
  // AI-found data (structured)
  foundImageUrl: text("found_image_url"),
  foundAdditionalImages: text("found_additional_images").array(),
  foundDescription: text("found_description"),
  foundMsrp: decimal("found_msrp", { precision: 10, scale: 2 }),
  foundFeatures: text("found_features").array(),
  foundSpecifications: jsonb("found_specifications"), // { dimensions: [...], details: [...] }
  foundWarranty: text("found_warranty"),
  foundWeight: decimal("found_weight", { precision: 10, scale: 2 }),
  foundDimensions: jsonb("found_dimensions"), // { width, depth, height }
  foundAttributes: jsonb("found_attributes"), // Category-specific attribute suggestions: { door_style: "French Door", ice_maker: true, btu: 12000, ... }
  // Conflict tracking - which fields differ from existing data
  conflicts: jsonb("conflicts"), // { msrp: { existing: "999.99", found: "1099.99" }, ... }
  hasConflicts: boolean("has_conflicts").default(false),
  // Source and confidence
  sourcesUsed: text("sources_used").array(), // ["amazon", "manufacturer_site", "google"]
  confidenceScore: decimal("confidence_score", { precision: 3, scale: 2 }), // 0.00 - 1.00
  rawAiResponse: jsonb("raw_ai_response"), // Full AI response for debugging
  // Resolution
  resolvedByUserId: varchar("resolved_by_user_id").references(() => users.id),
  resolvedAt: timestamp("resolved_at"),
  appliedFields: text("applied_fields").array(), // Which fields were actually applied
  rejectionReason: text("rejection_reason"),
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export type ProductEnrichmentResult = typeof productEnrichmentResults.$inferSelect;
export type InsertProductEnrichmentResult = typeof productEnrichmentResults.$inferInsert;

// Particles (Costco) enrichment results -- mirror of productEnrichmentResults
// but populated from the Particle Recycler partner API. Lives in its own
// table so the admin diff/apply UI can be reasoned about independently of
// the OpenAI-driven enrichment pipeline.
export const PARTICLES_RESULT_STATUSES = ["pending_review", "applied", "dismissed", "no_diff"] as const;
export type ParticlesResultStatus = typeof PARTICLES_RESULT_STATUSES[number];

export const particlesEnrichmentResults = pgTable("particles_enrichment_results", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  requestedByUserId: varchar("requested_by_user_id").references(() => users.id),
  particlesApplianceId: uuid("particles_appliance_id"), // appliance_id from Particles
  rawPayload: jsonb("raw_payload"), // full Particles response, snapshotted
  status: text("status").$type<ParticlesResultStatus>().notNull().default("pending_review"),
  // Proposed values - mirror models columns the admin may want to apply
  proposedFeatures: text("proposed_features").array(),
  proposedSpecifications: jsonb("proposed_specifications"), // {dimensions:[], details:[]}
  proposedImageUrl: text("proposed_image_url"),
  proposedAdditionalImages: text("proposed_additional_images").array(),
  proposedMsrp: decimal("proposed_msrp", { precision: 10, scale: 2 }),
  proposedWarranty: text("proposed_warranty"),
  proposedRating: decimal("proposed_rating", { precision: 3, scale: 2 }),
  proposedReviewsCount: integer("proposed_reviews_count"),
  proposedRepairVideos: jsonb("proposed_repair_videos"), // [{title, url, thumbnail}]
  proposedCostcoPriceUsd: decimal("proposed_costco_price_usd", { precision: 10, scale: 2 }),
  proposedCostcoOriginalPriceUsd: decimal("proposed_costco_original_price_usd", { precision: 10, scale: 2 }),
  appliedFields: text("applied_fields").array(),
  createdAt: timestamp("created_at").default(sql`now()`),
  decidedAt: timestamp("decided_at"),
});

export type ParticlesEnrichmentResult = typeof particlesEnrichmentResults.$inferSelect;
export type InsertParticlesEnrichmentResult = typeof particlesEnrichmentResults.$inferInsert;

// Webhook Logs - Persistent logging of ALL incoming webhook requests for debugging
export const WEBHOOK_PROCESSING_STATUSES = ["pending", "processing", "succeeded", "failed"] as const;
export type WebhookProcessingStatus = typeof WEBHOOK_PROCESSING_STATUSES[number];

export const webhookLogs = pgTable("webhook_logs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  createdAt: timestamp("created_at").default(sql`now()`),
  deliveryId: text("delivery_id"), // Delivery ID for replay detection
  snapshotId: text("snapshot_id"), // Snapshot ID for tracking
  eventType: text("event_type").notNull().default("webhook"), // "webhook", etc.
  requestHeaders: jsonb("request_headers"), // HTTP headers (scrubbed)
  rawBody: text("raw_body"), // Raw request body
  parsedPayload: jsonb("parsed_payload"), // Parsed JSON payload
  signatureValid: boolean("signature_valid"), // HMAC signature validation result
  signatureKeyUsed: text("signature_key_used"), // "current", "next", or "none"
  replayDetected: boolean("replay_detected"), // Duplicate delivery check
  processingStatus: text("processing_status").notNull().default("pending"), // "pending", "processing", "succeeded", "failed"
  processingError: text("processing_error"), // Error details if failed
  processingDurationMs: integer("processing_duration_ms"), // Time to process in milliseconds
  ipAddress: text("ip_address"), // Source IP address
  userAgent: text("user_agent"), // HTTP User-Agent header
});

// Settings - global application settings
export const settings = pgTable("settings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(), // e.g., "rental_enabled", "visible_categories"
  value: jsonb("value").notNull(), // Flexible JSON value
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// User Settings - per-user overrides of global settings (sticky preferences)
export const userSettings = pgTable("user_settings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: 'cascade' }).notNull(),
  key: text("key").notNull(), // Same keys as global settings
  value: jsonb("value").notNull(), // User's override value
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => [
  uniqueIndex("user_settings_user_key_idx").on(table.userId, table.key),
]);

// Setting Defaults - admin-saved default values (snapshot of settings for reset functionality)
export const settingDefaults = pgTable("setting_defaults", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  key: text("key").notNull().unique(), // Same keys as global settings
  value: jsonb("value").notNull(), // Admin-saved default value
  savedBy: varchar("saved_by").references(() => users.id), // Admin who saved this default
  savedAt: timestamp("saved_at").default(sql`now()`),
});

// Suppliers and Distributors
export const suppliers = pgTable("suppliers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull().unique(),
  code: text("code").notNull().unique(), // e.g., "SE" for Sohnen Enterprises, "SJ" for ShopJimmy
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  address: text("address"),
  notes: text("notes"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Warehouse inventory modes - controls how inventory availability is calculated
export const WAREHOUSE_INVENTORY_MODES = ["quantity", "strict_location"] as const;
export type WarehouseInventoryMode = typeof WAREHOUSE_INVENTORY_MODES[number];
// - "quantity": Trust the inventory.quantity field from supplier files (default, for remote warehouses)
// - "strict_location": Only count items that have physical warehouse location assignments (for Burnsville)

// Supplier addresses (pickup points and distribution centers)
export const supplierAddresses = pgTable("supplier_addresses", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  supplierId: uuid("supplier_id").references(() => suppliers.id, { onDelete: 'cascade' }).notNull(),
  locationName: text("location_name").notNull(), // "Main Warehouse", "Distribution Center A"
  warehouseCode: text("warehouse_code"), // e.g., "MN01", "CA01" - optional warehouse code for this location
  isPickupPoint: boolean("is_pickup_point").default(true), // Can we pick up full truckloads here?
  isDistributionCenter: boolean("is_distribution_center").default(true), // Can we ship from here?
  localPickupEnabled: boolean("local_pickup_enabled").default(false), // Enable local pickup for consumers
  localPickupFee: decimal("local_pickup_fee", { precision: 10, scale: 2 }).default("50.00"), // Fee for local pickup (default $50)
  isRemoteFulfillment: boolean("is_remote_fulfillment").default(false), // Inventory shipped directly from supplier (not stored locally)
  inventoryMode: varchar("inventory_mode").$type<WarehouseInventoryMode>().default("quantity"), // How to calculate available inventory
  line1: text("line1").notNull(),
  line2: text("line2"),
  city: text("city").notNull(),
  region: text("region").notNull(), // state/province
  postalCode: text("postal_code").notNull(),
  country: varchar("country").notNull().default("US"), // "US", "CA", "MX"
  phone: text("phone"),
  contactName: text("contact_name"),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Supplier programs (product categories with unique grading systems per supplier)
export const PROGRAM_CATEGORIES = ["Major Appliances", "Small Appliances", "TVs", "Consumer Electronics", "Furniture", "Exercise Equipment", "Other"] as const;
export type ProgramCategory = typeof PROGRAM_CATEGORIES[number];

// Grade handling modes for supplier programs
export const GRADE_HANDLING_MODES = ["quantity", "serial"] as const;
export type GradeHandlingMode = typeof GRADE_HANDLING_MODES[number];

// Grade handling configuration structure
export interface GradeHandlingConfig {
  defaultMode: GradeHandlingMode;
  gradeOverrides?: {
    'A'?: GradeHandlingMode;
    'B'?: GradeHandlingMode;
    'B+'?: GradeHandlingMode;
    'B-'?: GradeHandlingMode;
  };
}

export const supplierPrograms = pgTable("supplier_programs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  supplierId: uuid("supplier_id").references(() => suppliers.id, { onDelete: 'cascade' }).notNull(),
  name: text("name").notNull(), // "Refrigerator Program", "Vacuum Program"
  category: text("category").notNull(), // High-level category from PROGRAM_CATEGORIES
  revenueSplitNotes: text("revenue_split_notes"), // "70/30 split after fees", "Consignment - 80/20"
  gradingNotes: text("grading_notes"), // How this program grades products (A, B+, B-, etc.)
  gradeHandlingConfig: jsonb("grade_handling_config").$type<GradeHandlingConfig>(), // How grades are handled: quantity-based or serial-based
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Warehouse modes for file formats
export const FILE_WAREHOUSE_MODES = ["single", "multi_column", "inline_header"] as const;
export type FileWarehouseMode = typeof FILE_WAREHOUSE_MODES[number];

// Supplier File Formats - Configuration for how each supplier's Excel files are structured
export const supplierFileFormats = pgTable("supplier_file_formats", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  supplierId: uuid("supplier_id").references(() => suppliers.id, { onDelete: 'cascade' }).notNull(),
  programId: uuid("program_id").references(() => supplierPrograms.id, { onDelete: 'cascade' }), // Optional: specific program this format applies to
  formatName: text("format_name").notNull(), // "Scratch and Dent Pricing Form", "Sonin Weekly Inventory"
  formatDescription: text("format_description"), // Human-readable description of the file format
  // File structure
  headerRow: integer("header_row").default(1), // Which row contains column headers (1-indexed)
  dataStartRow: integer("data_start_row").default(2), // Which row data starts on
  sheetNamePattern: text("sheet_name_pattern"), // Regex pattern for sheet name, e.g., "Sheet1" or "Inventory.*"
  // Warehouse handling
  warehouseMode: varchar("warehouse_mode").$type<FileWarehouseMode>().notNull().default("single"),
  // single: User selects warehouse before upload (like Sonin)
  // multi_column: Multiple warehouse qty columns in file (like Johnson Fitness: Chicago Qty, LA Qty)
  // inline_header: Category headers contain warehouse info inline
  warehouseColumnMappings: jsonb("warehouse_column_mappings"), // For multi_column: { "JF-CHI": "E", "JF-LA": "F" } or { "JF-CHI": "Chicago Qty", "JF-LA": "LA Qty" }
  // Column mappings (column letter or header name)
  columnMappings: jsonb("column_mappings").notNull().$type<{
    sku: string;           // Required: Column for SKU/model number
    description?: string;  // Optional: Column for product description
    brand?: string;        // Optional: Column for brand (if not inline)
    regularPrice?: string; // Optional: Column for regular price
    salePrice?: string;    // Optional: Column for sale/wholesale price
    quantity?: string;     // Optional: Single quantity column (for single warehouse mode)
    grade?: string;        // Optional: Column for grade/condition
    category?: string;     // Optional: Column for category (if not using inline headers)
  }>(),
  // Custom parser override - when set, bypasses generic column-mapping parser and uses a dedicated parser with AI enrichment
  customParser: text("custom_parser"), // null = generic parser, "shark-ninja" = SharkNinja AI parser, "johnson-fitness" = Johnson Fitness parser
  // Parsing options
  skipEmptySkus: boolean("skip_empty_skus").default(true),
  trimWhitespace: boolean("trim_whitespace").default(true),
  defaultGrade: text("default_grade"), // If no grade column, use this grade
  inlineCategoryHeaders: boolean("inline_category_headers").default(false), // File has category names as row headers
  // Sample data for preview (first few rows to show admin what to expect)
  sampleColumns: text("sample_columns").array(), // ["SKU", "Description", "Regular Price", "Sale Price", "Chicago Qty", "LA Qty"]
  sampleData: jsonb("sample_data"), // First 3-5 rows of example data as array of arrays
  // Status
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  supplierIdx: index("supplier_file_formats_supplier_idx").on(table.supplierId),
  programIdx: index("supplier_file_formats_program_idx").on(table.programId),
}));

export const imageSearchQueue = pgTable("image_search_queue", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  status: text("status").notNull().default("pending"), // "pending", "processing", "completed", "failed"
  errorMessage: text("error_message"),
  createdAt: timestamp("created_at").default(sql`now()`),
  processedAt: timestamp("processed_at"),
});

export const parserMappings = pgTable("parser_mappings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sheetName: text("sheet_name").notNull(),
  filePattern: text("file_pattern").notNull(), // e.g., "OTHER ATS", "BLENDJET"
  categoryId: uuid("category_id").references(() => categories.id),
  columnMappings: jsonb("column_mappings").notNull(), // { brand: "BRAND", mfgSku: "MFGSKU", grade: "COSMETIC", ... }
  createdAt: timestamp("created_at").default(sql`now()`),
});

export const customers = pgTable("customers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  businessName: text("business_name").notNull(),
  contactName: text("contact_name").notNull(),
  email: text("email"),
  phone: text("phone"),
  address: text("address"),
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const rentals = pgTable("rentals", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: uuid("customer_id").references(() => customers.id).notNull(),
  inventoryId: uuid("inventory_id").references(() => inventory.id).notNull(),
  unitId: uuid("unit_id").references(() => serializedUnits.id), // Nullable - only for B+ units
  weeklyRate: decimal("weekly_rate", { precision: 10, scale: 2 }).notNull().default("10.00"),
  purchasePrice: decimal("purchase_price", { precision: 10, scale: 2 }).notNull(),
  startDate: timestamp("start_date").notNull().default(sql`now()`),
  endDate: timestamp("end_date"), // Null if still active
  status: text("status").notNull().default("active"), // "active", "returned", "purchased", "replaced"
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
});

// ============================================================================
// Category Grade Profiles - Define allowed grades per category
// ============================================================================
export const categoryGradeProfiles = pgTable("category_grade_profiles", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  categoryId: uuid("category_id").references(() => categories.id).notNull(),
  gradeCode: text("grade_code").notNull(), // e.g., "A", "B", "X", "B+", "B-"
  gradeName: text("grade_name").notNull(), // e.g., "Class A", "Class B", "Salvage"
  gradeDescription: text("grade_description"), // Detailed description for customers
  sortOrder: integer("sort_order").default(0), // Display order
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  uniqueCategoryGrade: uniqueIndex("unique_category_grade").on(table.categoryId, table.gradeCode),
}));

// ============================================================================
// Product Series - Groups related product variants (e.g., "Shark Vac 1" series)
// Used for small appliances where different colors = different model numbers
// ============================================================================
export const productSeries = pgTable("product_series", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  categoryId: uuid("category_id").references(() => categories.id).notNull(),
  brand: text("brand").notNull(),
  seriesName: text("series_name").notNull(), // e.g., "Shark Navigator Lift-Away"
  seriesDescription: text("series_description"), // Marketing description
  features: text("features").array(), // Feature bullets shared across all variants
  specifications: jsonb("specifications"), // Shared specs: { "Motor Power": "1200W", "Cord Length": "30ft" }
  warranty: text("warranty"),
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// ============================================================================
// Product Variants - Individual products within a series (color variants)
// Each variant has its own model number and images
// ============================================================================
export const productVariants = pgTable("product_variants", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  seriesId: uuid("series_id").references(() => productSeries.id).notNull(),
  modelNumber: text("model_number").notNull().unique(), // Manufacturer model number (unique across all variants)
  color: text("color").notNull(), // e.g., "Red", "Blue", "Stainless Steel"
  colorHex: text("color_hex"), // Optional hex code for color swatch
  variantName: text("variant_name"), // Optional variant-specific name override
  // Images
  primaryImageUrl: text("primary_image_url"),
  additionalImages: text("additional_images").array(),
  // Dimensions (unboxed product dimensions)
  dimensionsWidth: decimal("dimensions_width", { precision: 6, scale: 2 }), // in inches
  dimensionsDepth: decimal("dimensions_depth", { precision: 6, scale: 2 }), // in inches
  dimensionsHeight: decimal("dimensions_height", { precision: 6, scale: 2 }), // in inches
  weightLbs: decimal("weight_lbs", { precision: 10, scale: 2 }), // in pounds
  // Shipping dimensions (boxed)
  shippingWidth: decimal("shipping_width", { precision: 6, scale: 2 }), // in inches
  shippingDepth: decimal("shipping_depth", { precision: 6, scale: 2 }), // in inches
  shippingHeight: decimal("shipping_height", { precision: 6, scale: 2 }), // in inches
  shippingWeight: decimal("shipping_weight", { precision: 10, scale: 2 }), // in pounds
  // Pallet configuration for freight calculations
  itemsPerPallet: integer("items_per_pallet").default(1), // How many units fit on a pallet
  palletStackable: boolean("pallet_stackable").default(true), // Can pallets be stacked
  // MSRP
  msrp: decimal("msrp", { precision: 10, scale: 2 }),
  // Status
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// ============================================================================
// Variant Grade Inventory - Aggregate inventory by variant + grade + warehouse
// For small appliances: tracks quantity available rather than individual units
// ============================================================================
export const variantGradeInventory = pgTable("variant_grade_inventory", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  variantId: uuid("variant_id").references(() => productVariants.id).notNull(),
  warehouseId: uuid("warehouse_id").references(() => supplierAddresses.id).notNull(),
  grade: text("grade").notNull(), // e.g., "A", "B", "X"
  sellingPrice: decimal("selling_price", { precision: 10, scale: 2 }),
  onHandQuantity: integer("on_hand_quantity").default(0), // Total units physically present
  reservedQuantity: integer("reserved_quantity").default(0), // Units reserved for pending orders
  isAvailable: boolean("is_available").default(true),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  uniqueVariantGradeWarehouse: uniqueIndex("unique_variant_grade_warehouse").on(table.variantId, table.warehouseId, table.grade),
}));

// ============================================================================
// Small Appliance Units - Individual fingerprint-tracked units
// Tracks each physical unit with accessories for closed-loop receiving/shipping
// ============================================================================
export const SMALL_APPLIANCE_UNIT_STATUSES = ["RECEIVED", "IN_STOCK", "RESERVED", "PICKED", "SHIPPED", "RETURNED", "DAMAGED"] as const;
export type SmallApplianceUnitStatus = typeof SMALL_APPLIANCE_UNIT_STATUSES[number];

export const smallApplianceUnits = pgTable("small_appliance_units", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  inventoryId: uuid("inventory_id").references(() => inventory.id).notNull(), // Links to aggregate inventory record (inventory table)
  fingerprintId: text("fingerprint_id").notNull().unique(), // e.g., "FS0008893617" - unique barcode per unit
  upcCode: text("upc_code"), // UPC barcode on the box (e.g., "732388799473") - scanned at put-away
  // Accessories tracking
  accessoriesIncluded: text("accessories_included").array(), // e.g., ["power cord", "hose", "attachments"]
  accessoriesMissing: text("accessories_missing").array(), // e.g., ["crevice tool", "dust brush"]
  accessoriesNotes: text("accessories_notes"), // Free-form notes about condition/accessories
  // Location tracking
  warehouseLocationId: uuid("warehouse_location_id").references(() => warehouseLocations.id),
  palletId: uuid("pallet_id").references(() => pallets.id),
  // Status tracking
  status: text("status").$type<SmallApplianceUnitStatus>().notNull().default("RECEIVED"),
  // Order linkage
  orderId: uuid("order_id").references(() => orders.id), // Set when reserved/shipped
  orderItemId: uuid("order_item_id"), // Links to specific order item
  // Receiving info
  receivedAt: timestamp("received_at").default(sql`now()`),
  receivedByUserId: varchar("received_by_user_id").references(() => users.id),
  // Shipping info
  shippedAt: timestamp("shipped_at"),
  shippedByUserId: varchar("shipped_by_user_id").references(() => users.id),
  shipmentId: uuid("shipment_id").references(() => outboundShipments.id),
  // Notes
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  inventoryIdIdx: index("small_appliance_units_inventory_id_idx").on(table.inventoryId),
  statusIdx: index("small_appliance_units_status_idx").on(table.status),
  orderIdIdx: index("small_appliance_units_order_id_idx").on(table.orderId),
}));

// ============================================================================
// Small Appliance Unit Events - Audit trail for fingerprint scans
// ============================================================================
export const UNIT_EVENT_TYPES = ["RECEIVED", "PUTAWAY", "PICKED", "SHIPPED", "RETURNED", "ADJUSTED"] as const;
export type UnitEventType = typeof UNIT_EVENT_TYPES[number];

export const smallApplianceUnitEvents = pgTable("small_appliance_unit_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  unitId: uuid("unit_id").references(() => smallApplianceUnits.id).notNull(),
  eventType: text("event_type").$type<UnitEventType>().notNull(),
  userId: varchar("user_id").references(() => users.id),
  orderId: uuid("order_id").references(() => orders.id),
  warehouseLocationId: uuid("warehouse_location_id").references(() => warehouseLocations.id),
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Relations
export const categoriesRelations = relations(categories, ({ many }) => ({
  models: many(models),
  gradeProfiles: many(categoryGradeProfiles),
  productSeries: many(productSeries),
}));

export const categoryGradeProfilesRelations = relations(categoryGradeProfiles, ({ one }) => ({
  category: one(categories, {
    fields: [categoryGradeProfiles.categoryId],
    references: [categories.id],
  }),
}));

export const productSeriesRelations = relations(productSeries, ({ one, many }) => ({
  category: one(categories, {
    fields: [productSeries.categoryId],
    references: [categories.id],
  }),
  variants: many(productVariants),
}));

export const productVariantsRelations = relations(productVariants, ({ one, many }) => ({
  series: one(productSeries, {
    fields: [productVariants.seriesId],
    references: [productSeries.id],
  }),
  inventory: many(variantGradeInventory),
}));

export const variantGradeInventoryRelations = relations(variantGradeInventory, ({ one, many }) => ({
  variant: one(productVariants, {
    fields: [variantGradeInventory.variantId],
    references: [productVariants.id],
  }),
  warehouse: one(supplierAddresses, {
    fields: [variantGradeInventory.warehouseId],
    references: [supplierAddresses.id],
  }),
  units: many(smallApplianceUnits),
}));

export const smallApplianceUnitsRelations = relations(smallApplianceUnits, ({ one, many }) => ({
  inventory: one(variantGradeInventory, {
    fields: [smallApplianceUnits.inventoryId],
    references: [variantGradeInventory.id],
  }),
  warehouseLocation: one(warehouseLocations, {
    fields: [smallApplianceUnits.warehouseLocationId],
    references: [warehouseLocations.id],
  }),
  pallet: one(pallets, {
    fields: [smallApplianceUnits.palletId],
    references: [pallets.id],
  }),
  order: one(orders, {
    fields: [smallApplianceUnits.orderId],
    references: [orders.id],
  }),
  receivedByUser: one(users, {
    fields: [smallApplianceUnits.receivedByUserId],
    references: [users.id],
  }),
  shipment: one(outboundShipments, {
    fields: [smallApplianceUnits.shipmentId],
    references: [outboundShipments.id],
  }),
  events: many(smallApplianceUnitEvents),
}));

export const smallApplianceUnitEventsRelations = relations(smallApplianceUnitEvents, ({ one }) => ({
  unit: one(smallApplianceUnits, {
    fields: [smallApplianceUnitEvents.unitId],
    references: [smallApplianceUnits.id],
  }),
  user: one(users, {
    fields: [smallApplianceUnitEvents.userId],
    references: [users.id],
  }),
  order: one(orders, {
    fields: [smallApplianceUnitEvents.orderId],
    references: [orders.id],
  }),
  warehouseLocation: one(warehouseLocations, {
    fields: [smallApplianceUnitEvents.warehouseLocationId],
    references: [warehouseLocations.id],
  }),
}));

export const modelsRelations = relations(models, ({ one, many }) => ({
  category: one(categories, {
    fields: [models.categoryId],
    references: [categories.id],
  }),
  supplierProgram: one(supplierPrograms, {
    fields: [models.supplierProgramId],
    references: [supplierPrograms.id],
  }),
  inventory: many(inventory),
}));

export const inventoryRelations = relations(inventory, ({ one, many }) => ({
  model: one(models, {
    fields: [inventory.modelId],
    references: [models.id],
  }),
  address: one(supplierAddresses, {
    fields: [inventory.addressId],
    references: [supplierAddresses.id],
  }),
  serializedUnits: many(serializedUnits),
  orderItems: many(orderItems),
  locationAssignments: many(inventoryLocationAssignments),
}));

export const inventoryLocationAssignmentsRelations = relations(inventoryLocationAssignments, ({ one }) => ({
  inventory: one(inventory, {
    fields: [inventoryLocationAssignments.inventoryId],
    references: [inventory.id],
  }),
  location: one(warehouseLocations, {
    fields: [inventoryLocationAssignments.locationId],
    references: [warehouseLocations.id],
  }),
}));

export const suppliersRelations = relations(suppliers, ({ many }) => ({
  addresses: many(supplierAddresses),
  programs: many(supplierPrograms),
}));

export const supplierAddressesRelations = relations(supplierAddresses, ({ one }) => ({
  supplier: one(suppliers, {
    fields: [supplierAddresses.supplierId],
    references: [suppliers.id],
  }),
}));

export const supplierProgramsRelations = relations(supplierPrograms, ({ one, many }) => ({
  supplier: one(suppliers, {
    fields: [supplierPrograms.supplierId],
    references: [suppliers.id],
  }),
  models: many(models),
}));

export const imageSearchQueueRelations = relations(imageSearchQueue, ({ one }) => ({
  model: one(models, {
    fields: [imageSearchQueue.modelId],
    references: [models.id],
  }),
}));

export const serializedUnitsRelations = relations(serializedUnits, ({ one, many }) => ({
  inventory: one(inventory, {
    fields: [serializedUnits.inventoryId],
    references: [inventory.id],
  }),
  orderItems: many(orderItems),
  events: many(serializedUnitEvents),
}));

export const serializedUnitEventsRelations = relations(serializedUnitEvents, ({ one }) => ({
  unit: one(serializedUnits, {
    fields: [serializedUnitEvents.unitId],
    references: [serializedUnits.id],
  }),
  user: one(users, {
    fields: [serializedUnitEvents.userId],
    references: [users.id],
  }),
  order: one(orders, {
    fields: [serializedUnitEvents.orderId],
    references: [orders.id],
  }),
  warehouseLocation: one(warehouseLocations, {
    fields: [serializedUnitEvents.warehouseLocationId],
    references: [warehouseLocations.id],
  }),
}));

export const ordersRelations = relations(orders, ({ one, many }) => ({
  user: one(users, {
    fields: [orders.userId],
    references: [users.id],
  }),
  orderItems: many(orderItems),
  orderHistory: many(orderHistory),
  shipments: many(orderShipments),
}));

export const usersRelations = relations(users, ({ many }) => ({
  orders: many(orders),
  savedAddresses: many(savedAddresses),
}));

export const savedAddressesRelations = relations(savedAddresses, ({ one }) => ({
  user: one(users, {
    fields: [savedAddresses.userId],
    references: [users.id],
  }),
}));

export const orderItemsRelations = relations(orderItems, ({ one }) => ({
  order: one(orders, {
    fields: [orderItems.orderId],
    references: [orders.id],
  }),
  inventory: one(inventory, {
    fields: [orderItems.inventoryId],
    references: [inventory.id],
  }),
  unit: one(serializedUnits, {
    fields: [orderItems.unitId],
    references: [serializedUnits.id],
  }),
}));

export const orderHistoryRelations = relations(orderHistory, ({ one }) => ({
  order: one(orders, {
    fields: [orderHistory.orderId],
    references: [orders.id],
  }),
  user: one(users, {
    fields: [orderHistory.userId],
    references: [users.id],
  }),
}));

export const orderShipmentsRelations = relations(orderShipments, ({ one, many }) => ({
  order: one(orders, {
    fields: [orderShipments.orderId],
    references: [orders.id],
  }),
  trackingNumbers: many(shipmentTracking),
  items: many(shipmentItems),
}));

export const shipmentTrackingRelations = relations(shipmentTracking, ({ one }) => ({
  shipment: one(orderShipments, {
    fields: [shipmentTracking.shipmentId],
    references: [orderShipments.id],
  }),
  carrier: one(freightCarriers, {
    fields: [shipmentTracking.carrierId],
    references: [freightCarriers.id],
  }),
}));

export const shipmentItemsRelations = relations(shipmentItems, ({ one }) => ({
  shipment: one(orderShipments, {
    fields: [shipmentItems.shipmentId],
    references: [orderShipments.id],
  }),
  orderItem: one(orderItems, {
    fields: [shipmentItems.orderItemId],
    references: [orderItems.id],
  }),
}));

export const parserMappingsRelations = relations(parserMappings, ({ one }) => ({
  category: one(categories, {
    fields: [parserMappings.categoryId],
    references: [categories.id],
  }),
}));

export const customersRelations = relations(customers, ({ many }) => ({
  rentals: many(rentals),
}));

export const rentalsRelations = relations(rentals, ({ one }) => ({
  customer: one(customers, {
    fields: [rentals.customerId],
    references: [customers.id],
  }),
  inventory: one(inventory, {
    fields: [rentals.inventoryId],
    references: [inventory.id],
  }),
  unit: one(serializedUnits, {
    fields: [rentals.unitId],
    references: [serializedUnits.id],
  }),
}));

// Insert schemas
export const insertCategorySchema = createInsertSchema(categories).omit({
  id: true,
  createdAt: true,
});

export const insertModelSchema = createInsertSchema(models).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertInventorySchema = createInsertSchema(inventory).omit({
  id: true,
  updatedAt: true,
}).extend({
  inventoryStatus: z.enum(INVENTORY_STATUSES).default("in-stock"),
  inventoryType: z.enum(INVENTORY_TYPES).default("serialized"),
});

export const updateInventorySchema = z.object({
  quantity: z.number().int().min(0).optional(),
  sellingPrice: z.union([z.string(), z.number()]).optional(),
  promoPrice: z.union([z.string(), z.number()]).optional(),
  isAvailable: z.boolean().optional(),
  isShowroomEligible: z.boolean().optional(),
  accessoriesStatus: z.string().optional(),
  accessoryNotes: z.string().optional(),
  warehouseId: z.string().optional(),
  inventoryStatus: z.enum(INVENTORY_STATUSES).optional(),
  inventoryType: z.enum(INVENTORY_TYPES).optional(),
  eta: z.string().nullable().optional(),
  boxType: z.string().nullable().optional(),
});

export const insertSerializedUnitSchema = createInsertSchema(serializedUnits).omit({
  id: true,
  firstSeenDate: true,
});

export const updateSerializedUnitSchema = z.object({
  serialNumber: z.string().optional(),
  videoUrl: z.string().nullable().optional(),
  noVideoExpected: z.boolean().optional(),
  notes: z.string().nullable().optional(),
  isShipped: z.boolean().optional(),
  allocatedOrderItemId: z.string().uuid().nullable().optional(),
  allocatedAt: z.string().nullable().optional(),
  palletId: z.string().uuid().nullable().optional(),
});

// Serialized Unit Event schemas
export const insertSerializedUnitEventSchema = createInsertSchema(serializedUnitEvents).omit({
  id: true,
  createdAt: true,
});

// Pallet schemas
export const insertPalletSchema = createInsertSchema(pallets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updatePalletSchema = z.object({
  warehouseId: z.string().uuid().nullable().optional(),
  status: z.enum(["expected", "received", "shipped"]).optional(),
  receivedAt: z.string().nullable().optional(),
  receivedByUserId: z.string().nullable().optional(),
});

// User Warehouse Permission schemas
export const insertUserWarehousePermissionSchema = createInsertSchema(userWarehousePermissions).omit({
  id: true,
  createdAt: true,
});

// Inventory Manifest schemas
export const insertInventoryManifestSchema = createInsertSchema(inventoryManifests).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateInventoryManifestSchema = z.object({
  status: z.enum(["pending", "partial", "complete"]).optional(),
  totalItems: z.number().int().optional(),
  receivedItems: z.number().int().optional(),
  notes: z.string().nullable().optional(),
});

// Expected Inventory Item schemas
export const insertExpectedInventoryItemSchema = createInsertSchema(expectedInventoryItems).omit({
  id: true,
  createdAt: true,
});

export const updateExpectedInventoryItemSchema = z.object({
  status: z.enum(EXPECTED_ITEM_STATUSES).optional(),
  receivedAt: z.string().nullable().optional(),
  receivedByUserId: z.string().nullable().optional(),
  linkedSerializedUnitId: z.string().uuid().nullable().optional(),
  linkedPalletId: z.string().uuid().nullable().optional(),
  resolutionStatus: z.enum(["PENDING", "MATCHED", "SHORT", "EXTRA"]).optional(),
  resolutionNotes: z.string().nullable().optional(),
  receivedWarehouseLocationId: z.string().uuid().nullable().optional(),
});

// Supplier Sales Log schemas
export const insertSupplierSalesLogSchema = createInsertSchema(supplierSalesLog).omit({
  id: true,
  createdAt: true,
});

// Serial Events schemas
export const insertSerialEventSchema = createInsertSchema(serialEvents).omit({
  id: true,
});

export const serialEventFilterSchema = z.object({
  serialNumber: z.string().optional(),
  eventTypes: z.array(z.enum(SERIAL_EVENT_TYPES)).optional(),
  serializedUnitId: z.string().uuid().optional(),
  expectedItemId: z.string().uuid().optional(),
  orderId: z.string().uuid().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  limit: z.number().int().min(1).max(500).default(100),
  offset: z.number().int().min(0).default(0),
});

// Outbound Shipment schemas
export const insertOutboundShipmentSchema = createInsertSchema(outboundShipments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateOutboundShipmentSchema = z.object({
  status: z.enum(OUTBOUND_SHIPMENT_STATUSES).optional(),
  pickerUserId: z.string().nullable().optional(),
  pickingStartedAt: z.string().nullable().optional(),
  pickingCompletedAt: z.string().nullable().optional(),
  shippedAt: z.string().nullable().optional(),
  shippedByUserId: z.string().nullable().optional(),
  carrierName: z.string().nullable().optional(),
  trackingNumber: z.string().nullable().optional(),
  proNumber: z.string().nullable().optional(),
  bolDocumentId: z.string().uuid().nullable().optional(),
  estimatedDelivery: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  totalPallets: z.number().int().nullable().optional(),
});

// Outbound Shipment Photo schemas
export const insertOutboundShipmentPhotoSchema = createInsertSchema(outboundShipmentPhotos).omit({
  id: true,
  uploadedAt: true,
});

// Outbound Shipment Pallet Scan schemas
export const insertOutboundShipmentPalletScanSchema = createInsertSchema(outboundShipmentPalletScans).omit({
  id: true,
  scannedAt: true,
});

// Outbound Lane schemas
export const insertOutboundLaneSchema = createInsertSchema(outboundLanes).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateOutboundLaneSchema = z.object({
  name: z.string().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});

// Shipment Pallet schemas
export const insertShipmentPalletSchema = createInsertSchema(shipmentPallets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateShipmentPalletSchema = z.object({
  palletNumber: z.number().int().optional(),
  outboundLaneId: z.string().uuid().nullable().optional(),
  contents: z.string().nullable().optional(),
  weightLbs: z.string().nullable().optional(),
  labelPrintedAt: z.string().nullable().optional(),
});

// Shipment Pallet Item schemas
export const insertShipmentPalletItemSchema = createInsertSchema(shipmentPalletItems).omit({
  id: true,
  pickedAt: true,
});

// Order Document schemas
export const insertOrderDocumentSchema = createInsertSchema(orderDocuments).omit({
  id: true,
  uploadedAt: true,
});

// Warehouse Location schemas
export const insertWarehouseLocationSchema = createInsertSchema(warehouseLocations).omit({
  id: true,
  createdAt: true,
});

export const updateWarehouseLocationSchema = z.object({
  isActive: z.boolean().optional(),
});

// Unit Location schemas
export const insertUnitLocationSchema = createInsertSchema(unitLocations).omit({
  id: true,
  assignedAt: true,
});

export const clearUnitLocationSchema = z.object({
  clearedByUserId: z.string().optional(),
  clearReason: z.enum(["shipped", "moved", "damaged", "returned"]).optional(),
});

// Inventory Location Assignment schemas
export const insertInventoryLocationAssignmentSchema = createInsertSchema(inventoryLocationAssignments).omit({
  id: true,
  updatedAt: true,
});

export const upsertInventoryLocationAssignmentSchema = z.object({
  inventoryId: z.string().uuid(),
  locationId: z.string().uuid(),
  quantity: z.number().int().min(0),
  notes: z.string().optional(),
});

// Cart schemas
export const insertCartSchema = createInsertSchema(carts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCartItemSchema = createInsertSchema(cartItems).omit({
  id: true,
  addedAt: true,
  updatedAt: true,
});

export const addCartItemApiSchema = insertCartItemSchema.omit({
  cartId: true,
});

export const updateCartItemSchema = z.object({
  quantity: z.number().int().min(1).optional(),
  price: z.union([z.string(), z.number()]).optional(),
});

export const insertOrderSchema = createInsertSchema(orders).omit({
  id: true,
  createdAt: true,
}).extend({
  shippingMethod: z.enum(SHIPPING_METHODS).optional(),
  localPickupWarehouseId: z.string().uuid().optional(),
});

export const updateOrderStatusSchema = z.object({
  status: z.enum(ORDER_STATUSES),
});

export const updateOrderSchema = z.object({
  customerInfo: z.object({
    name: z.string().min(1),
    email: z.string().email(),
    phone: z.string().min(1),
    address: z.string().min(1),
  }).optional(),
  shippingMethod: z.enum(SHIPPING_METHODS).optional(),
  pickupWarehouseId: z.string().uuid().nullable().optional(),
  pickupFee: z.string().nullable().optional(),
  freightCarrierId: z.string().uuid().nullable().optional(),
  freightCarrierName: z.string().nullable().optional(),
  freightCarrierType: z.string().nullable().optional(),
  freightBaseAmount: z.string().nullable().optional(),
  freightFuelSurcharge: z.string().nullable().optional(),
  freightAccessorials: z.array(z.object({
    code: z.string(),
    name: z.string(),
    amount: z.number(),
  })).nullable().optional(),
  freightTotal: z.string().nullable().optional(),
  shippingState: z.string().nullable().optional(),
  hasLoadingDock: z.boolean().optional(),
  hasForklift: z.boolean().optional(),
  salesTaxRate: z.string().nullable().optional(),
  salesTaxAmount: z.string().nullable().optional(),
  totalAmount: z.string().optional(),
  poNumber: z.string().nullable().optional(),
});

export const addOrderItemSchema = z.object({
  inventoryId: z.string().uuid(),
  unitId: z.string().uuid().nullable().optional(),
  quantity: z.number().int().min(1),
  priceAtPurchase: z.string(),
  grade: z.string(),
});

export const updateOrderItemSchema = z.object({
  quantity: z.number().int().min(1).optional(),
  priceAtPurchase: z.string().optional(),
});

export const insertOrderItemSchema = createInsertSchema(orderItems).omit({
  id: true,
});

// Shipment schemas
export const insertOrderShipmentSchema = createInsertSchema(orderShipments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertShipmentTrackingSchema = createInsertSchema(shipmentTracking).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertShipmentItemSchema = createInsertSchema(shipmentItems).omit({
  id: true,
  createdAt: true,
});

export const insertParserMappingSchema = createInsertSchema(parserMappings).omit({
  id: true,
  createdAt: true,
});

export const insertCustomerSchema = createInsertSchema(customers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertRentalSchema = createInsertSchema(rentals).omit({
  id: true,
  createdAt: true,
});

export const insertSupplierSchema = createInsertSchema(suppliers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateSupplierSchema = insertSupplierSchema.partial().strict();

export const insertSupplierAddressSchema = createInsertSchema(supplierAddresses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertSupplierProgramSchema = createInsertSchema(supplierPrograms).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  category: z.enum(PROGRAM_CATEGORIES),
});

export const insertSupplierFileFormatSchema = createInsertSchema(supplierFileFormats).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const updateSupplierFileFormatSchema = insertSupplierFileFormatSchema.partial();

export const insertSettingsSchema = createInsertSchema(settings).omit({
  id: true,
  updatedAt: true,
});

export const updateSettingsValueSchema = z.object({
  value: z.any(), // Can be any JSON-serializable value
});

export const insertUserSettingsSchema = createInsertSchema(userSettings).omit({
  id: true,
  updatedAt: true,
});

export const insertSettingDefaultsSchema = createInsertSchema(settingDefaults).omit({
  id: true,
  savedAt: true,
});

export const insertImageSearchQueueSchema = createInsertSchema(imageSearchQueue).omit({
  id: true,
  createdAt: true,
  processedAt: true,
});

export const updateModelSchema = z.object({
  categoryId: z.string().uuid().nullable().optional(),
  brand: z.string().optional(),
  mfgSku: z.string().optional(),
  description: z.string().optional(),
  descriptor: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  additionalImages: z.array(z.string()).nullable().optional(),
  msrp: z.union([z.string(), z.number()]).optional(),
  dimensionsWidth: z.number().int().nullable().optional(),
  dimensionsDepth: z.number().int().nullable().optional(),
  dimensionsHeight: z.number().int().nullable().optional(),
  weightLbs: z.union([z.string(), z.number()]).nullable().optional(),
  shippingWidth: z.union([z.string(), z.number()]).nullable().optional(),
  shippingDepth: z.union([z.string(), z.number()]).nullable().optional(),
  shippingHeight: z.union([z.string(), z.number()]).nullable().optional(),
  shippingWeight: z.union([z.string(), z.number()]).nullable().optional(),
  stackable: z.boolean().nullable().optional(),
  fragilityScore: z.number().int().nullable().optional(),
  isStackable: z.boolean().nullable().optional(),
  isRotatable: z.boolean().nullable().optional(),
  attributes: z.record(z.any()).nullable().optional(),
  minOrderQty: z.number().int().min(1).optional(),
  orderMultiple: z.number().int().min(1).optional(),
  unitsPerPalletLayer: z.number().int().min(1).nullable().optional(),
  palletLayerWidth: z.union([z.string(), z.number()]).nullable().optional(),
  palletLayerDepth: z.union([z.string(), z.number()]).nullable().optional(),
  features: z.array(z.string()).nullable().optional(),
  warranty: z.string().nullable().optional(),
  // Per Task #161 (AI enrichment side-by-side diff), the editor can stage
  // accepted spec entries from the AI proposal into the form and persist
  // them via the same PUT path used for every other field.
  specifications: z.record(z.any()).nullable().optional(),
});

// Types
export type Category = typeof categories.$inferSelect;
export type InsertCategory = z.infer<typeof insertCategorySchema>;

export type Model = typeof models.$inferSelect;
export type InsertModel = z.infer<typeof insertModelSchema>;

export type Inventory = typeof inventory.$inferSelect;
export type InsertInventory = z.infer<typeof insertInventorySchema>;

export type SerializedUnit = typeof serializedUnits.$inferSelect;
export type InsertSerializedUnit = z.infer<typeof insertSerializedUnitSchema>;

export type SerializedUnitEvent = typeof serializedUnitEvents.$inferSelect;
export type InsertSerializedUnitEvent = z.infer<typeof insertSerializedUnitEventSchema>;

export type Pallet = typeof pallets.$inferSelect;
export type InsertPallet = z.infer<typeof insertPalletSchema>;

export type UserWarehousePermission = typeof userWarehousePermissions.$inferSelect;
export type InsertUserWarehousePermission = z.infer<typeof insertUserWarehousePermissionSchema>;

export type InventoryManifest = typeof inventoryManifests.$inferSelect;
export type InsertInventoryManifest = z.infer<typeof insertInventoryManifestSchema>;

export type ExpectedInventoryItem = typeof expectedInventoryItems.$inferSelect;
export type InsertExpectedInventoryItem = z.infer<typeof insertExpectedInventoryItemSchema>;

export type SupplierSalesLog = typeof supplierSalesLog.$inferSelect;
export type InsertSupplierSalesLog = z.infer<typeof insertSupplierSalesLogSchema>;

export type SerialEvent = typeof serialEvents.$inferSelect;
export type InsertSerialEvent = z.infer<typeof insertSerialEventSchema>;

export type OutboundShipment = typeof outboundShipments.$inferSelect;
export type InsertOutboundShipment = z.infer<typeof insertOutboundShipmentSchema>;

export type OutboundShipmentPhoto = typeof outboundShipmentPhotos.$inferSelect;
export type InsertOutboundShipmentPhoto = z.infer<typeof insertOutboundShipmentPhotoSchema>;

export type OutboundShipmentPalletScan = typeof outboundShipmentPalletScans.$inferSelect;
export type InsertOutboundShipmentPalletScan = z.infer<typeof insertOutboundShipmentPalletScanSchema>;

export type OutboundLane = typeof outboundLanes.$inferSelect;
export type InsertOutboundLane = z.infer<typeof insertOutboundLaneSchema>;

export type ShipmentPallet = typeof shipmentPallets.$inferSelect;
export type InsertShipmentPallet = z.infer<typeof insertShipmentPalletSchema>;

export type ShipmentPalletItem = typeof shipmentPalletItems.$inferSelect;
export type InsertShipmentPalletItem = z.infer<typeof insertShipmentPalletItemSchema>;

export type OrderDocument = typeof orderDocuments.$inferSelect;
export type InsertOrderDocument = z.infer<typeof insertOrderDocumentSchema>;

export type WarehouseLocation = typeof warehouseLocations.$inferSelect;
export type InsertWarehouseLocation = z.infer<typeof insertWarehouseLocationSchema>;

export type UnitLocation = typeof unitLocations.$inferSelect;
export type InsertUnitLocation = z.infer<typeof insertUnitLocationSchema>;

export type InventoryLocationAssignment = typeof inventoryLocationAssignments.$inferSelect;
export type InsertInventoryLocationAssignment = z.infer<typeof insertInventoryLocationAssignmentSchema>;

export type Cart = typeof carts.$inferSelect;
export type InsertCart = z.infer<typeof insertCartSchema>;

export type CartItem = typeof cartItems.$inferSelect;
export type InsertCartItem = z.infer<typeof insertCartItemSchema>;

export type Order = typeof orders.$inferSelect;
export type InsertOrder = z.infer<typeof insertOrderSchema>;

export type OrderItem = typeof orderItems.$inferSelect;
export type InsertOrderItem = z.infer<typeof insertOrderItemSchema>;

export type OrderHistory = typeof orderHistory.$inferSelect;

export type OrderPayment = typeof orderPayments.$inferSelect;

export const insertOrderPaymentSchema = createInsertSchema(orderPayments).omit({
  id: true,
  createdAt: true,
}).extend({
  paymentMethod: z.enum(PAYMENT_METHOD_TYPES),
  digitalPlatform: z.enum(DIGITAL_PLATFORMS).optional(),
});
export type InsertOrderPayment = z.infer<typeof insertOrderPaymentSchema>;

export type OrderShipment = typeof orderShipments.$inferSelect;
export type InsertOrderShipment = z.infer<typeof insertOrderShipmentSchema>;

export type ShipmentTracking = typeof shipmentTracking.$inferSelect;
export type InsertShipmentTracking = z.infer<typeof insertShipmentTrackingSchema>;

export type ShipmentItem = typeof shipmentItems.$inferSelect;
export type InsertShipmentItem = z.infer<typeof insertShipmentItemSchema>;

export type ParserMapping = typeof parserMappings.$inferSelect;
export type InsertParserMapping = z.infer<typeof insertParserMappingSchema>;

export type Customer = typeof customers.$inferSelect;
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;

export type Rental = typeof rentals.$inferSelect;
export type InsertRental = z.infer<typeof insertRentalSchema>;

export type Supplier = typeof suppliers.$inferSelect;
export type InsertSupplier = z.infer<typeof insertSupplierSchema>;

export type SupplierAddress = typeof supplierAddresses.$inferSelect;
export type InsertSupplierAddress = z.infer<typeof insertSupplierAddressSchema>;

export type SupplierProgram = typeof supplierPrograms.$inferSelect;
export type InsertSupplierProgram = z.infer<typeof insertSupplierProgramSchema>;

export type SupplierFileFormat = typeof supplierFileFormats.$inferSelect;
export type InsertSupplierFileFormat = z.infer<typeof insertSupplierFileFormatSchema>;

export type Settings = typeof settings.$inferSelect;
export type InsertSettings = z.infer<typeof insertSettingsSchema>;

export type UserSettings = typeof userSettings.$inferSelect;
export type InsertUserSettings = z.infer<typeof insertUserSettingsSchema>;

export type SettingDefaults = typeof settingDefaults.$inferSelect;
export type InsertSettingDefaults = z.infer<typeof insertSettingDefaultsSchema>;

export type ImageSearchQueue = typeof imageSearchQueue.$inferSelect;
export type InsertImageSearchQueue = z.infer<typeof insertImageSearchQueueSchema>;

// API Keys for customer access
export const apiKeys = pgTable("api_keys", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: uuid("customer_id").references(() => customers.id), // Link to customer (nullable for backwards compatibility)
  name: text("name").notNull(), // Friendly name for the API key (e.g., "Production Key")
  keyHash: text("key_hash").notNull(), // bcrypt hash of the actual key
  prefix: text("prefix").notNull(), // First 16 chars for identification (e.g., "shopsohnen_live_")
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  lastUsedAt: timestamp("last_used_at"),
});

export const insertApiKeySchema = createInsertSchema(apiKeys).omit({
  id: true,
  createdAt: true,
  lastUsedAt: true,
});

// API Usage Tracking
export const apiUsage = pgTable("api_usage", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  apiKeyId: uuid("api_key_id").references(() => apiKeys.id).notNull(),
  customerId: uuid("customer_id").references(() => customers.id).notNull(),
  endpoint: text("endpoint").notNull(), // API endpoint called
  method: text("method").notNull(), // HTTP method (GET, POST, etc.)
  statusCode: integer("status_code"), // Response status
  tokensUsed: integer("tokens_used").default(0), // For LLM/AI requests
  requestSize: integer("request_size"), // Bytes
  responseSize: integer("response_size"), // Bytes
  duration: integer("duration"), // Milliseconds
  metadata: jsonb("metadata"), // Additional tracking data
  createdAt: timestamp("created_at").default(sql`now()`),
});

export const insertApiUsageSchema = createInsertSchema(apiUsage).omit({
  id: true,
  createdAt: true,
});

// Paxter Optimizations (webhook data storage)
export const paxterOptimizations = pgTable("paxter_optimizations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  optimizationId: text("optimization_id").notNull(), // From Paxter
  orderId: uuid("order_id").references(() => orders.id),
  eventType: text("event_type").notNull(), // "item_added", "item_removed", "load_completed", "load_modified"
  payload: jsonb("payload").notNull(), // Full webhook payload from Paxter
  createdAt: timestamp("created_at").default(sql`now()`),
});

export const insertPaxterOptimizationSchema = createInsertSchema(paxterOptimizations).omit({
  id: true,
  createdAt: true,
});

// Audit Log for admin actions
export const auditLog = pgTable("audit_log", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  at: timestamp("at").default(sql`now()`).notNull(),
  actor: text("actor").notNull(), // User ID or identifier (e.g., "user:abc123" or "system")
  action: text("action").notNull(), // Action type: "decision.approve", "decision.reject", "retry", "purge", "export", "quota.override"
  targetId: text("target_id"), // ID of affected resource (e.g., queue ID, model ID)
  meta: jsonb("meta"), // Additional context (redacted for PII)
});

export const insertAuditLogSchema = createInsertSchema(auditLog).omit({
  id: true,
  at: true,
});

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

export type SavedAddress = typeof savedAddresses.$inferSelect;
export const insertSavedAddressSchema = createInsertSchema(savedAddresses).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertSavedAddress = z.infer<typeof insertSavedAddressSchema>;
export const updateSavedAddressSchema = insertSavedAddressSchema.omit({ userId: true }).partial().strict();
export type UpdateSavedAddress = z.infer<typeof updateSavedAddressSchema>;

// Schema for handling camelCase input from frontend and converting to snake_case
export const addressFormInputSchema = z.object({
  label: z.string().optional(),
  companyName: z.string().optional(),
  addressLine1: z.string(),
  addressLine2: z.string().optional(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  phone: z.string().optional(),
  hasLoadingDock: z.boolean().optional(),
  hasForklift: z.boolean().optional(),
  isDefault: z.boolean().optional(),
}).transform((data) => ({
  label: data.label,
  companyName: data.companyName,
  addressLine1: data.addressLine1,
  addressLine2: data.addressLine2,
  city: data.city,
  state: data.state,
  zipCode: data.zipCode,
  phone: data.phone,
  hasLoadingDock: data.hasLoadingDock ?? false,
  hasForklift: data.hasForklift ?? false,
  isDefault: data.isDefault ?? false,
}));

export type ApiKey = typeof apiKeys.$inferSelect;
export type InsertApiKey = z.infer<typeof insertApiKeySchema>;

export type ApiUsage = typeof apiUsage.$inferSelect;
export type InsertApiUsage = z.infer<typeof insertApiUsageSchema>;

export type PaxterOptimization = typeof paxterOptimizations.$inferSelect;
export type InsertPaxterOptimization = z.infer<typeof insertPaxterOptimizationSchema>;

export type AuditLog = typeof auditLog.$inferSelect;
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;

export const insertWebhookLogSchema = createInsertSchema(webhookLogs).omit({
  id: true,
  createdAt: true,
});
export type WebhookLog = typeof webhookLogs.$inferSelect;
export type InsertWebhookLog = z.infer<typeof insertWebhookLogSchema>;

// Account Applications (pending sign-ups awaiting approval)
export const accountApplications = pgTable("account_applications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountType: varchar("account_type").$type<AccountType>().notNull().default("BUSINESS"), // 'CONSUMER' or 'BUSINESS'
  legalCompanyName: text("legal_company_name"), // Required for BUSINESS, null for CONSUMER
  dbaName: text("dba_name"),
  ein: text("ein"), // Required for BUSINESS, null for CONSUMER
  companyType: text("company_type"), // Required for BUSINESS, null for CONSUMER
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  email: text("email").notNull(),
  phone: text("phone").notNull(),
  addressLine1: text("address_line1").notNull(),
  addressLine2: text("address_line2"),
  city: text("city").notNull(),
  state: text("state").notNull(),
  zipCode: text("zip_code").notNull(),
  requiresLiftgate: boolean("requires_liftgate").default(true), // Default to true if no dock/forklift
  hasLoadingDock: boolean("has_loading_dock").default(false),
  hasForklift: boolean("has_forklift").default(false),
  useCorporateAsShipTo: boolean("use_corporate_as_ship_to").default(false), // Copy corporate address to shipping on setup
  employeeCount: text("employee_count"), // Required for BUSINESS, null for CONSUMER
  businessType: text("business_type"), // Required for BUSINESS, null for CONSUMER
  annualRevenue: text("annual_revenue"), // Required for BUSINESS, null for CONSUMER
  salesTaxExemptState: text("sales_tax_exempt_state"), // State for resale exemption
  salesTaxExemptId: text("sales_tax_exempt_id"), // Resale certificate number
  salesTaxExemptUrl: text("sales_tax_exempt_url"),
  // Business justification fields
  resaleStrategy: text("resale_strategy"), // How they plan to resell products
  hasPhysicalStore: boolean("has_physical_store").default(false), // Whether they have a physical store
  storePhotoUrl: text("store_photo_url"), // Optional photo of their store
  whyBusinessAccount: text("why_business_account"), // Why they want a business account with us
  status: text("status").notNull().default("pending"), // "pending", "approved", "rejected"
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  rejectionReason: text("rejection_reason"),
  finalizedAt: timestamp("finalized_at"), // Most recent status change timestamp
  // Cosmetic grading scale acknowledgement (BUSINESS applicants must check the box on submit)
  gradingScaleAcknowledgedAt: timestamp("grading_scale_acknowledged_at"),
  gradingScaleAcknowledgedVersion: text("grading_scale_acknowledged_version"),
  createdAt: timestamp("created_at").default(sql`now()`),
});

export const accountApplicationStatusHistory = pgTable("account_application_status_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  applicationId: uuid("application_id").references(() => accountApplications.id, { onDelete: "cascade" }).notNull(),
  oldStatus: text("old_status"), // null for initial submission
  newStatus: text("new_status").notNull(), // "pending", "approved", "rejected"
  changedBy: varchar("changed_by").references(() => users.id).notNull(), // Admin who made the change
  changedAt: timestamp("changed_at").default(sql`now()`).notNull(),
  reason: text("reason").notNull(), // Why the status was changed (approval/rejection/reversion reason)
  notes: text("notes"), // Optional additional notes from admin
  createdAt: timestamp("created_at").default(sql`now()`),
});

export const insertAccountApplicationSchema = createInsertSchema(accountApplications).omit({
  id: true,
  status: true,
  reviewedBy: true,
  reviewedAt: true,
  rejectionReason: true,
  finalizedAt: true,
  createdAt: true,
});

export type AccountApplication = typeof accountApplications.$inferSelect;
export type InsertAccountApplication = z.infer<typeof insertAccountApplicationSchema>;

export const insertAccountApplicationStatusHistorySchema = createInsertSchema(accountApplicationStatusHistory).omit({
  id: true,
  createdAt: true,
});

export type AccountApplicationStatusHistory = typeof accountApplicationStatusHistory.$inferSelect;
export type InsertAccountApplicationStatusHistory = z.infer<typeof insertAccountApplicationStatusHistorySchema>;

// Account schemas
export const insertAccountSchema = createInsertSchema(accounts).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type Account = typeof accounts.$inferSelect;
export type InsertAccount = z.infer<typeof insertAccountSchema>;

// Account-User junction schemas
export const insertAccountUserSchema = createInsertSchema(accountUsers).omit({
  id: true,
  createdAt: true,
});
export type AccountUser = typeof accountUsers.$inferSelect;
export type InsertAccountUser = z.infer<typeof insertAccountUserSchema>;

// Password token schemas
export const insertPasswordTokenSchema = createInsertSchema(passwordTokens).omit({
  id: true,
  usedAt: true,
  createdAt: true,
});
export type PasswordToken = typeof passwordTokens.$inferSelect;
export type InsertPasswordToken = z.infer<typeof insertPasswordTokenSchema>;

// Address schemas
export const insertAddressSchema = createInsertSchema(addresses).omit({
  id: true,
  accountId: true,
  createdAt: true,
  updatedAt: true,
}).refine(
  (data) => data.isBilling || data.isShipping,
  {
    message: "Address must be marked as either billing, shipping, or both",
  }
);

export const updateAddressSchema = z.object({
  isBilling: z.boolean().optional(),
  isShipping: z.boolean().optional(),
  locationName: z.string().optional(),
  deliveryContactName: z.string().optional(),
  deliveryContactPhone: z.string().optional(),
  deliveryContactEmail: z.string().optional(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  city: z.string().optional(),
  region: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  phone: z.string().optional(),
  hasLoadingDock: z.boolean().optional(),
  hasForklift: z.boolean().optional(),
  isDefaultShipping: z.boolean().optional(),
}).refine(
  (data) => {
    if (data.isBilling !== undefined || data.isShipping !== undefined) {
      return data.isBilling || data.isShipping;
    }
    return true;
  },
  { message: "Address must be marked as billing, shipping, or both" }
);

export type Address = typeof addresses.$inferSelect;
export type InsertAddress = z.infer<typeof insertAddressSchema>;

// Account update schema
export const updateAccountSchema = z.object({
  companyName: z.string().optional(),
  legalCompanyName: z.string().nullable().optional(),
  dbaName: z.string().nullable().optional(),
  ein: z.string().nullable().optional(),
  companyType: z.enum(["llc", "corporation", "partnership", "sole_proprietorship", "other"]).nullable().optional(),
  businessType: z.enum(["contractor", "wholesaler", "distributor", "repair_shop", "retailer", "other"]).nullable().optional(),
  employeeCount: z.enum(["1-10", "11-50", "51-200", "201-500", "500+"]).nullable().optional(),
  annualRevenue: z.enum(["<1m", "1m-5m", "5m-10m", "10m-50m", "50m+"]).nullable().optional(),
  hasLoadingDock: z.boolean().optional(),
  hasForklift: z.boolean().optional(),
  notes: z.string().nullable().optional(),
  isActive: z.boolean().optional(),
  accountRepId: z.string().nullable().optional(),
  minimumOrderQuantity: z.number().int().min(1).optional(),
  paymentTerms: z.enum(["COD", "NET_15"]).optional(),
});

// Admin Note schemas
export const insertAdminNoteSchema = createInsertSchema(adminNotes).omit({
  id: true,
  createdAt: true,
});
export type AdminNote = typeof adminNotes.$inferSelect;
export type InsertAdminNote = z.infer<typeof insertAdminNoteSchema>;

// ============================================================================
// Freight Carrier System
// ============================================================================

// Freight Carriers (Estes, future carriers)
export const freightCarriers = pgTable("freight_carriers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  code: varchar("code").notNull().unique(), // "ESTES", "UPS_FREIGHT", etc.
  name: text("name").notNull(), // "Estes Forwarding Worldwide"
  logoUrl: text("logo_url"), // Carrier logo stored in object storage
  trackingUrlTemplate: text("tracking_url_template"), // URL template with {tracking} placeholder
  dimDivisor: integer("dim_divisor").notNull().default(200), // For dimensional weight calculation
  isActive: boolean("is_active").default(true),
  supportsApi: boolean("supports_api").default(false), // Future: some carriers may have quoting APIs
  apiCredentials: jsonb("api_credentials"), // Store API keys, endpoints, etc.
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Carrier Zones - Maps states to zone numbers per carrier
export const carrierZones = pgTable("carrier_zones", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierId: uuid("carrier_id").references(() => freightCarriers.id).notNull(),
  stateCode: varchar("state_code", { length: 2 }).notNull(), // "MN", "NV", etc.
  zoneNumber: integer("zone_number").notNull(), // 1-19 for Estes
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Carrier Zone Matrix - Maps origin zone + destination zone to rate letter
export const carrierZoneMatrix = pgTable("carrier_zone_matrix", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierId: uuid("carrier_id").references(() => freightCarriers.id).notNull(),
  fromZone: integer("from_zone").notNull(),
  toZone: integer("to_zone").notNull(),
  rateLetter: varchar("rate_letter", { length: 1 }).notNull(), // A-H for Estes
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Carrier Rates - Rate table per letter and weight tier
export const carrierRates = pgTable("carrier_rates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierId: uuid("carrier_id").references(() => freightCarriers.id).notNull(),
  rateLetter: varchar("rate_letter", { length: 1 }).notNull(), // A-H
  minWeight: integer("min_weight").notNull(), // 1, 500, 1000, etc.
  maxWeight: integer("max_weight"), // 499, 999, null for 5000+
  ratePerLb: decimal("rate_per_lb", { precision: 10, scale: 4 }).notNull(), // $0.24, etc.
  minimumCharge: decimal("minimum_charge", { precision: 10, scale: 2 }), // $110.00, etc.
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Markup types
export const MARKUP_TYPES = ["DOLLAR", "PERCENT"] as const;
export type MarkupType = typeof MARKUP_TYPES[number];

// Carrier Markups - Admin-configured markups for freight, fuel, accessorials
export const carrierMarkups = pgTable("carrier_markups", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierId: uuid("carrier_id").references(() => freightCarriers.id).notNull(),
  // Freight rate markup
  freightMarkupType: varchar("freight_markup_type").notNull().default("PERCENT"), // DOLLAR or PERCENT
  freightMarkupValue: decimal("freight_markup_value", { precision: 10, scale: 2 }).notNull().default("0"),
  // Fuel surcharge markup
  fuelMarkupType: varchar("fuel_markup_type").notNull().default("PERCENT"),
  fuelMarkupValue: decimal("fuel_markup_value", { precision: 10, scale: 2 }).notNull().default("0"),
  // Accessorial markup
  accessorialMarkupType: varchar("accessorial_markup_type").notNull().default("PERCENT"),
  accessorialMarkupValue: decimal("accessorial_markup_value", { precision: 10, scale: 2 }).notNull().default("0"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Fuel Surcharge History - Fuel price ranges and surcharge percentages
export const fuelSurchargeHistory = pgTable("fuel_surcharge_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierId: uuid("carrier_id").references(() => freightCarriers.id).notNull(),
  fuelPriceMin: decimal("fuel_price_min", { precision: 10, scale: 2 }).notNull(), // $3.50
  fuelPriceMax: decimal("fuel_price_max", { precision: 10, scale: 2 }), // $3.74, null for top tier
  surchargePercent: decimal("surcharge_percent", { precision: 10, scale: 2 }).notNull(), // 30.50
  effectiveDate: timestamp("effective_date").default(sql`now()`).notNull(),
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Carrier Accessorials - Additional charges (liftgate, residential, etc.)
export const carrierAccessorials = pgTable("carrier_accessorials", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  carrierId: uuid("carrier_id").references(() => freightCarriers.id).notNull(),
  code: varchar("code").notNull(), // "LIFTGATE", "RESIDENTIAL", etc.
  name: text("name").notNull(), // "Liftgate Service"
  description: text("description"),
  chargeType: varchar("charge_type").notNull().default("FLAT"), // FLAT, PER_LB, PERCENT
  baseAmount: decimal("base_amount", { precision: 10, scale: 2 }).notNull(), // $50.00
  conditions: jsonb("conditions"), // Complex rules for when to apply
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Diesel Fuel Prices - Historical tracking from EIA RSS feed
export const dieselFuelPrices = pgTable("diesel_fuel_prices", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  pricePerGallon: decimal("price_per_gallon", { precision: 10, scale: 3 }).notNull(), // $3.567
  eiaPublishDate: timestamp("eia_publish_date").notNull(), // When EIA published this price
  weekStartDate: timestamp("week_start_date").notNull(), // Monday of the week this price is for
  fetchedAt: timestamp("fetched_at").default(sql`now()`).notNull(), // When we retrieved it
  source: text("source").default("EIA_RSS").notNull(), // Source identifier
  rssData: jsonb("rss_data"), // Raw RSS feed data for debugging
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Estes Distance Surcharge Zip Codes - Zip codes subject to $65 distance surcharge
export const estesDistanceSurchargeZips = pgTable("estes_distance_surcharge_zips", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  zipCode: varchar("zip_code", { length: 5 }).notNull().unique(),
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Stock Notifications - Track user requests to be notified when inventory is available
export const stockNotifications = pgTable("stock_notifications", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  requestedGrade: text("requested_grade").notNull(), // "A", "B+", "B-", etc.
  requestedQuantity: integer("requested_quantity").notNull().default(1),
  notified: boolean("notified").default(false), // Whether user has been notified
  notifiedAt: timestamp("notified_at"), // When the notification was sent
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Insert schemas for freight carrier tables
export const insertFreightCarrierSchema = createInsertSchema(freightCarriers).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertCarrierZoneSchema = createInsertSchema(carrierZones).omit({
  id: true,
  createdAt: true,
});

export const insertCarrierZoneMatrixSchema = createInsertSchema(carrierZoneMatrix).omit({
  id: true,
  createdAt: true,
});

export const insertCarrierRateSchema = createInsertSchema(carrierRates).omit({
  id: true,
  createdAt: true,
});

export const insertCarrierMarkupSchema = createInsertSchema(carrierMarkups).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertFuelSurchargeHistorySchema = createInsertSchema(fuelSurchargeHistory).omit({
  id: true,
  createdAt: true,
});

export const insertCarrierAccessorialSchema = createInsertSchema(carrierAccessorials).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertDieselFuelPriceSchema = createInsertSchema(dieselFuelPrices).omit({
  id: true,
  fetchedAt: true,
  createdAt: true,
});

export const insertEstesDistanceSurchargeZipSchema = createInsertSchema(estesDistanceSurchargeZips).omit({
  id: true,
  createdAt: true,
});

export const insertStockNotificationSchema = createInsertSchema(stockNotifications).omit({
  id: true,
  notified: true,
  notifiedAt: true,
  createdAt: true,
});

// Email Template Categories
export const EMAIL_TEMPLATE_CATEGORIES = ["INVITATION", "APPLICATION", "ORDER", "NOTIFICATION", "SYSTEM"] as const;
export type EmailTemplateCategory = typeof EMAIL_TEMPLATE_CATEGORIES[number];

// Email Invitation Statuses
export const EMAIL_INVITATION_STATUSES = ["PENDING", "SENT", "DELIVERED", "FAILED"] as const;
export type EmailInvitationStatus = typeof EMAIL_INVITATION_STATUSES[number];

// Email Templates - Stores reusable email templates with placeholders
export const emailTemplates = pgTable("email_templates", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  key: varchar("key").notNull().unique(), // Unique identifier like "invitation_standard", "application_approved"
  name: text("name").notNull(), // Human-readable name like "Standard Invitation"
  subject: text("subject").notNull(), // Email subject with placeholders
  preheader: text("preheader"), // Preview text shown in email clients (appears after subject in inbox)
  body: text("body").notNull(), // HTML body with placeholders like {{customerName}}, {{companyName}}
  category: varchar("category").$type<EmailTemplateCategory>().notNull().default("INVITATION"),
  description: text("description"), // Optional description of when to use this template
  placeholders: text("placeholders").array(), // List of available placeholders for reference
  customMessageDefault: text("custom_message_default"), // Default custom message for this template
  version: integer("version").default(1), // Current version number
  isActive: boolean("is_active").default(true),
  isSystem: boolean("is_system").default(false), // System templates can't be deleted
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Email Template Versions - Stores version history for each template
export const emailTemplateVersions = pgTable("email_template_versions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: uuid("template_id").references(() => emailTemplates.id).notNull(),
  version: integer("version").notNull(), // Version number (1, 2, 3, etc.)
  subject: text("subject").notNull(), // Frozen subject at this version
  preheader: text("preheader"), // Frozen preheader at this version
  body: text("body").notNull(), // Frozen body at this version
  customMessageDefault: text("custom_message_default"), // Frozen default custom message
  placeholders: text("placeholders").array(), // Frozen placeholder list
  createdBy: varchar("created_by").references(() => users.id), // Who created this version
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  templateVersionIdx: index("email_template_versions_template_version_idx").on(table.templateId, table.version),
}));

// Email Invitations - Tracks invitation emails sent to potential customers
export const emailInvitations = pgTable("email_invitations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: uuid("template_id").references(() => emailTemplates.id).notNull(),
  templateVersionId: uuid("template_version_id").references(() => emailTemplateVersions.id), // Which version was used
  templateVersion: integer("template_version"), // Version number at time of send
  recipientEmail: varchar("recipient_email").notNull(),
  recipientName: text("recipient_name"),
  recipientCompany: text("recipient_company"),
  placeholderData: jsonb("placeholder_data"), // JSON object with placeholder values used
  // Snapshots - exact content sent for historical viewing
  subjectSnapshot: text("subject_snapshot"), // Rendered subject at time of send
  bodyHtmlSnapshot: text("body_html_snapshot"), // Rendered HTML body at time of send
  customMessage: text("custom_message"), // Custom message used for this invitation
  sentByUserId: varchar("sent_by_user_id").references(() => users.id),
  salesRepUserId: varchar("sales_rep_user_id").references(() => users.id), // Sales rep whose reply-to email was used (for attribution)
  status: varchar("status").$type<EmailInvitationStatus>().notNull().default("PENDING"),
  sendgridMessageId: text("sendgrid_message_id"), // For tracking delivery
  errorMessage: text("error_message"), // If sending failed
  sentAt: timestamp("sent_at"),
  deliveredAt: timestamp("delivered_at"), // When SendGrid confirms delivery
  // Signup tracking
  signupUserId: varchar("signup_user_id").references(() => users.id), // Links to user if they signed up
  signedUpAt: timestamp("signed_up_at"), // When they created an account
  // Order tracking
  firstOrderId: uuid("first_order_id").references(() => orders.id), // Links to their first order
  firstOrderAt: timestamp("first_order_at"), // When they placed their first order
  // Engagement tracking
  trackingToken: varchar("tracking_token").unique(), // Unique token for this invitation's tracking URLs
  openedAt: timestamp("opened_at"), // When the email was first opened (via tracking pixel)
  openCount: integer("open_count").default(0), // How many times the email was opened
  clickCount: integer("click_count").default(0), // Total link clicks
  lastClickedAt: timestamp("last_clicked_at"), // When the last link was clicked
  unsubscribedAt: timestamp("unsubscribed_at"), // When the recipient unsubscribed from invitation emails
  isAutoFired: boolean("is_auto_fired").default(false), // True if auto-fired from failed login (not manually sent by admin)
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Email Link Clicks - Tracks individual link clicks from invitation emails
export const emailLinkClicks = pgTable("email_link_clicks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  invitationId: uuid("invitation_id").references(() => emailInvitations.id).notNull(),
  linkType: varchar("link_type").notNull(), // e.g., "signup", "showroom", "site", or custom URL
  destinationUrl: text("destination_url").notNull(), // Where they were redirected to
  clickedAt: timestamp("clicked_at").default(sql`now()`),
  userAgent: text("user_agent"), // Browser info
  ipHash: varchar("ip_hash"), // Hashed IP for privacy
});

// Invitation Sends - Tracks each email send event for follow-up sequence tracking
// Each time an invitation email is sent (initial or follow-up), a record is created here
export const invitationSends = pgTable("invitation_sends", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  invitationId: uuid("invitation_id").references(() => emailInvitations.id).notNull(),
  templateId: uuid("template_id").references(() => emailTemplates.id).notNull(),
  templateVersionId: uuid("template_version_id").references(() => emailTemplateVersions.id),
  sequenceNumber: integer("sequence_number").notNull().default(1), // 1 = first send, 2 = first follow-up, etc.
  sendType: varchar("send_type").notNull().default("initial"), // "initial", "followup_no_open", "followup_no_click"
  // Snapshots - exact content sent for historical viewing
  subjectSnapshot: text("subject_snapshot"),
  bodyHtmlSnapshot: text("body_html_snapshot"),
  customMessage: text("custom_message"),
  // Delivery tracking
  sendgridMessageId: text("sendgrid_message_id"),
  status: varchar("status").$type<EmailInvitationStatus>().notNull().default("PENDING"),
  errorMessage: text("error_message"),
  sentByUserId: varchar("sent_by_user_id").references(() => users.id),
  sentAt: timestamp("sent_at"),
  deliveredAt: timestamp("delivered_at"),
  // Engagement tracking for this specific send
  openedAt: timestamp("opened_at"),
  openCount: integer("open_count").default(0),
  clickCount: integer("click_count").default(0),
  lastClickedAt: timestamp("last_clicked_at"),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  invitationSendIdx: index("invitation_sends_invitation_idx").on(table.invitationId),
  sequenceIdx: index("invitation_sends_sequence_idx").on(table.invitationId, table.sequenceNumber),
}));

// Email Assets - Centralized storage for email images with aliases
export const emailAssets = pgTable("email_assets", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  alias: varchar("alias").notNull().unique(), // Shorthand code like "promo_banner", must be unique
  url: text("url").notNull(), // Full URL for email use (e.g., https://openbox.shopjimmy.com/objects/...)
  relativePath: text("relative_path"), // Relative path for preview (e.g., /objects/...)
  fileName: text("file_name").notNull(), // Original filename
  mimeType: varchar("mime_type"), // Image MIME type
  uploadedBy: varchar("uploaded_by").references(() => users.id), // Who uploaded it
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// User Signatures - Per-user email signature settings for admin users
export const userSignatures = pgTable("user_signatures", {
  userId: varchar("user_id").primaryKey().references(() => users.id), // One signature per user
  firstName: text("first_name"),
  lastName: text("last_name"),
  title: text("title"), // Job title/position
  company: text("company"),
  phone: text("phone"),
  email: text("email"), // Reply-to email address
  followUpSubject: text("follow_up_subject"), // Subject for follow-up mailto links
  followUpBody: text("follow_up_body"), // Body template for follow-up mailto links (use {{name}} for recipient name)
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Newsletter Subscribers - Marketing email signups from the showroom
export const newsletterSubscribers = pgTable("newsletter_subscribers", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").notNull().unique(),
  source: varchar("source").default("showroom"), // Where the signup came from (showroom, splash, etc.)
  subscribedAt: timestamp("subscribed_at").default(sql`now()`),
  unsubscribedAt: timestamp("unsubscribed_at"), // When they opted out (null = still subscribed)
  unsubscribeToken: varchar("unsubscribe_token").unique(), // Token for unsubscribe links
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Insert schemas for email tables
export const insertEmailTemplateSchema = createInsertSchema(emailTemplates).omit({
  id: true,
  version: true,
  createdAt: true,
  updatedAt: true,
});

export const insertEmailTemplateVersionSchema = createInsertSchema(emailTemplateVersions).omit({
  id: true,
  createdAt: true,
});

export const insertEmailInvitationSchema = createInsertSchema(emailInvitations).omit({
  id: true,
  status: true,
  sendgridMessageId: true,
  errorMessage: true,
  sentAt: true,
  deliveredAt: true,
  signupUserId: true,
  signedUpAt: true,
  firstOrderId: true,
  firstOrderAt: true,
  trackingToken: true,
  openedAt: true,
  openCount: true,
  clickCount: true,
  lastClickedAt: true,
  createdAt: true,
});

export const insertEmailLinkClickSchema = createInsertSchema(emailLinkClicks).omit({
  id: true,
  clickedAt: true,
});

export const insertInvitationSendSchema = createInsertSchema(invitationSends).omit({
  id: true,
  sequenceNumber: true, // Always computed atomically by storage layer
  deliveredAt: true,
  openedAt: true,
  openCount: true,
  clickCount: true,
  lastClickedAt: true,
  createdAt: true,
});

export const insertEmailAssetSchema = createInsertSchema(emailAssets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertUserSignatureSchema = createInsertSchema(userSignatures).omit({
  updatedAt: true,
});

export const upsertUserSignatureSchema = insertUserSignatureSchema.partial().required({ userId: true });

export const insertNewsletterSubscriberSchema = createInsertSchema(newsletterSubscribers).omit({
  id: true,
  subscribedAt: true,
  unsubscribedAt: true,
  unsubscribeToken: true,
  createdAt: true,
});

export type InsertNewsletterSubscriber = z.infer<typeof insertNewsletterSubscriberSchema>;
export type NewsletterSubscriber = typeof newsletterSubscribers.$inferSelect;

// Pickup Scheduling - Secure links sent to logistics contacts for local pickup scheduling
export const PICKUP_LINK_STATUSES = ["ACTIVE", "USED", "EXPIRED", "CANCELLED"] as const;
export type PickupLinkStatus = typeof PICKUP_LINK_STATUSES[number];

export const pickupSchedulingLinks = pgTable("pickup_scheduling_links", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  token: varchar("token", { length: 64 }).notNull().unique(), // Secure random token for URL
  logisticsContactEmail: text("logistics_contact_email").notNull(), // Email of logistics contact
  logisticsContactName: text("logistics_contact_name"),
  warehouseCode: varchar("warehouse_code").notNull(), // Which warehouse for pickup
  status: varchar("status").default("ACTIVE").notNull(), // ACTIVE, USED, EXPIRED, CANCELLED
  expiresAt: timestamp("expires_at").notNull(), // Link expiration (e.g., 7 days from order)
  createdAt: timestamp("created_at").default(sql`now()`),
});

// Pickup Appointments - Actual booked pickup times
export const PICKUP_TRANSPORT_TYPES = ["OWN_TRUCK", "CARRIER"] as const;
export type PickupTransportType = typeof PICKUP_TRANSPORT_TYPES[number];

export const pickupAppointments = pgTable("pickup_appointments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  linkId: uuid("link_id").references(() => pickupSchedulingLinks.id).notNull(),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  scheduledDate: timestamp("scheduled_date").notNull(), // Date of pickup
  scheduledTimeSlot: varchar("scheduled_time_slot").notNull(), // "08:00-08:30", "08:30-09:00", etc.
  transportType: varchar("transport_type").notNull(), // OWN_TRUCK or CARRIER
  carrierName: text("carrier_name"), // If CARRIER, which carrier
  carrierContact: text("carrier_contact"), // Contact info for carrier
  bolUrl: text("bol_url"), // URL to uploaded bill of lading
  notes: text("notes"), // Additional notes
  confirmedAt: timestamp("confirmed_at"), // When the appointment was confirmed
  bookedByName: text("booked_by_name"), // Who made the booking
  bookedByEmail: text("booked_by_email"),
  bookedByPhone: text("booked_by_phone"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

// Insert schemas for pickup scheduling
export const insertPickupSchedulingLinkSchema = createInsertSchema(pickupSchedulingLinks).omit({
  id: true,
  status: true,
  createdAt: true,
});

export const insertPickupAppointmentSchema = createInsertSchema(pickupAppointments).omit({
  id: true,
  confirmedAt: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type FreightCarrier = typeof freightCarriers.$inferSelect;
export type InsertFreightCarrier = z.infer<typeof insertFreightCarrierSchema>;
export type CarrierZone = typeof carrierZones.$inferSelect;
export type InsertCarrierZone = z.infer<typeof insertCarrierZoneSchema>;
export type CarrierZoneMatrix = typeof carrierZoneMatrix.$inferSelect;
export type InsertCarrierZoneMatrix = z.infer<typeof insertCarrierZoneMatrixSchema>;
export type CarrierRate = typeof carrierRates.$inferSelect;
export type InsertCarrierRate = z.infer<typeof insertCarrierRateSchema>;
export type CarrierMarkup = typeof carrierMarkups.$inferSelect;
export type InsertCarrierMarkup = z.infer<typeof insertCarrierMarkupSchema>;
export type FuelSurchargeHistory = typeof fuelSurchargeHistory.$inferSelect;
export type InsertFuelSurchargeHistory = z.infer<typeof insertFuelSurchargeHistorySchema>;
export type CarrierAccessorial = typeof carrierAccessorials.$inferSelect;
export type InsertCarrierAccessorial = z.infer<typeof insertCarrierAccessorialSchema>;
export type DieselFuelPrice = typeof dieselFuelPrices.$inferSelect;
export type InsertDieselFuelPrice = z.infer<typeof insertDieselFuelPriceSchema>;
export type EstesDistanceSurchargeZip = typeof estesDistanceSurchargeZips.$inferSelect;
export type InsertEstesDistanceSurchargeZip = z.infer<typeof insertEstesDistanceSurchargeZipSchema>;
export type StockNotification = typeof stockNotifications.$inferSelect;
export type InsertStockNotification = z.infer<typeof insertStockNotificationSchema>;
export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;
export type EmailTemplateVersion = typeof emailTemplateVersions.$inferSelect;
export type InsertEmailTemplateVersion = z.infer<typeof insertEmailTemplateVersionSchema>;
export type EmailInvitation = typeof emailInvitations.$inferSelect;
export type InsertEmailInvitation = z.infer<typeof insertEmailInvitationSchema>;
export type EmailLinkClick = typeof emailLinkClicks.$inferSelect;
export type InsertEmailLinkClick = z.infer<typeof insertEmailLinkClickSchema>;
export type InvitationSend = typeof invitationSends.$inferSelect;
export type InsertInvitationSend = z.infer<typeof insertInvitationSendSchema>;
export type EmailAsset = typeof emailAssets.$inferSelect;
export type InsertEmailAsset = z.infer<typeof insertEmailAssetSchema>;
export type UserSignature = typeof userSignatures.$inferSelect;
export type InsertUserSignature = z.infer<typeof insertUserSignatureSchema>;
export type UpsertUserSignature = z.infer<typeof upsertUserSignatureSchema>;
export type PickupSchedulingLink = typeof pickupSchedulingLinks.$inferSelect;
export type InsertPickupSchedulingLink = z.infer<typeof insertPickupSchedulingLinkSchema>;
export type PickupAppointment = typeof pickupAppointments.$inferSelect;
export type InsertPickupAppointment = z.infer<typeof insertPickupAppointmentSchema>;

// ============================================================================
// PUBLIC API SYSTEM - External API access for B2B customers
// ============================================================================

// API Credential Status
export const API_CREDENTIAL_STATUS = ["pending", "approved", "rejected", "suspended", "revoked"] as const;
export type ApiCredentialStatus = typeof API_CREDENTIAL_STATUS[number];

// API Scopes - What operations are allowed
export const API_SCOPES = [
  "inventory:read",      // View products and stock levels
  "orders:read",         // View their own orders
  "orders:write",        // Place new orders
  "tracking:read",       // Get tracking information
  "webhooks:manage",     // Manage webhook subscriptions
] as const;
export type ApiScope = typeof API_SCOPES[number];

// Public API Credentials - B2B customer API keys with approval workflow
export const publicApiCredentials = pgTable("public_api_credentials", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id).notNull(),
  name: text("name").notNull(), // Friendly name for the API key (e.g., "Production Integration")
  description: text("description"), // Optional description of intended use
  keyHash: text("key_hash").notNull(), // bcrypt hash of the actual API key
  keyPrefix: varchar("key_prefix", { length: 16 }).notNull(), // First 16 chars for identification (e.g., "obx_live_abc123")
  status: varchar("status").$type<ApiCredentialStatus>().notNull().default("pending"),
  scopes: text("scopes").array().default(sql`ARRAY['inventory:read', 'orders:read', 'orders:write', 'tracking:read']::text[]`),
  // Rate limiting
  monthlyInventoryLimit: integer("monthly_inventory_limit").notNull().default(1000), // Requests per month for inventory endpoints
  currentMonthInventoryUsage: integer("current_month_inventory_usage").notNull().default(0),
  usageResetAt: timestamp("usage_reset_at").notNull().default(sql`date_trunc('month', now()) + interval '1 month'`),
  // IP whitelisting (optional)
  allowedIps: text("allowed_ips").array(), // Optional IP whitelist
  // Approval tracking
  requestedAt: timestamp("requested_at").default(sql`now()`),
  reviewedBy: varchar("reviewed_by").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  rejectionReason: text("rejection_reason"),
  // Usage tracking
  lastUsedAt: timestamp("last_used_at"),
  totalRequests: integer("total_requests").notNull().default(0),
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  accountIdIdx: index("public_api_credentials_account_id_idx").on(table.accountId),
  statusIdx: index("public_api_credentials_status_idx").on(table.status),
  keyPrefixIdx: index("public_api_credentials_key_prefix_idx").on(table.keyPrefix),
}));

// Public API Usage Logs - Request-level logging for analytics and debugging
export const publicApiUsageLogs = pgTable("public_api_usage_logs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  credentialId: uuid("credential_id").references(() => publicApiCredentials.id).notNull(),
  accountId: uuid("account_id").references(() => accounts.id).notNull(),
  endpoint: text("endpoint").notNull(), // API endpoint called (e.g., "/api/public/v1/products")
  method: varchar("method", { length: 10 }).notNull(), // HTTP method (GET, POST, etc.)
  statusCode: integer("status_code").notNull(),
  responseTimeMs: integer("response_time_ms"), // Milliseconds
  requestBody: jsonb("request_body"), // Sanitized request body (no sensitive data)
  errorMessage: text("error_message"), // Error message if failed
  ipAddress: varchar("ip_address", { length: 45 }), // IPv4 or IPv6
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  credentialIdIdx: index("public_api_usage_logs_credential_id_idx").on(table.credentialId),
  accountIdIdx: index("public_api_usage_logs_account_id_idx").on(table.accountId),
  createdAtIdx: index("public_api_usage_logs_created_at_idx").on(table.createdAt),
}));

// Webhook Event Types
export const WEBHOOK_EVENT_TYPES = [
  "order.created",
  "order.confirmed",
  "order.shipped",
  "order.delivered",
  "order.cancelled",
  "inventory.updated",
  "tracking.updated",
] as const;
export type WebhookEventType = typeof WEBHOOK_EVENT_TYPES[number];

// Public API Webhooks - Webhook subscriptions for event notifications
export const publicApiWebhooks = pgTable("public_api_webhooks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  credentialId: uuid("credential_id").references(() => publicApiCredentials.id).notNull(),
  accountId: uuid("account_id").references(() => accounts.id).notNull(),
  eventTypes: text("event_types").array().notNull(), // Array of event types to subscribe to
  webhookUrl: text("webhook_url").notNull(), // URL to send webhook payloads
  secretHash: text("secret_hash").notNull(), // bcrypt hash of webhook secret for verification during secret rotation
  encryptedHmacSecret: text("encrypted_hmac_secret").notNull(), // AES-256-GCM encrypted HMAC secret for signature generation
  isActive: boolean("is_active").default(true),
  // Reliability tracking
  lastDeliveryAt: timestamp("last_delivery_at"),
  lastDeliveryStatus: varchar("last_delivery_status", { length: 20 }), // "success", "failed"
  consecutiveFailures: integer("consecutive_failures").default(0),
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  credentialIdIdx: index("public_api_webhooks_credential_id_idx").on(table.credentialId),
  accountIdIdx: index("public_api_webhooks_account_id_idx").on(table.accountId),
}));

// Webhook Delivery Logs - Track webhook delivery attempts
export const publicApiWebhookDeliveries = pgTable("public_api_webhook_deliveries", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  webhookId: uuid("webhook_id").references(() => publicApiWebhooks.id).notNull(),
  eventType: varchar("event_type", { length: 50 }).notNull(),
  payload: jsonb("payload").notNull(), // The webhook payload sent
  statusCode: integer("status_code"), // HTTP response status
  responseBody: text("response_body"), // First 1000 chars of response
  responseTimeMs: integer("response_time_ms"),
  success: boolean("success").notNull(),
  errorMessage: text("error_message"),
  attemptNumber: integer("attempt_number").notNull().default(1),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  webhookIdIdx: index("public_api_webhook_deliveries_webhook_id_idx").on(table.webhookId),
  createdAtIdx: index("public_api_webhook_deliveries_created_at_idx").on(table.createdAt),
}));

// Insert schemas for public API
export const insertPublicApiCredentialSchema = createInsertSchema(publicApiCredentials).omit({
  id: true,
  keyHash: true, // Set by server
  keyPrefix: true, // Set by server
  status: true, // Default pending
  currentMonthInventoryUsage: true,
  usageResetAt: true,
  reviewedBy: true,
  reviewedAt: true,
  rejectionReason: true,
  lastUsedAt: true,
  totalRequests: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPublicApiUsageLogSchema = createInsertSchema(publicApiUsageLogs).omit({
  id: true,
  createdAt: true,
});

export const insertPublicApiWebhookSchema = createInsertSchema(publicApiWebhooks).omit({
  id: true,
  secretHash: true, // Set by server
  lastDeliveryAt: true,
  lastDeliveryStatus: true,
  consecutiveFailures: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPublicApiWebhookDeliverySchema = createInsertSchema(publicApiWebhookDeliveries).omit({
  id: true,
  createdAt: true,
});

// Types for public API
export type PublicApiCredential = typeof publicApiCredentials.$inferSelect;
export type InsertPublicApiCredential = z.infer<typeof insertPublicApiCredentialSchema>;
export type PublicApiUsageLog = typeof publicApiUsageLogs.$inferSelect;
export type InsertPublicApiUsageLog = z.infer<typeof insertPublicApiUsageLogSchema>;
export type PublicApiWebhook = typeof publicApiWebhooks.$inferSelect;
export type InsertPublicApiWebhook = z.infer<typeof insertPublicApiWebhookSchema>;
export type PublicApiWebhookDelivery = typeof publicApiWebhookDeliveries.$inferSelect;
export type InsertPublicApiWebhookDelivery = z.infer<typeof insertPublicApiWebhookDeliverySchema>;

// ============================================================================
// SHOPIFY INTEGRATION
// ============================================================================

// Shopify Sync Schedule Options
export const SHOPIFY_SYNC_SCHEDULES = ["manual", "hourly", "every_6_hours", "daily"] as const;
export type ShopifySyncSchedule = typeof SHOPIFY_SYNC_SCHEDULES[number];

// Shopify Sync Status
export const SHOPIFY_SYNC_STATUSES = ["pending", "in_progress", "completed", "failed"] as const;
export type ShopifySyncStatus = typeof SHOPIFY_SYNC_STATUSES[number];

// Shopify Connections - Store per-account Shopify credentials and sync settings
export const shopifyConnections = pgTable("shopify_connections", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id).notNull().unique(),
  // Shopify store details
  shopName: varchar("shop_name", { length: 255 }).notNull(), // e.g., "my-store" from my-store.myshopify.com
  accessToken: text("access_token").notNull(), // Shopify Admin API access token (encrypted at rest by DB)
  // Sync configuration
  syncSchedule: varchar("sync_schedule").$type<ShopifySyncSchedule>().notNull().default("manual"),
  syncAllCategories: boolean("sync_all_categories").default(true),
  selectedCategories: text("selected_categories").array().default(sql`ARRAY[]::text[]`), // Category IDs to sync if not all
  // Field mapping options
  syncPricing: boolean("sync_pricing").default(true),
  syncImages: boolean("sync_images").default(true),
  syncDimensions: boolean("sync_dimensions").default(true), // As metafields
  useCompareAtPrice: boolean("use_compare_at_price").default(false), // Show MSRP as compare-at
  // Location mapping (optional - for inventory levels)
  shopifyLocationId: varchar("shopify_location_id", { length: 100 }),
  // Status tracking
  isActive: boolean("is_active").default(true),
  lastSyncAt: timestamp("last_sync_at"),
  lastSyncStatus: varchar("last_sync_status").$type<ShopifySyncStatus>(),
  lastSyncError: text("last_sync_error"),
  nextScheduledSync: timestamp("next_scheduled_sync"),
  // Stats
  totalProductsSynced: integer("total_products_synced").default(0),
  totalVariantsSynced: integer("total_variants_synced").default(0),
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  accountIdIdx: index("shopify_connections_account_id_idx").on(table.accountId),
  nextSyncIdx: index("shopify_connections_next_sync_idx").on(table.nextScheduledSync),
}));

// Shopify Product Mappings - Track which OpenBox models map to which Shopify products
export const shopifyProductMappings = pgTable("shopify_product_mappings", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  connectionId: uuid("connection_id").references(() => shopifyConnections.id).notNull(),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  shopifyProductId: varchar("shopify_product_id", { length: 100 }).notNull(), // Shopify's product ID
  shopifyProductHandle: varchar("shopify_product_handle", { length: 255 }), // URL slug in Shopify
  // Variant mappings stored as JSON: { "A": "shopify_variant_id", "B": "shopify_variant_id", ... }
  gradeVariantMappings: jsonb("grade_variant_mappings").default(sql`'{}'::jsonb`),
  lastSyncedAt: timestamp("last_synced_at"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  connectionModelIdx: uniqueIndex("shopify_product_mappings_connection_model_idx").on(table.connectionId, table.modelId),
  shopifyProductIdx: index("shopify_product_mappings_shopify_product_idx").on(table.connectionId, table.shopifyProductId),
}));

// Shopify Sync Logs - Track sync history and errors
export const shopifySyncLogs = pgTable("shopify_sync_logs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  connectionId: uuid("connection_id").references(() => shopifyConnections.id).notNull(),
  // Sync details
  syncType: varchar("sync_type", { length: 20 }).notNull(), // "manual", "scheduled", "webhook"
  status: varchar("status").$type<ShopifySyncStatus>().notNull(),
  // Progress tracking
  startedAt: timestamp("started_at").notNull(),
  completedAt: timestamp("completed_at"),
  // Results
  productsCreated: integer("products_created").default(0),
  productsUpdated: integer("products_updated").default(0),
  variantsUpdated: integer("variants_updated").default(0),
  inventoryUpdated: integer("inventory_updated").default(0),
  // Errors
  errorCount: integer("error_count").default(0),
  errors: jsonb("errors").default(sql`'[]'::jsonb`), // Array of { modelId, error, timestamp }
  // Summary
  summary: text("summary"),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  connectionIdIdx: index("shopify_sync_logs_connection_id_idx").on(table.connectionId),
  createdAtIdx: index("shopify_sync_logs_created_at_idx").on(table.createdAt),
}));

// Insert schemas for Shopify
export const insertShopifyConnectionSchema = createInsertSchema(shopifyConnections).omit({
  id: true,
  lastSyncAt: true,
  lastSyncStatus: true,
  lastSyncError: true,
  nextScheduledSync: true,
  totalProductsSynced: true,
  totalVariantsSynced: true,
  createdAt: true,
  updatedAt: true,
});

export const insertShopifyProductMappingSchema = createInsertSchema(shopifyProductMappings).omit({
  id: true,
  lastSyncedAt: true,
  createdAt: true,
  updatedAt: true,
});

export const insertShopifySyncLogSchema = createInsertSchema(shopifySyncLogs).omit({
  id: true,
  createdAt: true,
});

// Types for Shopify
export type ShopifyConnection = typeof shopifyConnections.$inferSelect;
export type InsertShopifyConnection = z.infer<typeof insertShopifyConnectionSchema>;
export type ShopifyProductMapping = typeof shopifyProductMappings.$inferSelect;
export type InsertShopifyProductMapping = z.infer<typeof insertShopifyProductMappingSchema>;
export type ShopifySyncLog = typeof shopifySyncLogs.$inferSelect;
export type InsertShopifySyncLog = z.infer<typeof insertShopifySyncLogSchema>;

// ============================================================================
// Scheduled Visits - In-person warehouse visits, phone calls, or video calls for B2B customers
// ============================================================================
export const VISIT_STATUSES = ["PENDING", "CONFIRMED", "COMPLETED", "CANCELLED", "NO_SHOW"] as const;
export type VisitStatus = typeof VISIT_STATUSES[number];

export const MEETING_TYPES = ["in-person", "phone", "video"] as const;
export type MeetingType = typeof MEETING_TYPES[number];

export const scheduledVisits = pgTable("scheduled_visits", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  accountId: uuid("account_id").references(() => accounts.id).notNull(),
  userId: varchar("user_id").references(() => users.id).notNull(), // The user who scheduled the visit
  scheduledDate: date("scheduled_date").notNull(), // Date of the visit/call
  scheduledTime: varchar("scheduled_time").notNull(), // Time slot (e.g., "09:00", "10:00")
  meetingType: varchar("meeting_type").$type<MeetingType>().notNull().default("in-person"), // in-person, phone, or video
  customerPhone: varchar("customer_phone"), // For phone calls - the number to call
  purpose: text("purpose"), // Optional notes about what they want to discuss
  status: varchar("status").$type<VisitStatus>().notNull().default("PENDING"),
  confirmedByUserId: varchar("confirmed_by_user_id").references(() => users.id), // Admin who confirmed
  confirmedAt: timestamp("confirmed_at"),
  notes: text("notes"), // Admin notes
  cancellationReason: text("cancellation_reason"),
  calendarInviteSent: boolean("calendar_invite_sent").default(false), // Track if ICS was sent
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  accountIdIdx: index("scheduled_visits_account_id_idx").on(table.accountId),
  scheduledDateIdx: index("scheduled_visits_date_idx").on(table.scheduledDate),
  statusIdx: index("scheduled_visits_status_idx").on(table.status),
  meetingTypeIdx: index("scheduled_visits_meeting_type_idx").on(table.meetingType),
}));

export const insertScheduledVisitSchema = createInsertSchema(scheduledVisits).omit({
  id: true,
  confirmedByUserId: true,
  confirmedAt: true,
  notes: true,
  cancellationReason: true,
  calendarInviteSent: true,
  createdAt: true,
  updatedAt: true,
});

export type ScheduledVisit = typeof scheduledVisits.$inferSelect;
export type InsertScheduledVisit = z.infer<typeof insertScheduledVisitSchema>;

// ============================================================================
// Report Schedules - Automated report generation and email delivery
// ============================================================================
export const REPORT_FREQUENCIES = ["DAILY", "WEEKLY", "MONTHLY"] as const;
export type ReportFrequency = typeof REPORT_FREQUENCIES[number];

export const REPORT_DATE_PRESETS = ["YESTERDAY", "LAST_7_DAYS", "LAST_WEEK", "MONTH_TO_DATE", "LAST_MONTH", "CUSTOM"] as const;
export type ReportDatePreset = typeof REPORT_DATE_PRESETS[number];

export const reportSchedules = pgTable("report_schedules", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  reportId: varchar("report_id").notNull(), // e.g., "sales", "inventory", etc.
  name: varchar("name").notNull(), // Human-readable schedule name
  description: text("description"),
  // Schedule configuration
  frequency: varchar("frequency").$type<ReportFrequency>().notNull().default("DAILY"),
  datePreset: varchar("date_preset").$type<ReportDatePreset>().notNull().default("YESTERDAY"),
  customDateFrom: date("custom_date_from"), // Only used if datePreset is CUSTOM
  customDateTo: date("custom_date_to"), // Only used if datePreset is CUSTOM
  // Timing
  scheduleTime: varchar("schedule_time").notNull().default("08:00"), // Time to run (HH:mm format)
  timezone: varchar("timezone").notNull().default("America/Chicago"),
  weeklyDays: text("weekly_days").array(), // For WEEKLY: ["monday", "wednesday", "friday"]
  monthlyDay: integer("monthly_day"), // For MONTHLY: day of month (1-31, or -1 for last day)
  // Filters (stored as JSON)
  filters: jsonb("filters").default(sql`'{}'::jsonb`), // Report-specific filters
  // Email configuration
  recipients: text("recipients").array().notNull(), // Array of email addresses
  emailSubject: text("email_subject").notNull(),
  emailBody: text("email_body"),
  // State
  isEnabled: boolean("is_enabled").default(true),
  lastRunAt: timestamp("last_run_at"),
  nextRunAt: timestamp("next_run_at"),
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  reportIdIdx: index("report_schedules_report_id_idx").on(table.reportId),
  isEnabledIdx: index("report_schedules_is_enabled_idx").on(table.isEnabled),
  nextRunAtIdx: index("report_schedules_next_run_at_idx").on(table.nextRunAt),
}));

export const REPORT_RUN_STATUSES = ["SUCCESS", "FAILED", "RUNNING"] as const;
export type ReportRunStatus = typeof REPORT_RUN_STATUSES[number];

export const reportScheduleRuns = pgTable("report_schedule_runs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  scheduleId: uuid("schedule_id").references(() => reportSchedules.id).notNull(),
  status: varchar("status").$type<ReportRunStatus>().notNull().default("RUNNING"),
  startedAt: timestamp("started_at").default(sql`now()`),
  completedAt: timestamp("completed_at"),
  // Results
  recipientCount: integer("recipient_count"),
  rowCount: integer("row_count"),
  errorMessage: text("error_message"),
  // Metadata
  triggeredBy: varchar("triggered_by"), // "schedule" or user ID for manual runs
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  scheduleIdIdx: index("report_schedule_runs_schedule_id_idx").on(table.scheduleId),
  startedAtIdx: index("report_schedule_runs_started_at_idx").on(table.startedAt),
}));

export const insertReportScheduleSchema = createInsertSchema(reportSchedules).omit({
  id: true,
  lastRunAt: true,
  nextRunAt: true,
  createdAt: true,
  updatedAt: true,
});

export const insertReportScheduleRunSchema = createInsertSchema(reportScheduleRuns).omit({
  id: true,
  createdAt: true,
});

export type ReportSchedule = typeof reportSchedules.$inferSelect;
export type InsertReportSchedule = z.infer<typeof insertReportScheduleSchema>;
export type ReportScheduleRun = typeof reportScheduleRuns.$inferSelect;
export type InsertReportScheduleRun = z.infer<typeof insertReportScheduleRunSchema>;

// ============================================================================
// Small Appliances System - Insert Schemas and Types
// ============================================================================

// Category Grade Profiles
export const insertCategoryGradeProfileSchema = createInsertSchema(categoryGradeProfiles).omit({
  id: true,
  createdAt: true,
});
export type CategoryGradeProfile = typeof categoryGradeProfiles.$inferSelect;
export type InsertCategoryGradeProfile = z.infer<typeof insertCategoryGradeProfileSchema>;

// Product Series
export const insertProductSeriesSchema = createInsertSchema(productSeries).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type ProductSeries = typeof productSeries.$inferSelect;
export type InsertProductSeries = z.infer<typeof insertProductSeriesSchema>;

// Product Variants
export const insertProductVariantSchema = createInsertSchema(productVariants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type ProductVariant = typeof productVariants.$inferSelect;
export type InsertProductVariant = z.infer<typeof insertProductVariantSchema>;

// Variant Grade Inventory
export const insertVariantGradeInventorySchema = createInsertSchema(variantGradeInventory).omit({
  id: true,
  updatedAt: true,
});
export type VariantGradeInventory = typeof variantGradeInventory.$inferSelect;
export type InsertVariantGradeInventory = z.infer<typeof insertVariantGradeInventorySchema>;

// Small Appliance Units
export const insertSmallApplianceUnitSchema = createInsertSchema(smallApplianceUnits).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type SmallApplianceUnit = typeof smallApplianceUnits.$inferSelect;
export type InsertSmallApplianceUnit = z.infer<typeof insertSmallApplianceUnitSchema>;

// Small Appliance Unit Events
export const insertSmallApplianceUnitEventSchema = createInsertSchema(smallApplianceUnitEvents).omit({
  id: true,
  createdAt: true,
});
export type SmallApplianceUnitEvent = typeof smallApplianceUnitEvents.$inferSelect;
export type InsertSmallApplianceUnitEvent = z.infer<typeof insertSmallApplianceUnitEventSchema>;

// ============================================================================
// Security and Error Logging System
// ============================================================================

// Security Event Types
export const SECURITY_EVENT_TYPES = [
  "FAILED_LOGIN",
  "SUCCESSFUL_LOGIN", 
  "RATE_LIMITED",
  "SUSPICIOUS_PATTERN",
  "IP_BLOCKED",
  "IP_UNBLOCKED",
  "PASSWORD_RESET_REQUEST",
  "PASSWORD_CHANGED",
  "ACCOUNT_LOCKED",
  "ACCOUNT_UNLOCKED"
] as const;
export type SecurityEventType = typeof SECURITY_EVENT_TYPES[number];

// Security Events table - persistent storage for login security events
export const securityEvents = pgTable("security_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  type: varchar("type").$type<SecurityEventType>().notNull(),
  ipAddress: varchar("ip_address"),
  email: varchar("email"),
  userId: varchar("user_id").references(() => users.id),
  userAgent: text("user_agent"),
  details: text("details"),
  metadata: jsonb("metadata"), // Additional structured data
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  typeIdx: index("security_events_type_idx").on(table.type),
  ipAddressIdx: index("security_events_ip_address_idx").on(table.ipAddress),
  emailIdx: index("security_events_email_idx").on(table.email),
  createdAtIdx: index("security_events_created_at_idx").on(table.createdAt),
}));

// Blocked IPs table - tracks currently blocked IP addresses
export const blockedIps = pgTable("blocked_ips", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  ipAddress: varchar("ip_address").notNull().unique(),
  reason: text("reason"),
  failedAttempts: integer("failed_attempts").default(0),
  blockedAt: timestamp("blocked_at").default(sql`now()`),
  expiresAt: timestamp("expires_at"), // null = permanent block
  unblockedAt: timestamp("unblocked_at"),
  unblockedBy: varchar("unblocked_by").references(() => users.id),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  ipAddressIdx: index("blocked_ips_ip_address_idx").on(table.ipAddress),
  expiresAtIdx: index("blocked_ips_expires_at_idx").on(table.expiresAt),
}));

// Application Error Categories
export const ERROR_CATEGORIES = [
  "API_ERROR",
  "VALIDATION_ERROR",
  "DATABASE_ERROR",
  "AUTHENTICATION_ERROR",
  "AUTHORIZATION_ERROR",
  "NETWORK_ERROR",
  "UI_ERROR",
  "UNHANDLED_EXCEPTION",
  "BUSINESS_LOGIC_ERROR",
  "INTEGRATION_ERROR",
  "RECEIVING",
  "SHIPPING",
  "OTHER"
] as const;
export type ErrorCategory = typeof ERROR_CATEGORIES[number];

// Error Severity Levels
export const ERROR_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;
export type ErrorSeverity = typeof ERROR_SEVERITIES[number];

// Application Errors table - tracks all errors for admin review
export const applicationErrors = pgTable("application_errors", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  category: varchar("category").$type<ErrorCategory>().notNull(),
  severity: varchar("severity").$type<ErrorSeverity>().notNull().default("MEDIUM"),
  message: text("message").notNull(),
  stack: text("stack"),
  fingerprint: varchar("fingerprint", { length: 64 }), // SHA-256 hash for grouping similar errors
  // User context
  userId: varchar("user_id").references(() => users.id),
  userEmail: varchar("user_email"),
  userName: varchar("user_name"),
  // Session context - links to user session for replay capability
  sessionId: uuid("session_id"), // References userSessions.id for session replay
  // Request context
  url: text("url"),
  method: varchar("method"), // GET, POST, etc.
  route: varchar("route"), // API route or page
  userAgent: text("user_agent"),
  ipAddress: varchar("ip_address"),
  // Additional context
  requestBody: jsonb("request_body"),
  responseStatus: integer("response_status"),
  metadata: jsonb("metadata"), // Component name, action, etc.
  // Status
  isResolved: boolean("is_resolved").default(false),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: varchar("resolved_by").references(() => users.id),
  resolvedNotes: text("resolved_notes"),
  fixResponse: text("fix_response"), // AI-generated or manual fix notes to help with future similar errors
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  categoryIdx: index("application_errors_category_idx").on(table.category),
  severityIdx: index("application_errors_severity_idx").on(table.severity),
  userIdIdx: index("application_errors_user_id_idx").on(table.userId),
  isResolvedIdx: index("application_errors_is_resolved_idx").on(table.isResolved),
  createdAtIdx: index("application_errors_created_at_idx").on(table.createdAt),
  fingerprintIdx: index("application_errors_fingerprint_idx").on(table.fingerprint),
}));

// Error Resolutions - stores structured fix information for AI knowledge base
export const errorResolutions = pgTable("error_resolutions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  fingerprint: varchar("fingerprint", { length: 64 }).notNull(), // Links to error fingerprint
  // Fix details
  fixDescription: text("fix_description").notNull(), // Brief description of what was changed
  rootCause: text("root_cause"), // What caused the error
  filesChanged: text("files_changed").array(), // Array of file paths that were modified
  codeChangeSummary: text("code_change_summary"), // Summary of the actual code changes
  // Context
  errorMessage: text("error_message"), // Original error message for context
  errorCategory: varchar("error_category").$type<ErrorCategory>(),
  // Tracking
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdByAI: boolean("created_by_ai").default(false), // Was this resolution created by AI?
  // Effectiveness tracking
  occurrencesBeforeFix: integer("occurrences_before_fix").default(0), // How many times error occurred before fix
  occurrencesAfterFix: integer("occurrences_after_fix").default(0), // Has error recurred after fix?
  isEffective: boolean("is_effective").default(true), // Was this fix effective?
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at"),
}, (table) => ({
  fingerprintIdx: index("error_resolutions_fingerprint_idx").on(table.fingerprint),
  createdAtIdx: index("error_resolutions_created_at_idx").on(table.createdAt),
}));

// Insert schemas
export const insertSecurityEventSchema = createInsertSchema(securityEvents).omit({
  id: true,
  createdAt: true,
});

export const insertBlockedIpSchema = createInsertSchema(blockedIps).omit({
  id: true,
  createdAt: true,
});

export const insertApplicationErrorSchema = createInsertSchema(applicationErrors).omit({
  id: true,
  createdAt: true,
});

export const insertErrorResolutionSchema = createInsertSchema(errorResolutions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type SecurityEvent = typeof securityEvents.$inferSelect;
export type InsertSecurityEvent = z.infer<typeof insertSecurityEventSchema>;
export type BlockedIp = typeof blockedIps.$inferSelect;
export type InsertBlockedIp = z.infer<typeof insertBlockedIpSchema>;
export type ApplicationError = typeof applicationErrors.$inferSelect;
export type InsertApplicationError = z.infer<typeof insertApplicationErrorSchema>;
export type ErrorResolution = typeof errorResolutions.$inferSelect;
export type InsertErrorResolution = z.infer<typeof insertErrorResolutionSchema>;

// =====================================================
// SESSION TRACKING & ANALYTICS
// =====================================================

// Session Event Types - categorizes what kind of interaction occurred
export const SESSION_EVENT_TYPES = [
  "PAGE_VIEW",        // User navigated to a page
  "CLICK",            // User clicked an element
  "RAGE_CLICK",       // User clicked same element 3+ times rapidly (within 1 second)
  "FORM_SUBMIT",      // User submitted a form
  "FORM_ERROR",       // Form validation failed
  "API_REQUEST",      // API call made
  "API_ERROR",        // API call returned error
  "TOAST_ERROR",      // Error toast notification shown
  "TOAST_WARNING",    // Warning toast shown
  "CONSOLE_ERROR",    // Browser console error
  "CONSOLE_WARN",     // Browser console warning
  "CART_ADD",         // Item added to cart
  "CART_REMOVE",      // Item removed from cart
  "CHECKOUT_START",   // User started checkout
  "CHECKOUT_COMPLETE", // Order placed successfully
  "CHECKOUT_ERROR",   // Checkout failed
  "LOGIN",            // User logged in
  "LOGOUT",           // User logged out
  "SCROLL_DEPTH",     // User scrolled to % of page
  "VISIBILITY_CHANGE", // Tab became visible/hidden
  "SESSION_START",    // New session started
  "SESSION_END",      // Session ended (tab closed/navigated away)
] as const;
export type SessionEventType = typeof SESSION_EVENT_TYPES[number];

// User Sessions table - tracks individual browsing sessions
export const userSessions = pgTable("user_sessions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id), // null for anonymous sessions
  userEmail: varchar("user_email"), // Captured at session start for easier querying
  // Session identification
  sessionToken: varchar("session_token").notNull().unique(), // Client-generated unique token
  // Session context
  userAgent: text("user_agent"),
  browserName: varchar("browser_name"),
  browserVersion: varchar("browser_version"),
  osName: varchar("os_name"),
  osVersion: varchar("os_version"),
  deviceType: varchar("device_type"), // desktop, mobile, tablet
  screenWidth: integer("screen_width"),
  screenHeight: integer("screen_height"),
  // Traffic source
  referrer: text("referrer"),
  utmSource: varchar("utm_source"),
  utmMedium: varchar("utm_medium"),
  utmCampaign: varchar("utm_campaign"),
  utmContent: varchar("utm_content"),
  utmTerm: varchar("utm_term"),
  // Session metrics
  pageViewCount: integer("page_view_count").default(0),
  clickCount: integer("click_count").default(0),
  errorCount: integer("error_count").default(0),
  hasErrors: boolean("has_errors").default(false), // Quick filter for problem sessions
  hasReplayData: boolean("has_replay_data").default(false), // Quick filter for sessions with replay
  // Timestamps
  startedAt: timestamp("started_at").default(sql`now()`),
  lastActivityAt: timestamp("last_activity_at").default(sql`now()`),
  endedAt: timestamp("ended_at"),
  durationSeconds: integer("duration_seconds"),
  // IP and Location (for geographic analysis)
  ipHash: varchar("ip_hash"), // Hashed IP for privacy-compliant analytics
  ipAddress: varchar("ip_address"), // Raw IP for admin visibility
  country: varchar("country"),
  region: varchar("region"),
  city: varchar("city"),
  // User context for logged-in users
  userName: varchar("user_name"), // Display name for easier identification
  isLoggedIn: boolean("is_logged_in").default(false), // Quick filter for authenticated sessions
  // True when this session was created (or actively used) while an admin was masquerading
  // as another user. Excluded from "last activity" rollups so admin impersonation doesn't
  // make a dormant account look active.
  wasMasquerade: boolean("was_masquerade").default(false),
}, (table) => ({
  userIdIdx: index("user_sessions_user_id_idx").on(table.userId),
  sessionTokenIdx: index("user_sessions_session_token_idx").on(table.sessionToken),
  startedAtIdx: index("user_sessions_started_at_idx").on(table.startedAt),
  hasErrorsIdx: index("user_sessions_has_errors_idx").on(table.hasErrors),
}));

// Session Events table - tracks every interaction within a session
export const sessionEvents = pgTable("session_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: uuid("session_id").references(() => userSessions.id).notNull(),
  // Event details
  eventType: varchar("event_type").$type<SessionEventType>().notNull(),
  // Page context
  pagePath: varchar("page_path"), // Current URL path
  pageTitle: varchar("page_title"), // Document title
  // Element context (for clicks)
  elementTag: varchar("element_tag"), // button, a, input, etc.
  elementId: varchar("element_id"), // DOM id
  elementClass: varchar("element_class"), // CSS classes
  elementText: text("element_text"), // Button text, link text (truncated)
  elementTestId: varchar("element_test_id"), // data-testid attribute
  elementHref: text("element_href"), // For links
  // Error context
  isError: boolean("is_error").default(false),
  errorMessage: text("error_message"),
  errorStack: text("error_stack"),
  errorCode: varchar("error_code"), // HTTP status code or custom error code
  // Additional context
  metadata: jsonb("metadata"), // Extra data like form values, API payload summary, etc.
  // Timing
  timestamp: timestamp("timestamp").default(sql`now()`),
  pageLoadTime: integer("page_load_time"), // ms since page load
}, (table) => ({
  sessionIdIdx: index("session_events_session_id_idx").on(table.sessionId),
  eventTypeIdx: index("session_events_event_type_idx").on(table.eventType),
  isErrorIdx: index("session_events_is_error_idx").on(table.isError),
  timestampIdx: index("session_events_timestamp_idx").on(table.timestamp),
  pagePathIdx: index("session_events_page_path_idx").on(table.pagePath),
}));

// Insert schemas
export const insertUserSessionSchema = createInsertSchema(userSessions).omit({
  id: true,
  startedAt: true,
});

export const insertSessionEventSchema = createInsertSchema(sessionEvents).omit({
  id: true,
});

// Batch events schema for the API endpoint
export const batchSessionEventsSchema = z.object({
  sessionToken: z.string(),
  events: z.array(z.object({
    eventType: z.enum(SESSION_EVENT_TYPES),
    pagePath: z.string().optional(),
    pageTitle: z.string().optional(),
    elementTag: z.string().optional(),
    elementId: z.string().optional(),
    elementClass: z.string().optional(),
    elementText: z.string().max(500).optional(),
    elementTestId: z.string().optional(),
    elementHref: z.string().optional(),
    isError: z.boolean().optional(),
    errorMessage: z.string().optional(),
    errorStack: z.string().optional(),
    errorCode: z.string().optional(),
    metadata: z.record(z.any()).optional(),
    timestamp: z.string().optional(), // ISO string from client
    pageLoadTime: z.number().optional(),
  })),
  // Session metadata (sent with first batch, can be null after first batch)
  sessionMeta: z.object({
    userAgent: z.string().optional(),
    browserName: z.string().optional(),
    browserVersion: z.string().optional(),
    osName: z.string().optional(),
    osVersion: z.string().optional(),
    deviceType: z.string().optional(),
    screenWidth: z.number().optional(),
    screenHeight: z.number().optional(),
    referrer: z.string().optional(),
    utmSource: z.string().optional(),
    utmMedium: z.string().optional(),
    utmCampaign: z.string().optional(),
    utmContent: z.string().optional(),
    utmTerm: z.string().optional(),
  }).optional().nullable(),
});

// Types
export type UserSession = typeof userSessions.$inferSelect;
export type InsertUserSession = z.infer<typeof insertUserSessionSchema>;
export type SessionEvent = typeof sessionEvents.$inferSelect;
export type InsertSessionEvent = z.infer<typeof insertSessionEventSchema>;
export type BatchSessionEvents = z.infer<typeof batchSessionEventsSchema>;

// ============================================================================
// Session Replay System - DOM recording and playback
// ============================================================================

// Replay event types for DOM recording
export const REPLAY_EVENT_TYPES = [
  "FULL_SNAPSHOT",      // Initial or periodic full DOM snapshot
  "INCREMENTAL",        // DOM mutation (add/remove/modify nodes)
  "MOUSE_MOVE",         // Mouse position update
  "MOUSE_CLICK",        // Mouse click with position
  "SCROLL",             // Scroll position change
  "VIEWPORT_RESIZE",    // Window resize event
  "INPUT",              // Input value change (masked for sensitive fields)
  "MEDIA_INTERACTION",  // Video/audio play/pause
  "STYLE_SHEET",        // Dynamic style changes
  "FOCUS",              // Element focus change
  "SELECTION",          // Text selection
] as const;

export type ReplayEventType = (typeof REPLAY_EVENT_TYPES)[number];

// Replay Snapshots table - stores full DOM snapshots (compressed)
export const replaySnapshots = pgTable("replay_snapshots", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: uuid("session_id").references(() => userSessions.id).notNull(),
  // Snapshot data
  snapshotData: text("snapshot_data").notNull(), // Compressed/serialized DOM tree
  capturedStyles: text("captured_styles"), // Captured CSS stylesheets for proper rendering
  // Page context
  pagePath: varchar("page_path"),
  pageTitle: varchar("page_title"),
  // Viewport info at snapshot time
  viewportWidth: integer("viewport_width"),
  viewportHeight: integer("viewport_height"),
  scrollX: integer("scroll_x"),
  scrollY: integer("scroll_y"),
  // Timing
  timestamp: timestamp("timestamp").default(sql`now()`),
  sequenceNumber: integer("sequence_number").notNull(), // Order within session
  // Size tracking for storage management
  compressedSize: integer("compressed_size"), // bytes
}, (table) => ({
  sessionIdIdx: index("replay_snapshots_session_id_idx").on(table.sessionId),
  timestampIdx: index("replay_snapshots_timestamp_idx").on(table.timestamp),
  sessionSeqIdx: index("replay_snapshots_session_seq_idx").on(table.sessionId, table.sequenceNumber),
}));

// Replay Events table - stores incremental changes between snapshots
export const replayEvents = pgTable("replay_events", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: uuid("session_id").references(() => userSessions.id).notNull(),
  snapshotId: uuid("snapshot_id").references(() => replaySnapshots.id), // Reference to base snapshot
  // Event details
  eventType: varchar("event_type").$type<ReplayEventType>().notNull(),
  // Event data (varies by type)
  eventData: jsonb("event_data").notNull(), // Contains mutations, positions, etc.
  // Mouse position (for mouse events)
  mouseX: integer("mouse_x"),
  mouseY: integer("mouse_y"),
  // Target element info (for clicks, inputs, focus)
  targetNodeId: integer("target_node_id"), // Internal node ID for replay
  targetSelector: varchar("target_selector"), // CSS selector for debugging
  // Timing
  timestamp: timestamp("timestamp").default(sql`now()`),
  timeOffset: integer("time_offset").notNull(), // ms since snapshot
  // Batching
  batchId: varchar("batch_id"), // Groups events sent together
}, (table) => ({
  sessionIdIdx: index("replay_events_session_id_idx").on(table.sessionId),
  snapshotIdIdx: index("replay_events_snapshot_id_idx").on(table.snapshotId),
  timestampIdx: index("replay_events_timestamp_idx").on(table.timestamp),
  sessionTimeIdx: index("replay_events_session_time_idx").on(table.sessionId, table.timeOffset),
}));

// Insert schemas for replay
export const insertReplaySnapshotSchema = createInsertSchema(replaySnapshots).omit({
  id: true,
  timestamp: true,
});

export const insertReplayEventSchema = createInsertSchema(replayEvents).omit({
  id: true,
  timestamp: true,
});

// Batch replay events schema for efficient upload
export const batchReplayEventsSchema = z.object({
  sessionToken: z.string(),
  batchId: z.string(),
  // Optional full snapshot (sent at start or periodically)
  snapshot: z.object({
    snapshotData: z.string(),
    capturedStyles: z.string().optional(),
    pagePath: z.string().optional(),
    pageTitle: z.string().optional(),
    viewportWidth: z.number().optional(),
    viewportHeight: z.number().optional(),
    scrollX: z.number().optional(),
    scrollY: z.number().optional(),
    sequenceNumber: z.number(),
    compressedSize: z.number().optional(),
  }).optional(),
  // Incremental events
  events: z.array(z.object({
    eventType: z.enum(REPLAY_EVENT_TYPES),
    eventData: z.record(z.any()),
    mouseX: z.number().optional(),
    mouseY: z.number().optional(),
    targetNodeId: z.number().optional(),
    targetSelector: z.string().optional(),
    timeOffset: z.number(),
    timestamp: z.string().optional(), // ISO string
  })),
});

// Types
export type ReplaySnapshot = typeof replaySnapshots.$inferSelect;
export type InsertReplaySnapshot = z.infer<typeof insertReplaySnapshotSchema>;
export type ReplayEvent = typeof replayEvents.$inferSelect;
export type InsertReplayEvent = z.infer<typeof insertReplayEventSchema>;
export type BatchReplayEvents = z.infer<typeof batchReplayEventsSchema>;

// ============================================================================
// Click Screenshots - Simple screenshot-based session replay
// ============================================================================

export const clickScreenshots = pgTable("click_screenshots", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: uuid("session_id").references(() => userSessions.id).notNull(),
  imageData: text("image_data").notNull(), // Base64 JPEG screenshot
  clickX: integer("click_x").notNull(),
  clickY: integer("click_y").notNull(),
  pagePath: varchar("page_path"),
  pageTitle: varchar("page_title"),
  viewportWidth: integer("viewport_width"),
  viewportHeight: integer("viewport_height"),
  targetSelector: varchar("target_selector"),
  timeOffset: integer("time_offset").notNull(), // ms since session start
  timestamp: timestamp("timestamp").default(sql`now()`),
}, (table) => ({
  sessionIdIdx: index("click_screenshots_session_id_idx").on(table.sessionId),
  timestampIdx: index("click_screenshots_timestamp_idx").on(table.timestamp),
}));

export const insertClickScreenshotSchema = createInsertSchema(clickScreenshots).omit({
  id: true,
  timestamp: true,
});

export const clickScreenshotUploadSchema = z.object({
  sessionToken: z.string(),
  screenshot: z.object({
    imageData: z.string(),
    clickX: z.number(),
    clickY: z.number(),
    pagePath: z.string().optional(),
    pageTitle: z.string().optional(),
    viewportWidth: z.number().optional(),
    viewportHeight: z.number().optional(),
    targetSelector: z.string().optional(),
    timestamp: z.string().optional(),
    timeOffset: z.number(),
  }),
});

export type ClickScreenshot = typeof clickScreenshots.$inferSelect;
export type InsertClickScreenshot = z.infer<typeof insertClickScreenshotSchema>;

// =====================================================
// OPERATIONAL HEALTH ISSUES
// =====================================================

// Issue Types - categories of operational problems
export const HEALTH_ISSUE_TYPES = [
  "INVENTORY_NO_LOCATION",        // Inventory item without warehouse location
  "INVENTORY_NO_DIMENSIONS",      // Product missing dimensions (can't calculate freight)
  "INVENTORY_NO_IMAGES",          // Product without images
  "INVENTORY_MISSING_DETAILS",    // Product missing critical details (brand, category, etc.)
  "INVENTORY_ZERO_PRICE",         // Product with $0 or missing price
  "INVENTORY_NO_MODEL",           // Inventory without associated product/model (orphaned)
  "BGRADE_NO_VIDEO",              // Serialized unit without video URL
  "DUPLICATE_BGRADE_UNITS",       // Multiple Serialized units with same serial number
  "OVERSOLD_ITEM",                // More units sold than in stock
  "ORDER_STUCK",                  // Order stuck in processing state too long
  "ORDER_FULFILLMENT_SHORTFALL",  // Order item cannot be fulfilled (B-grade no location or A-grade oversold)
  "MANIFEST_UNRECONCILED",        // Manifest with unreconciled items
  "RECEIVING_LINKAGE_FAILED",     // Expected item not linked to serialized_unit during receiving
  "RECEIVING_ORPHANED_ITEM",      // Expected item exists but no serialized_unit was created
  "EMAIL_FAILED",                 // Failed email notification
  "SHOPIFY_SYNC_FAILED",          // Shopify sync failure
  "APPLICATION_ERROR",            // Application code error (links to application_errors)
  "ALLOCATION_ORPHAN",            // Unit allocated to order item that doesn't exist or is from cancelled/shipped order
  "ALLOCATION_MISMATCH",          // Order item references unit that isn't allocated to it
  "INBOUND_RETURN_OVERDUE",       // Inbound return past its expected arrival date and not yet received
  "SESSION_NO_PAGEVIEW",          // Session exists but has no PAGE_VIEW events (tracking issue)
  "MV_REFRESH_FAILED",            // Materialized view refresh failure (analytics may be stale)
] as const;
export type HealthIssueType = typeof HEALTH_ISSUE_TYPES[number];

// Issue Severity
export const HEALTH_ISSUE_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"] as const;
export type HealthIssueSeverity = typeof HEALTH_ISSUE_SEVERITIES[number];

// Resource Types - what kind of entity is affected
export const HEALTH_RESOURCE_TYPES = [
  "INVENTORY",
  "MODEL",
  "BGRADE_UNIT",
  "ORDER",
  "ORDER_ITEM",
  "MANIFEST",
  "EXPECTED_ITEM",
  "EMAIL",
  "SHOPIFY_CONNECTION",
  "APPLICATION_ERROR",
  "SESSION",
  "MATERIALIZED_VIEW",
] as const;
export type HealthResourceType = typeof HEALTH_RESOURCE_TYPES[number];

// Operational Health Issues table
export const operationalHealthIssues = pgTable("operational_health_issues", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  // Issue classification
  issueType: varchar("issue_type").$type<HealthIssueType>().notNull(),
  severity: varchar("severity").$type<HealthIssueSeverity>().notNull().default("MEDIUM"),
  // Affected resource
  resourceType: varchar("resource_type").$type<HealthResourceType>().notNull(),
  resourceId: varchar("resource_id").notNull(), // ID of the affected entity
  resourceName: varchar("resource_name"), // Human-readable name (e.g., model number, order ID)
  // Issue details
  title: text("title").notNull(), // Brief description
  description: text("description"), // Detailed explanation
  metadata: jsonb("metadata"), // Additional context (missing fields, counts, etc.)
  fixUrl: varchar("fix_url"), // URL to fix the issue (e.g., /admin?tab=inventory&id=123)
  // Detection tracking
  fingerprint: varchar("fingerprint", { length: 64 }).notNull(), // Unique identifier to prevent duplicates
  detectedAt: timestamp("detected_at").default(sql`now()`),
  lastCheckedAt: timestamp("last_checked_at").default(sql`now()`),
  occurrenceCount: integer("occurrence_count").default(1), // How many times this was detected
  // Resolution status
  isResolved: boolean("is_resolved").default(false),
  resolvedAt: timestamp("resolved_at"),
  resolvedBy: varchar("resolved_by").references(() => users.id),
  resolvedNotes: text("resolved_notes"),
  autoResolved: boolean("auto_resolved").default(false), // True if system detected it was fixed
}, (table) => ({
  issueTypeIdx: index("health_issues_type_idx").on(table.issueType),
  severityIdx: index("health_issues_severity_idx").on(table.severity),
  resourceIdx: index("health_issues_resource_idx").on(table.resourceType, table.resourceId),
  isResolvedIdx: index("health_issues_resolved_idx").on(table.isResolved),
  detectedAtIdx: index("health_issues_detected_at_idx").on(table.detectedAt),
  fingerprintIdx: index("health_issues_fingerprint_idx").on(table.fingerprint),
}));

// Insert schema
export const insertOperationalHealthIssueSchema = createInsertSchema(operationalHealthIssues).omit({
  id: true,
  detectedAt: true,
  lastCheckedAt: true,
});

// Types
export type OperationalHealthIssue = typeof operationalHealthIssues.$inferSelect;
export type InsertOperationalHealthIssue = z.infer<typeof insertOperationalHealthIssueSchema>;

// ============================================
// BACKGROUND JOB QUEUE SYSTEM
// ============================================

// Job Types - extensible list of background task types
export const BACKGROUND_JOB_TYPES = [
  "EMAIL_INVITATION",         // Initial invitation email
  "EMAIL_NOTIFICATION",       // Bulk notification email (e.g. new categories announcement)
  "EMAIL_FOLLOWUP",           // Follow-up campaign email
  "EMAIL_NEWSLETTER",         // Marketing newsletter send
  "VIDEO_COMPRESSION",        // Video processing/compression task
  "VIDEO_IMPORT",             // Import video from external URL
  "SHOPIFY_SYNC",             // Sync inventory to Shopify
  "DATA_EXPORT",              // Generate and email data export
  "PRODUCT_AI_ENRICH",        // AI enrichment pass for a newly-created model
] as const;
export type BackgroundJobType = typeof BACKGROUND_JOB_TYPES[number];

// Job Statuses
export const BACKGROUND_JOB_STATUSES = [
  "PENDING",      // Waiting to be processed
  "SCHEDULED",    // Scheduled for future execution
  "RUNNING",      // Currently being processed
  "COMPLETED",    // Successfully completed
  "FAILED",       // Failed after all retries
  "CANCELLED",    // Manually cancelled
] as const;
export type BackgroundJobStatus = typeof BACKGROUND_JOB_STATUSES[number];

// Rate Limit Groups - jobs in same group share rate limits
export const RATE_LIMIT_GROUPS = [
  "EMAIL",           // All email sends share rate limit (1/minute)
  "VIDEO",           // Video processing tasks
  "EXTERNAL_API",    // External API calls (Shopify, etc.)
  "SCRAPER",         // Web scraping tasks (rate limited to avoid detection)
  "NONE",            // No rate limiting
] as const;
export type RateLimitGroup = typeof RATE_LIMIT_GROUPS[number];

// Background Jobs table - The core job queue
export const backgroundJobs = pgTable("background_jobs", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  // Job classification
  jobType: varchar("job_type").$type<BackgroundJobType>().notNull(),
  rateLimitGroup: varchar("rate_limit_group").$type<RateLimitGroup>().notNull().default("NONE"),
  // Job data
  payload: jsonb("payload").notNull(), // Job-specific data (email recipient, video URL, etc.)
  result: jsonb("result"), // Result data on completion
  // Scheduling
  status: varchar("status").$type<BackgroundJobStatus>().notNull().default("PENDING"),
  priority: integer("priority").default(0), // Higher = more urgent (0 = normal)
  scheduledFor: timestamp("scheduled_for").default(sql`now()`), // When to process (allows future scheduling)
  // Deduplication
  dedupeKey: varchar("dedupe_key", { length: 255 }), // Unique key to prevent duplicate jobs
  // Execution tracking
  attempts: integer("attempts").default(0),
  maxAttempts: integer("max_attempts").default(3),
  lastError: text("last_error"), // Most recent error message
  // Locking for concurrent workers
  lockedAt: timestamp("locked_at"), // When a worker claimed this job
  lockedBy: varchar("locked_by", { length: 100 }), // Worker ID that claimed this job
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
  startedAt: timestamp("started_at"), // When processing began
  completedAt: timestamp("completed_at"), // When processing finished
  // Reference back to creating entity (for tracking)
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  relatedEntityType: varchar("related_entity_type", { length: 50 }), // e.g., "invitation", "bgrade_unit"
  relatedEntityId: varchar("related_entity_id", { length: 100 }), // ID of related entity
}, (table) => ({
  statusIdx: index("background_jobs_status_idx").on(table.status),
  jobTypeIdx: index("background_jobs_type_idx").on(table.jobType),
  scheduledIdx: index("background_jobs_scheduled_idx").on(table.scheduledFor),
  dedupeIdx: index("background_jobs_dedupe_idx").on(table.dedupeKey),
  rateLimitGroupIdx: index("background_jobs_rate_limit_idx").on(table.rateLimitGroup),
  lockedAtIdx: index("background_jobs_locked_idx").on(table.lockedAt),
  relatedEntityIdx: index("background_jobs_related_entity_idx").on(table.relatedEntityType, table.relatedEntityId),
}));

// Insert schema
export const insertBackgroundJobSchema = createInsertSchema(backgroundJobs).omit({
  id: true,
  result: true,
  attempts: true,
  lastError: true,
  lockedAt: true,
  lockedBy: true,
  createdAt: true,
  startedAt: true,
  completedAt: true,
});

// Types
export type BackgroundJob = typeof backgroundJobs.$inferSelect;
export type InsertBackgroundJob = z.infer<typeof insertBackgroundJobSchema>;

// ============================================
// EMAIL CONTACTS - Unified email subscriber management
// ============================================

// Email Contacts table - Aggregates all email addresses we've contacted
export const emailContacts = pgTable("email_contacts", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").notNull().unique(), // Normalized lowercase email
  // Name info (from various sources)
  name: text("name"), // Best known name
  company: text("company"), // Company name if known
  // Invitation settings (separate from newsletter)
  doNotInvite: boolean("do_not_invite").default(false), // Block from future invitations
  doNotInviteReason: text("do_not_invite_reason"), // Why they were blocked
  doNotInviteAt: timestamp("do_not_invite_at"), // When they were blocked
  doNotInviteBy: varchar("do_not_invite_by").references(() => users.id), // Who blocked them
  // Newsletter subscription (synced from newsletterSubscribers)
  newsletterSubscribed: boolean("newsletter_subscribed").default(false),
  newsletterSubscribedAt: timestamp("newsletter_subscribed_at"),
  newsletterUnsubscribedAt: timestamp("newsletter_unsubscribed_at"),
  // User account link (if they signed up)
  userId: varchar("user_id").references(() => users.id),
  // Engagement stats (aggregated)
  totalEmailsSent: integer("total_emails_sent").default(0),
  totalOpens: integer("total_opens").default(0),
  totalClicks: integer("total_clicks").default(0),
  lastEmailSentAt: timestamp("last_email_sent_at"),
  lastOpenAt: timestamp("last_open_at"),
  lastClickAt: timestamp("last_click_at"),
  // First/last invitation tracking
  firstInvitationId: uuid("first_invitation_id").references(() => emailInvitations.id),
  lastInvitationId: uuid("last_invitation_id").references(() => emailInvitations.id),
  // Source tracking
  source: varchar("source", { length: 50 }), // Where we first got this email
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  emailIdx: index("email_contacts_email_idx").on(table.email),
  doNotInviteIdx: index("email_contacts_do_not_invite_idx").on(table.doNotInvite),
  userIdIdx: index("email_contacts_user_id_idx").on(table.userId),
  lastEmailSentIdx: index("email_contacts_last_email_idx").on(table.lastEmailSentAt),
}));

// Insert schema
export const insertEmailContactSchema = createInsertSchema(emailContacts).omit({
  id: true,
  totalEmailsSent: true,
  totalOpens: true,
  totalClicks: true,
  createdAt: true,
  updatedAt: true,
});

// Types
export type EmailContact = typeof emailContacts.$inferSelect;
export type InsertEmailContact = z.infer<typeof insertEmailContactSchema>;

// ============================================
// STAGED IMPORT PIPELINE - Preview imports before committing
// ============================================

// Import session status
export const IMPORT_SESSION_STATUSES = ["pending", "approved", "rejected", "expired"] as const;
export type ImportSessionStatus = typeof IMPORT_SESSION_STATUSES[number];

// Staged item action type
export const STAGED_ITEM_ACTIONS = ["CREATE_MODEL", "UPDATE_MODEL", "CREATE_INVENTORY", "UPDATE_INVENTORY", "SUPPLIER_SALE", "NO_CHANGE"] as const;
export type StagedItemAction = typeof STAGED_ITEM_ACTIONS[number];

// Import Sessions - tracks each uploaded file that needs review
export const importSessions = pgTable("import_sessions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  fileName: text("file_name").notNull(),
  fileSize: integer("file_size"), // Size in bytes
  fileHash: text("file_hash"), // SHA-256 hash for deduplication
  uploadedByUserId: varchar("uploaded_by_user_id").references(() => users.id).notNull(),
  supplierProgramId: uuid("supplier_program_id").references(() => supplierPrograms.id),
  addressId: uuid("address_id").references(() => supplierAddresses.id),
  importMode: text("import_mode").$type<ImportMode>().default("add"),
  status: text("status").$type<ImportSessionStatus>().notNull().default("pending"),
  // Summary stats (computed after parsing)
  totalItems: integer("total_items").default(0),
  newModels: integer("new_models").default(0),
  priceUpdates: integer("price_updates").default(0),
  quantityChanges: integer("quantity_changes").default(0),
  supplierSales: integer("supplier_sales").default(0),
  errors: integer("errors").default(0),
  parserErrorLog: jsonb("parser_error_log"), // Array of parser-level error strings for post-mortem analysis
  // Review metadata
  reviewedByUserId: varchar("reviewed_by_user_id").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  reviewNotes: text("review_notes"),
  // Timing
  expiresAt: timestamp("expires_at").notNull(), // Auto-expire after 24h if not reviewed
  createdAt: timestamp("created_at").default(sql`now()`),
  committedAt: timestamp("committed_at"), // When changes were applied
}, (table) => ({
  statusIdx: index("import_sessions_status_idx").on(table.status),
  createdAtIdx: index("import_sessions_created_at_idx").on(table.createdAt),
  uploadedByIdx: index("import_sessions_uploaded_by_idx").on(table.uploadedByUserId),
}));

// Staged Import Items - individual changes that will be applied
export const stagedImportItems = pgTable("staged_import_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  sessionId: uuid("session_id").references(() => importSessions.id, { onDelete: "cascade" }).notNull(),
  rowNumber: integer("row_number"), // Original row in Excel for error reference
  action: text("action").$type<StagedItemAction>().notNull(),
  // Model identification
  brand: text("brand"),
  mfgSku: text("mfg_sku"),
  categorySlug: text("category_slug"),
  // Current values (for updates/comparison)
  currentPrice: decimal("current_price", { precision: 10, scale: 2 }),
  currentQuantity: integer("current_quantity"),
  currentGrade: text("current_grade"),
  // New values
  newPrice: decimal("new_price", { precision: 10, scale: 2 }),
  newQuantity: integer("new_quantity"),
  newGrade: text("new_grade"),
  // Additional model data (for CREATE_MODEL)
  modelData: jsonb("model_data"), // Full model insert data as JSON
  // Status
  isError: boolean("is_error").default(false),
  errorMessage: text("error_message"),
  // Tracking
  appliedAt: timestamp("applied_at"), // When this specific item was committed
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  sessionIdIdx: index("staged_import_items_session_id_idx").on(table.sessionId),
  actionIdx: index("staged_import_items_action_idx").on(table.action),
  brandSkuIdx: index("staged_import_items_brand_sku_idx").on(table.brand, table.mfgSku),
}));

// Insert schemas
export const insertImportSessionSchema = createInsertSchema(importSessions).omit({
  id: true,
  reviewedByUserId: true,
  reviewedAt: true,
  reviewNotes: true,
  createdAt: true,
  committedAt: true,
});

export const insertStagedImportItemSchema = createInsertSchema(stagedImportItems).omit({
  id: true,
  appliedAt: true,
  createdAt: true,
});

// Types
export type ImportSession = typeof importSessions.$inferSelect;
export type InsertImportSession = z.infer<typeof insertImportSessionSchema>;
export type StagedImportItem = typeof stagedImportItems.$inferSelect;
export type InsertStagedImportItem = z.infer<typeof insertStagedImportItemSchema>;


// ============================================================
// CUSTOMER SELF-SERVICE PORTAL
// ============================================================

// Claim/Return Issue Types
export const CLAIM_ISSUE_TYPES = [
  "DAMAGED_IN_TRANSIT",
  "WRONG_ITEM",
  "MISSING_PARTS",
  "DEFECTIVE",
  "NOT_AS_DESCRIBED",
  "OTHER"
] as const;
export type ClaimIssueType = typeof CLAIM_ISSUE_TYPES[number];

// Claim Statuses
export const CLAIM_STATUSES = [
  "SUBMITTED",
  "UNDER_REVIEW",
  "APPROVED",
  "DENIED",
  "RESOLVED",
  "CANCELLED"
] as const;
export type ClaimStatus = typeof CLAIM_STATUSES[number];

// Customer Claims - for returns, refunds, and issue reports
export const customerClaims = pgTable("customer_claims", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimNumber: varchar("claim_number", { length: 20 }).unique().notNull(), // CL-10001, CL-10002, etc.
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  orderItemId: uuid("order_item_id").references(() => orderItems.id), // Optional: specific item in the order
  userId: varchar("user_id").references(() => users.id).notNull(),
  issueType: varchar("issue_type").$type<ClaimIssueType>().notNull(),
  status: varchar("status").$type<ClaimStatus>().notNull().default("SUBMITTED"),
  description: text("description").notNull(), // Customer's description of the issue
  resolutionNotes: text("resolution_notes"), // Admin notes on resolution
  resolutionType: varchar("resolution_type"), // "REFUND", "REPLACEMENT", "CREDIT", "NO_ACTION"
  refundAmount: decimal("refund_amount", { precision: 10, scale: 2 }), // Amount refunded if applicable
  // Admin workflow
  assignedToUserId: varchar("assigned_to_user_id").references(() => users.id),
  reviewedAt: timestamp("reviewed_at"),
  resolvedAt: timestamp("resolved_at"),
  // Timestamps
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  userIdIdx: index("customer_claims_user_id_idx").on(table.userId),
  orderIdIdx: index("customer_claims_order_id_idx").on(table.orderId),
  statusIdx: index("customer_claims_status_idx").on(table.status),
  claimNumberIdx: index("customer_claims_claim_number_idx").on(table.claimNumber),
}));

// Insert schema for customer claims
export const insertCustomerClaimSchema = createInsertSchema(customerClaims).omit({
  id: true,
  claimNumber: true,
  status: true,
  resolutionNotes: true,
  resolutionType: true,
  refundAmount: true,
  assignedToUserId: true,
  reviewedAt: true,
  resolvedAt: true,
  createdAt: true,
  updatedAt: true,
});

export type CustomerClaim = typeof customerClaims.$inferSelect;
export type InsertCustomerClaim = z.infer<typeof insertCustomerClaimSchema>;

// Claim Photos - photos uploaded by customers for damage documentation
export const claimPhotos = pgTable("claim_photos", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => customerClaims.id, { onDelete: 'cascade' }).notNull(),
  objectStorageKey: varchar("object_storage_key", { length: 500 }).notNull(), // Path in Object Storage
  originalFilename: varchar("original_filename", { length: 255 }),
  mimeType: varchar("mime_type", { length: 100 }),
  fileSize: integer("file_size"), // Size in bytes
  caption: text("caption"), // Optional description of what the photo shows
  uploadedAt: timestamp("uploaded_at").default(sql`now()`),
}, (table) => ({
  claimIdIdx: index("claim_photos_claim_id_idx").on(table.claimId),
}));

export const insertClaimPhotoSchema = createInsertSchema(claimPhotos).omit({
  id: true,
  uploadedAt: true,
});

export type ClaimPhoto = typeof claimPhotos.$inferSelect;
export type InsertClaimPhoto = z.infer<typeof insertClaimPhotoSchema>;

// Order Returns / Refunds - admin-initiated returns and refunds on shipped orders
export const RETURN_REASONS = [
  'CUSTOMER_SATISFACTION',
  'DAMAGED_UNIT',
  'SHIPMENT_REFUSED',
  'GOOD_UNUSED',
] as const;
export type ReturnReason = typeof RETURN_REASONS[number];

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  CUSTOMER_SATISFACTION: 'Customer Satisfaction',
  DAMAGED_UNIT: 'Damaged Unit',
  SHIPMENT_REFUSED: 'Shipment Refused',
  GOOD_UNUSED: 'Good, Unused',
};

export const orderReturns = pgTable("order_returns", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  reason: varchar("reason").$type<ReturnReason>().notNull(),
  refundAmount: decimal("refund_amount", { precision: 10, scale: 2 }).notNull(), // Total refund amount (wire + credit)
  bankAccount: varchar("bank_account", { length: 100 }).notNull(), // Which bank sent the refund (or 'STORE_CREDIT' if 100% credit)
  bankNote: text("bank_note"), // Required when bankAccount = 'OTHER'
  paymentReference: varchar("payment_reference", { length: 200 }), // Optional wire/check reference
  notes: text("notes"), // Optional internal notes
  // Split refund fields (Task 5 store credit)
  wireRefundAmount: decimal("wire_refund_amount", { precision: 10, scale: 2 }), // Portion wired back (null = not split)
  storeCreditAmount: decimal("store_credit_amount", { precision: 10, scale: 2 }), // Portion credited to wallet (null = not split)
  storeCreditLedgerId: uuid("store_credit_ledger_id"), // FK set after store_credit_ledger table
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  orderIdIdx: index("order_returns_order_id_idx").on(table.orderId),
}));

export const insertOrderReturnSchema = createInsertSchema(orderReturns).omit({
  id: true,
  createdAt: true,
});

export type OrderReturn = typeof orderReturns.$inferSelect;
export type InsertOrderReturn = z.infer<typeof insertOrderReturnSchema>;

// Return Photos - photos documenting the condition/reason for a return
export const returnPhotos = pgTable("return_photos", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  returnId: uuid("return_id").references(() => orderReturns.id, { onDelete: 'cascade' }).notNull(),
  objectStorageKey: varchar("object_storage_key", { length: 500 }).notNull(),
  originalFilename: varchar("original_filename", { length: 255 }),
  mimeType: varchar("mime_type", { length: 100 }),
  fileSize: integer("file_size"),
  uploadedAt: timestamp("uploaded_at").default(sql`now()`),
}, (table) => ({
  returnIdIdx: index("return_photos_return_id_idx").on(table.returnId),
}));

export const insertReturnPhotoSchema = createInsertSchema(returnPhotos).omit({
  id: true,
  uploadedAt: true,
});

export type ReturnPhoto = typeof returnPhotos.$inferSelect;
export type InsertReturnPhoto = z.infer<typeof insertReturnPhotoSchema>;

// ============================================================
// Inbound Returns - tracks refunded units that are physically
// coming back from the customer (Task #170). Replaces the
// "stuck allocated to delivered/refunded order" orphan state
// with an explicit in-transit lifecycle the warehouse can
// actually receive against.
// ============================================================
export const INBOUND_RETURN_STATUSES = [
  "EXPECTED",     // Refund logged; awaiting carrier pickup / tracking
  "IN_TRANSIT",   // Carrier has it / on its way back to the warehouse
  "RECEIVED",     // Physically received at warehouse and re-graded into stock
  "SCRAPPED",     // Physically received but unsellable; written off
  "SALVAGED",     // Physically received, parted-out for salvage (Task #187)
  "CANCELLED",    // Customer kept the unit / return aborted
] as const;
export type InboundReturnStatus = typeof INBOUND_RETURN_STATUSES[number];

export const INBOUND_RETURN_STATUS_LABELS: Record<InboundReturnStatus, string> = {
  EXPECTED: "Awaiting Pickup",
  IN_TRANSIT: "In Transit",
  RECEIVED: "Received",
  SCRAPPED: "Scrapped",
  SALVAGED: "Salvaged",
  CANCELLED: "Cancelled",
};

export const inboundReturns = pgTable("inbound_returns", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  orderItemId: uuid("order_item_id"), // No FK — order item may be edited later
  unitId: uuid("unit_id").references(() => serializedUnits.id).notNull(),
  returnId: uuid("return_id").references(() => orderReturns.id), // Optional link to refund row
  status: varchar("status").$type<InboundReturnStatus>().notNull().default("EXPECTED"),
  carrier: varchar("carrier", { length: 100 }), // e.g. "UPS", "FedEx", "OB Truck"
  trackingNumber: varchar("tracking_number", { length: 200 }),
  trackingUrl: text("tracking_url"),
  expectedArrivalAt: timestamp("expected_arrival_at"),
  receivedAt: timestamp("received_at"),
  receivedByUserId: varchar("received_by_user_id").references(() => users.id),
  receivedWarehouseLocationId: uuid("received_warehouse_location_id").references(() => warehouseLocations.id),
  dispositionGrade: text("disposition_grade"), // Grade assigned on receive: A / B+ / B / B-
  scrappedReason: text("scrapped_reason"),
  notes: text("notes"),
  createdByUserId: varchar("created_by_user_id").references(() => users.id),
  createdAt: timestamp("created_at").default(sql`now()`).notNull(),
  updatedAt: timestamp("updated_at").default(sql`now()`).notNull(),
}, (table) => ({
  orderIdIdx: index("inbound_returns_order_id_idx").on(table.orderId),
  unitIdIdx: index("inbound_returns_unit_id_idx").on(table.unitId),
  statusIdx: index("inbound_returns_status_idx").on(table.status),
}));

export const insertInboundReturnSchema = createInsertSchema(inboundReturns).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InboundReturn = typeof inboundReturns.$inferSelect;
export type InsertInboundReturn = z.infer<typeof insertInboundReturnSchema>;

// ============================================================
// Order Line Adjustments — Task #190
// Per-line damage/misgrade adjustments on shipped orders.
// Original priceAtPurchase is NEVER mutated; adjustments are
// additive negative entries that flow through totals/invoice
// and route to invoice (unpaid) or store credit (paid).
// ============================================================
export const ORDER_ADJUSTMENT_TYPES = [
  "GRADE_DOWNGRADE",  // Re-grade unit(s) to a lower tier; credit = price delta × qty
  "CUSTOM_CREDIT",    // Free-form admin-entered credit (damaged box, missing accessory, etc.)
] as const;
export type OrderAdjustmentType = typeof ORDER_ADJUSTMENT_TYPES[number];

export const ORDER_ADJUSTMENT_DESTINATIONS = [
  "INVOICE",        // Net against unpaid invoice (reduces amount owed)
  "STORE_CREDIT",   // Issued as REFUND_CREDIT to the customer wallet (paid orders)
] as const;
export type OrderAdjustmentDestination = typeof ORDER_ADJUSTMENT_DESTINATIONS[number];

export const ORDER_ADJUSTMENT_STATUSES = [
  "ACTIVE",     // In effect
  "REVERSED",   // Reversed via wire-back conversion (store credit refunded, then wire issued)
  "VOIDED",     // Admin voided (mistake)
] as const;
export type OrderAdjustmentStatus = typeof ORDER_ADJUSTMENT_STATUSES[number];

// Supplier chargeback lifecycle (per Task #190 spec)
export const SUPPLIER_CHARGEBACK_STATUSES = [
  "PENDING",     // Flagged for chargeback; not yet submitted to supplier
  "SUBMITTED",   // Submitted to supplier (reference recorded)
  "RECEIVED",    // Supplier paid us back (amount + date recorded)
  "WAIVED",      // Decided not to pursue (reason recorded)
] as const;
export type SupplierChargebackStatus = typeof SUPPLIER_CHARGEBACK_STATUSES[number];

export const orderAdjustments = pgTable("order_adjustments", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: uuid("order_id").references(() => orders.id).notNull(),
  orderItemId: uuid("order_item_id").references(() => orderItems.id).notNull(),
  adjustmentType: text("adjustment_type").$type<OrderAdjustmentType>().notNull(),
  // For GRADE_DOWNGRADE: the new grade the unit(s) are being re-graded to
  newGrade: text("new_grade"),
  // Original line grade snapshot at time of adjustment (audit)
  originalGrade: text("original_grade"),
  // Per-unit price delta (positive number = amount credited per unit)
  perUnitDelta: decimal("per_unit_delta", { precision: 10, scale: 2 }).notNull().default("0.00"),
  // Affected unit count (1..line.quantity)
  unitsAffected: integer("units_affected").notNull(),
  // Total credit amount = perUnitDelta * unitsAffected (always positive; subtracted from order totals)
  totalCreditAmount: decimal("total_credit_amount", { precision: 10, scale: 2 }).notNull(),
  destination: text("destination").$type<OrderAdjustmentDestination>().notNull(),
  status: text("status").$type<OrderAdjustmentStatus>().notNull().default("ACTIVE"),
  // Standard return reason (RETURN_REASONS enum). Free-form admin commentary goes in `notes`.
  reason: varchar("reason").$type<ReturnReason>().notNull().default("DAMAGED_UNIT"),
  notes: text("notes"),
  // Linked store credit ledger entry id (CREDIT side, when destination=STORE_CREDIT)
  storeCreditLedgerId: uuid("store_credit_ledger_id"),
  // Linked reversal ledger entry id (DEBIT side, when status=REVERSED via wire-back)
  reversalLedgerId: uuid("reversal_ledger_id"),
  // Linked order_returns row (created for paid orders so it surfaces in returns reporting)
  linkedOrderReturnId: uuid("linked_order_return_id"),
  // Payout method — STORE_CREDIT by default; flipped to WIRE when admin
  // converts the credit to a wire-back. The adjustment itself stays ACTIVE
  // so the order economics are preserved; only the payout vehicle changes.
  payoutMethod: varchar("payout_method", { length: 20 }).notNull().default("STORE_CREDIT"),
  wireConvertedAt: timestamp("wire_converted_at"),
  wireConfirmationNumber: varchar("wire_confirmation_number", { length: 200 }),
  wireBankAccount: varchar("wire_bank_account", { length: 100 }),
  wireReturnId: uuid("wire_return_id"), // order_returns row for the wire payout
  // Supplier chargeback bookkeeping
  flagForChargeback: boolean("flag_for_chargeback").notNull().default(false),
  chargebackStatus: varchar("chargeback_status").$type<SupplierChargebackStatus>(),
  chargebackReference: varchar("chargeback_reference", { length: 200 }),
  chargebackAmount: decimal("chargeback_amount", { precision: 10, scale: 2 }),
  chargebackSubmittedAt: timestamp("chargeback_submitted_at"),
  chargebackReceivedAt: timestamp("chargeback_received_at"),
  chargebackResolvedAt: timestamp("chargeback_resolved_at"),
  chargebackResolvedByUserId: varchar("chargeback_resolved_by_user_id").references(() => users.id),
  chargebackResolutionNotes: text("chargeback_resolution_notes"),
  // Audit
  createdByUserId: varchar("created_by_user_id").references(() => users.id).notNull(),
  createdAt: timestamp("created_at").default(sql`now()`).notNull(),
  updatedAt: timestamp("updated_at").default(sql`now()`).notNull(),
}, (table) => ({
  orderIdIdx: index("order_adjustments_order_id_idx").on(table.orderId),
  orderItemIdIdx: index("order_adjustments_order_item_id_idx").on(table.orderItemId),
  statusIdx: index("order_adjustments_status_idx").on(table.status),
  chargebackIdx: index("order_adjustments_chargeback_idx").on(table.flagForChargeback, table.chargebackResolvedAt),
}));

// Many-to-many: which specific serialized units were re-graded by an adjustment
export const orderAdjustmentUnits = pgTable("order_adjustment_units", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  adjustmentId: uuid("adjustment_id").references(() => orderAdjustments.id, { onDelete: "cascade" }).notNull(),
  unitId: uuid("unit_id").references(() => serializedUnits.id).notNull(),
  // Snapshot of unit grade BEFORE the downgrade (so reversal can restore)
  previousGrade: text("previous_grade"),
}, (table) => ({
  adjustmentIdx: index("order_adjustment_units_adjustment_idx").on(table.adjustmentId),
  unitIdx: index("order_adjustment_units_unit_idx").on(table.unitId),
}));

// Photo attachments for adjustments (damaged box photos, etc.)
export const orderAdjustmentPhotos = pgTable("order_adjustment_photos", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  adjustmentId: uuid("adjustment_id").references(() => orderAdjustments.id, { onDelete: "cascade" }).notNull(),
  photoUrl: text("photo_url").notNull(),
  caption: text("caption"),
  uploadedByUserId: varchar("uploaded_by_user_id").references(() => users.id),
  uploadedAt: timestamp("uploaded_at").default(sql`now()`).notNull(),
}, (table) => ({
  adjustmentIdx: index("order_adjustment_photos_adjustment_idx").on(table.adjustmentId),
}));

export const insertOrderAdjustmentSchema = createInsertSchema(orderAdjustments).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  storeCreditLedgerId: true,
  reversalLedgerId: true,
  status: true,
  chargebackResolvedAt: true,
  chargebackResolvedByUserId: true,
  chargebackResolutionNotes: true,
});
export type OrderAdjustment = typeof orderAdjustments.$inferSelect;
export type InsertOrderAdjustment = z.infer<typeof insertOrderAdjustmentSchema>;
export type OrderAdjustmentUnit = typeof orderAdjustmentUnits.$inferSelect;
export type OrderAdjustmentPhoto = typeof orderAdjustmentPhotos.$inferSelect;

// ============================================================
// Store Credit Wallet & Ledger System
// ============================================================

export const STORE_CREDIT_TYPES = ["CREDIT", "DEBIT"] as const;
export type StoreCreditType = typeof STORE_CREDIT_TYPES[number];

export const STORE_CREDIT_SUBTYPES = [
  // Credit subtypes (money coming IN to the wallet)
  "REFUND_CREDIT",        // From an order return / refund
  "CONSIGNMENT_PAYOUT",   // Revenue share owed to a supplier/partner
  "GOODWILL_CREDIT",      // Customer satisfaction gesture
  "MANUAL_ADMIN_CREDIT",  // Arbitrary admin adjustment
  // Debit subtypes (money going OUT of the wallet)
  "ORDER_PAYMENT",        // Applied toward an order at checkout
  "MANUAL_ADMIN_DEBIT",   // Admin correction / reversal
  "EXPIRED",              // Auto-generated when a credit entry expires
  "REFUND_CREDIT_REVERSAL", // Task #190: reverses a REFUND_CREDIT after wire-back conversion
] as const;
export type StoreCreditSubtype = typeof STORE_CREDIT_SUBTYPES[number];

export const STORE_CREDIT_SUBTYPE_LABELS: Record<StoreCreditSubtype, string> = {
  REFUND_CREDIT: "Refund Credit",
  CONSIGNMENT_PAYOUT: "Consignment Payout",
  GOODWILL_CREDIT: "Goodwill Credit",
  MANUAL_ADMIN_CREDIT: "Manual Credit",
  ORDER_PAYMENT: "Order Payment",
  MANUAL_ADMIN_DEBIT: "Manual Debit",
  EXPIRED: "Expired Credit",
  REFUND_CREDIT_REVERSAL: "Refund Credit Reversal (Wire-back)",
};

// One wallet per user — caches the running balance for fast reads
export const storeCreditWallets = pgTable("store_credit_wallets", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull().unique(),
  balance: decimal("balance", { precision: 10, scale: 2 }).notNull().default("0.00"),
  lifetimeCredited: decimal("lifetime_credited", { precision: 10, scale: 2 }).notNull().default("0.00"),
  lifetimeDebited: decimal("lifetime_debited", { precision: 10, scale: 2 }).notNull().default("0.00"),
  /** Date (YYYY-MM-DD) of the last expiry warning email sent — used to prevent repeated daily notifications */
  lastExpiryWarnDate: date("last_expiry_warn_date"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  userIdIdx: index("store_credit_wallets_user_id_idx").on(table.userId),
}));

export const insertStoreCreditWalletSchema = createInsertSchema(storeCreditWallets).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type StoreCreditWallet = typeof storeCreditWallets.$inferSelect;
export type InsertStoreCreditWallet = z.infer<typeof insertStoreCreditWalletSchema>;

// Immutable ledger — every credit and debit is a permanent record
export const storeCreditLedger = pgTable("store_credit_ledger", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  type: text("type").$type<StoreCreditType>().notNull(),       // CREDIT | DEBIT
  subtype: text("subtype").$type<StoreCreditSubtype>().notNull(),
  amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),         // Always positive
  balanceAfter: decimal("balance_after", { precision: 10, scale: 2 }).notNull(), // Snapshot post-entry
  // Order / return linkage (optional)
  orderId: uuid("order_id").references(() => orders.id),       // order this credit/debit relates to
  returnId: uuid("return_id"),                                  // order_returns.id (no FK to avoid circular)
  // Expiry (for CREDIT entries only)
  expiresAt: timestamp("expires_at"),                           // null = never expires
  isExpired: boolean("is_expired").notNull().default(false),
  expirationSourceLedgerId: uuid("expiration_source_ledger_id"), // for EXPIRED debits: the credit entry that expired
  // Metadata
  note: text("note"),                                           // Admin / system note
  attachmentUrl: text("attachment_url"),                        // Supporting doc (consignment statements, etc.)
  // Audit
  createdById: varchar("created_by_id").references(() => users.id), // null for system-generated entries
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  userIdIdx: index("store_credit_ledger_user_id_idx").on(table.userId),
  orderIdIdx: index("store_credit_ledger_order_id_idx").on(table.orderId),
  isExpiredIdx: index("store_credit_ledger_is_expired_idx").on(table.isExpired),
  expiresAtIdx: index("store_credit_ledger_expires_at_idx").on(table.expiresAt),
}));

export const insertStoreCreditLedgerSchema = createInsertSchema(storeCreditLedger).omit({
  id: true,
  createdAt: true,
});
export type StoreCreditLedgerEntry = typeof storeCreditLedger.$inferSelect;
export type InsertStoreCreditLedgerEntry = z.infer<typeof insertStoreCreditLedgerSchema>;

// Customer Saved Payment Methods - secure token storage for quick checkout
export const PAYMENT_CARD_BRANDS = ["VISA", "MASTERCARD", "AMEX", "DISCOVER", "OTHER"] as const;
export type PaymentCardBrand = typeof PAYMENT_CARD_BRANDS[number];

export const customerPaymentMethods = pgTable("customer_payment_methods", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id).notNull(),
  accountId: uuid("account_id").references(() => accounts.id), // Optional: for B2B accounts
  // Stripe integration
  stripeCustomerId: varchar("stripe_customer_id", { length: 100 }), // Stripe customer ID
  stripePaymentMethodId: varchar("stripe_payment_method_id", { length: 100 }).notNull(), // Stripe payment method ID
  // Display info (safe to store - not actual card numbers)
  cardBrand: varchar("card_brand").$type<PaymentCardBrand>(),
  last4: varchar("last_4", { length: 4 }),
  expiryMonth: integer("expiry_month"),
  expiryYear: integer("expiry_year"),
  cardholderName: varchar("cardholder_name", { length: 255 }),
  // Settings
  isDefault: boolean("is_default").default(false),
  nickname: varchar("nickname", { length: 100 }), // "Personal Card", "Business Card", etc.
  // Status
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  userIdIdx: index("customer_payment_methods_user_id_idx").on(table.userId),
  accountIdIdx: index("customer_payment_methods_account_id_idx").on(table.accountId),
  stripeCustomerIdx: index("customer_payment_methods_stripe_customer_idx").on(table.stripeCustomerId),
}));

export const insertCustomerPaymentMethodSchema = createInsertSchema(customerPaymentMethods).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type CustomerPaymentMethod = typeof customerPaymentMethods.$inferSelect;
export type InsertCustomerPaymentMethod = z.infer<typeof insertCustomerPaymentMethodSchema>;

// =============================================================================
// Virtual Pallet Definitions
// A virtual pallet is a curated multi-SKU bundle (e.g., 8x model A + 8x model B
// + 4x model C) sold as a single product. Each pallet definition has a synthetic
// "pallet model" entry in `models` (with isPallet=true) that owns the
// inventory + listing. The composition lives here.
// =============================================================================
export const palletDefinitions = pgTable("pallet_definitions", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  palletNumber: integer("pallet_number").notNull().unique(), // From the source spreadsheet (1, 2, 3, ...)
  name: text("name").notNull(), // Display name e.g. "AC Virtual Pallet #1"
  modelId: uuid("model_id").references(() => models.id).notNull(), // Synthetic pallet model row (the SKU customers buy)
  categoryId: uuid("category_id").references(() => categories.id), // Primary category (mixed if components span)
  programType: text("program_type").notNull().default("AC"), // "AC", "MICROWAVE", etc. for routing parser logic later
  grade: text("grade").notNull().default("B"), // Pallet-level grade
  totalUnits: integer("total_units").notNull(), // Sum of line item quantities (e.g., 20)
  totalPriceCents: integer("total_price_cents").notNull(), // Marked-up pallet price in cents
  rawTotalCents: integer("raw_total_cents"), // Pre-markup total in cents (audit/compare)
  heroImageUrl: text("hero_image_url"), // Composite image rendered from constituents
  sourceFile: text("source_file"), // Original upload filename
  sourceRowStart: integer("source_row_start"), // Row in source spreadsheet (for traceability)
  status: text("status").notNull().default("active"), // "active", "archived"
  notes: text("notes"),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  modelIdIdx: index("pallet_definitions_model_id_idx").on(table.modelId),
  programTypeIdx: index("pallet_definitions_program_type_idx").on(table.programType),
}));

export const palletDefinitionItems = pgTable("pallet_definition_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  palletDefinitionId: uuid("pallet_definition_id").references(() => palletDefinitions.id, { onDelete: "cascade" }).notNull(),
  modelId: uuid("model_id").references(() => models.id).notNull(), // The constituent AC/microwave model
  quantity: integer("quantity").notNull(), // How many of this model are in the pallet
  unitPriceCents: integer("unit_price_cents").notNull(), // Marked-up unit price
  rawUnitPriceCents: integer("raw_unit_price_cents"), // Pre-markup price (audit)
  lineTotalCents: integer("line_total_cents").notNull(), // unitPriceCents * quantity
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  palletDefIdx: index("pallet_definition_items_def_id_idx").on(table.palletDefinitionId),
  modelIdIdx: index("pallet_definition_items_model_id_idx").on(table.modelId),
}));

export const insertPalletDefinitionSchema = createInsertSchema(palletDefinitions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export const insertPalletDefinitionItemSchema = createInsertSchema(palletDefinitionItems).omit({
  id: true,
  createdAt: true,
});

export type PalletDefinition = typeof palletDefinitions.$inferSelect;
export type InsertPalletDefinition = z.infer<typeof insertPalletDefinitionSchema>;
export type PalletDefinitionItem = typeof palletDefinitionItems.$inferSelect;
export type InsertPalletDefinitionItem = z.infer<typeof insertPalletDefinitionItemSchema>;

// =============================================================================
// SharkNinja Pallet System (Task #133)
// One sellable pallet model per unique combo signature; multiple physical
// pallet_units behind it. Each pallet_unit carries an immutable list of
// FS# serials (pallet_unit_items) that are surfaced on admin order detail,
// invoice PDF, and customer order confirmation but NOT on cart/PDP.
// =============================================================================
export const sharkninjaConstituentPricing = pgTable("sharkninja_constituent_pricing", {
  mfgSku: text("mfg_sku").primaryKey(),
  brand: text("brand").notNull(),
  description: text("description"),
  weightedRetailUsd: decimal("weighted_retail_usd", { precision: 10, scale: 2 }),
  wholesalePriceUsd: decimal("wholesale_price_usd", { precision: 10, scale: 2 }).notNull(),
  sourceFile: text("source_file"),
  updatedAt: timestamp("updated_at").default(sql`now()`),
});

export const PALLET_UNIT_STATUSES = ["available", "sold"] as const;
export type PalletUnitStatus = typeof PALLET_UNIT_STATUSES[number];

export const palletUnits = pgTable("pallet_units", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  palletDefinitionId: uuid("pallet_definition_id").references(() => palletDefinitions.id, { onDelete: "cascade" }).notNull(),
  label: text("label").notNull(), // "SN1", "SN2" — supplier tab label
  status: text("status").$type<PalletUnitStatus>().notNull().default("available"),
  allocatedOrderItemId: uuid("allocated_order_item_id"), // Set when sold; cleared on cancel
  soldAt: timestamp("sold_at"),
  sourceFile: text("source_file"),
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").default(sql`now()`),
  updatedAt: timestamp("updated_at").default(sql`now()`),
}, (table) => ({
  defIdx: index("pallet_units_def_id_idx").on(table.palletDefinitionId),
  statusIdx: index("pallet_units_status_idx").on(table.status),
  uniqueLabel: uniqueIndex("pallet_units_def_label_unique").on(table.palletDefinitionId, table.label),
}));

// Pallet Unit Locations - parallel to unit_locations, but for virtual pallet_units
// (bulk_quantity inventory has no serialized_units to FK against). Same shape so
// the warehouse-location UI and reports can overlay both occupancy maps.
export const palletUnitLocations = pgTable("pallet_unit_locations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  palletUnitId: uuid("pallet_unit_id").references(() => palletUnits.id, { onDelete: "cascade" }).notNull(),
  locationId: uuid("location_id").references(() => warehouseLocations.id).notNull(),
  isCurrent: boolean("is_current").default(true),
  assignedAt: timestamp("assigned_at").default(sql`now()`),
  assignedByUserId: varchar("assigned_by_user_id").references(() => users.id),
  clearedAt: timestamp("cleared_at"),
  clearedByUserId: varchar("cleared_by_user_id").references(() => users.id),
  clearReason: text("clear_reason"),
}, (table) => ({
  currentIdx: index("idx_pallet_unit_current_location").on(table.palletUnitId, table.isCurrent),
}));

export const palletUnitItems = pgTable("pallet_unit_items", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  palletUnitId: uuid("pallet_unit_id").references(() => palletUnits.id, { onDelete: "cascade" }).notNull(),
  modelId: uuid("model_id").references(() => models.id).notNull(),
  supplierSuffix: text("supplier_suffix"), // raw suffix without underscore: "EGXB", "ENXB"
  fsNumber: text("fs_number").notNull(), // FS#####
  sortOrder: integer("sort_order").notNull().default(0),
  createdAt: timestamp("created_at").default(sql`now()`),
}, (table) => ({
  unitIdx: index("pallet_unit_items_unit_id_idx").on(table.palletUnitId),
  modelIdx: index("pallet_unit_items_model_id_idx").on(table.modelId),
}));

export const insertSharkninjaConstituentPricingSchema = createInsertSchema(sharkninjaConstituentPricing).omit({
  updatedAt: true,
});
export const insertPalletUnitSchema = createInsertSchema(palletUnits).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export const insertPalletUnitItemSchema = createInsertSchema(palletUnitItems).omit({
  id: true,
  createdAt: true,
});

export type SharkninjaConstituentPricing = typeof sharkninjaConstituentPricing.$inferSelect;
export type InsertSharkninjaConstituentPricing = z.infer<typeof insertSharkninjaConstituentPricingSchema>;
export type PalletUnit = typeof palletUnits.$inferSelect;
export type InsertPalletUnit = z.infer<typeof insertPalletUnitSchema>;
export type PalletUnitItem = typeof palletUnitItems.$inferSelect;
export type InsertPalletUnitItem = z.infer<typeof insertPalletUnitItemSchema>;

// =============================================================================
// Sohnen Video Links Cache
// Stores fingerprint → inspection page URL mappings from Video_Links.xlsx uploads.
// Applied automatically when matching serialized_units are created during receiving.
// =============================================================================
export const sohnenVideoLinks = pgTable("sohnen_video_links", {
  fingerprint: text("fingerprint").primaryKey(),
  pageUrl: text("page_url").notNull(),
  grade: text("grade"),
  modelNumber: text("model_number"),
  palletStorageId: text("pallet_storage_id"),
  uploadedAt: timestamp("uploaded_at").default(sql`now()`),
});
